import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Icon } from "@/components/Icon";
import ProviderAdminPanel from "@/components/casino/ProviderAdminPanel";
import AdminSportsbookEngine from "@/pages/AdminSportsbookEngine";
import PlayingCard from "@/components/PlayingCard";
import AdminShell, { ADMIN_NAV } from "@/components/admin/AdminShell";
import PayoutsTab from "@/components/admin/PayoutsTab";
import SportsbookUPAControls from "@/components/admin/SportsbookUPAControls";
import { ConfirmProvider, useConfirm } from "@/components/ui/confirm";
import {
  Panel, PageHeader, BtnPrimary, BtnGhost, BtnDanger, RowBtn, StatTile, StatGrid,
  Chip, SearchBox, StatusPill, DataTable, TRow, EmptyRow, Loading, CardHead,
} from "@/components/admin/kit";

const SPORTS = ["basketball", "football", "baseball", "hockey", "mma", "esports", "soccer", "tennis", "other"];

export default function Admin() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner" || user?.role === "super_admin";
  const isSuperAdmin = user?.role === "super_admin";
  const [stats, setStats] = useState(null);
  const loadStats = () => api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
  useEffect(() => { loadStats(); }, []);

  const sections = {
    events: <EventsTab onChange={loadStats} stats={stats} />,
    engine: <AdminSportsbookEngine />,
    house: <HouseTab />,
    props: <PropsGradingTab onChange={loadStats} />,
    cash: <CashTab onChange={loadStats} isSuperAdmin={isSuperAdmin} />,
    // Payouts (incl. the super-admin on/off controls) is super_admin-only; a deep link by a
    // non-super-admin falls through to the default section.
    ...(isSuperAdmin ? { payouts: <PayoutsTab />, proam: <SportsbookUPAControls /> } : {}),
    kyc: <KycTab />,
    users: <UsersTab isSuperAdmin={isSuperAdmin} />,
    allbets: <AllBetsFeed />,
    casinolog: <CasinoLogTab />,
    dealer: isSuperAdmin ? <LiveTablesTab /> : <ProviderAdminPanel />,
    audit: <AuditTab />,
    support: <SupportTab onChange={loadStats} />,
    owner: <OwnerTab />,
  };

  // Section is URL-backed (?section=…) so it survives refresh / back-forward and
  // deep links (e.g. MarketOps' /admin?section=events). Unknown values fall back
  // to the default "events" tab explicitly.
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get("section");
  const active = requested && sections[requested] ? requested : "events";
  const setActive = (key) => {
    const next = new URLSearchParams(searchParams);
    next.set("section", key);
    setSearchParams(next, { replace: false });
  };

  const badges = {
    cash: stats?.pending_transactions || null,
    kyc: stats?.pending_kyc || null,
    support: stats?.open_support || null,
  };
  const title = (ADMIN_NAV.find((n) => n.key === active) || {}).label || "Admin";

  return (
    <ConfirmProvider>
      <AdminShell active={active} onChange={setActive} badges={badges} user={user} title={title}>
        {sections[active] || sections.events}
      </AdminShell>
    </ConfirmProvider>
  );
}

function StatCard({ icon, label, value, highlight }) {
  return (
    <div className={`rounded-xl border p-4 ${highlight ? "border-[#00D632] bg-[#00D632]/5" : "border-[#27272A] bg-[#141417]"}`}>
      <Icon name={icon} size={16} className="text-[#a1a1aa]" />
      <p className="mt-2 font-display text-3xl font-black tabular-nums">{value}</p>
      <p className="text-xs uppercase tracking-wider text-[#a1a1aa]">{label}</p>
    </div>
  );
}

/* -------- Events -------- */
function PropsGradingTab() {
  const [events, setEvents] = useState([]);
  const [grades, setGrades] = useState([]);
  const [busy, setBusy] = useState("");
  const [actuals, setActuals] = useState({});
  const load = () => {
    api.get("/admin/props/pending").then(({ data }) => setEvents(data)).catch(() => {});
    api.get("/admin/props/grades?limit=40").then(({ data }) => setGrades(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const undo = async (id) => {
    setBusy("undo" + id);
    try {
      await api.post(`/admin/props/undo?action_id=${id}`);
      toast.success("Grade undone — bets reset and payouts reversed");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const key = (eid, l) => `${eid}|${l.player}|${l.prop_type}|${l.line}`;

  const gradeLine = async (eid, l, winner) => {
    const k = key(eid, l);
    const actual = actuals[k];
    const body = { event_id: eid, player: l.player, prop_type: l.prop_type, line: l.line };
    if (winner) body.winner = winner;
    else if (actual !== undefined && actual !== "") body.actual = parseFloat(actual);
    else { toast.error("Enter the actual result, or tap a winner"); return; }
    setBusy(k);
    try {
      const { data } = await api.post("/admin/props/grade-line", body);
      toast.success(`Graded — ${data.winner.toUpperCase()} won (${data.settled_bets} bets settled)`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const gradeOutcome = async (eid, oid, result) => {
    setBusy(oid);
    try {
      const { data } = await api.post("/admin/props/grade-outcome", { event_id: eid, outcome_id: oid, result });
      toast.success(`Settled ${data.settled_bets} bet(s)`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const autoGrade = async (eid) => {
    setBusy("auto" + eid);
    try {
      const { data } = await api.post(`/admin/props/auto-grade?event_id=${eid}`);
      toast.success(`Auto-graded ${data.lines_graded} prop line(s) from the final box score`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  return (
    <div className="flex flex-col gap-4" data-testid="props-grading-tab">
      <PageHeader
        title="Grade Props"
        meta="No stats feed — enter the actual stat to auto-pay Over/Under, or grade a side manually."
        actions={<BtnGhost data-testid="props-refresh" onClick={load}>Refresh</BtnGhost>}
      />

      {events.length === 0 ? (
        <Panel className="p-6 text-center text-[13px] text-[#52525b]" data-testid="props-empty">
          No player-prop bets waiting to be graded.
        </Panel>
      ) : events.map((ev) => (
        <Panel key={ev.event_id} className="flex flex-col gap-2.5 p-[18px]" data-testid={`props-event-${ev.event_id}`}>
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="font-display text-[16px] font-extrabold">{ev.away_team} @ {ev.home_team}</span>
            <span className="text-[13px] font-medium text-[#71717a]">{ev.league || ev.sport} · {ev.status}</span>
            <BtnPrimary className="ml-auto h-[30px] px-3" data-testid={`props-autograde-${ev.event_id}`}
              disabled={busy === "auto" + ev.event_id} onClick={() => autoGrade(ev.event_id)}>
              Auto-grade from stats
            </BtnPrimary>
          </div>

          <div className="flex flex-col">
            {ev.lines.map((l) => {
              const k = key(ev.event_id, l);
              const overUnder = l.sides.filter((s) => s.side === "over" || s.side === "under").length === 2;
              return (
                <div key={k}
                  className="flex flex-col gap-2.5 border-t border-[#1F1F23] py-2.5 sm:flex-row sm:items-center sm:gap-2"
                  data-testid={`props-line-${k}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-semibold text-white">{l.player}</div>
                    <div className="text-[11px] font-medium text-[#71717a]">{l.prop_short} <span className="text-[#00D632]">{l.line}</span></div>
                  </div>

                  {overUnder ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <input type="number" step="any" placeholder="Actual result" value={actuals[k] ?? ""}
                        data-testid={`props-actual-${k}`} name={`props-actual-${k}`}
                        onChange={(e) => setActuals((s) => ({ ...s, [k]: e.target.value }))}
                        className="h-[38px] w-32 rounded-[10px] bg-[#0A0A0A] px-2.5 text-[13px] font-medium text-white outline-none placeholder:text-[#52525b] focus:ring-1 focus:ring-[#00D632]/40"
                        style={{ boxShadow: "inset 0 0 0 1px #26262B" }} />
                      <button data-testid={`props-grade-actual-${k}`} disabled={busy === k}
                        onClick={() => gradeLine(ev.event_id, l)}
                        className="rounded-[10px] bg-[#00D632] px-3.5 py-[9px] font-display text-[12px] font-extrabold text-[#0A0A0A] hover:bg-[#00c02d] disabled:opacity-50">
                        Auto-grade
                      </button>
                      <span className="text-[11px] text-[#71717a]">or</span>
                      <PropPill testid={`props-over-${k}`} tone="green" disabled={busy === k}
                        onClick={() => gradeLine(ev.event_id, l, "over")}>Over</PropPill>
                      <PropPill testid={`props-under-${k}`} disabled={busy === k}
                        onClick={() => gradeLine(ev.event_id, l, "under")}>Under</PropPill>
                      <PropPill testid={`props-push-${k}`} disabled={busy === k}
                        onClick={() => gradeLine(ev.event_id, l, "push")}>Push</PropPill>
                    </div>
                  ) : (
                    <div className="flex w-full flex-col gap-2 sm:flex-1 sm:items-end">
                      {l.sides.map((s) => (
                        <div key={s.outcome_id} className="flex w-full flex-wrap items-center justify-between gap-2">
                          <span className="text-[12px] text-white">{s.label}
                            <span className="text-[#71717a]"> ({s.pending_bets} bets, {s.pending_legs} legs){s.graded ? ` · graded ${s.graded}` : ""}</span>
                          </span>
                          <span className="flex gap-2">
                            <PropPill testid={`props-win-${s.outcome_id}`} tone="green" disabled={busy === s.outcome_id}
                              onClick={() => gradeOutcome(ev.event_id, s.outcome_id, "win")}>Won</PropPill>
                            <PropPill testid={`props-lose-${s.outcome_id}`} disabled={busy === s.outcome_id}
                              onClick={() => gradeOutcome(ev.event_id, s.outcome_id, "lose")}>Lost</PropPill>
                            <PropPill testid={`props-refund-${s.outcome_id}`} disabled={busy === s.outcome_id}
                              onClick={() => gradeOutcome(ev.event_id, s.outcome_id, "push")}>Push</PropPill>
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Panel>
      ))}

      <Panel className="flex flex-col gap-2.5 p-[18px]" data-testid="props-history">
        <CardHead title="Recently graded" count={grades.length || null} />
        {grades.length === 0 ? (
          <EmptyRow>No prop grades yet.</EmptyRow>
        ) : (
          <div className="flex flex-col">
            {grades.map((g) => (
              <div key={g.id}
                className={`flex flex-wrap items-center gap-3 border-t border-[#1F1F23] py-2.5 ${g.undone ? "opacity-50" : ""}`}
                data-testid={`grade-${g.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 truncate text-[13px] font-semibold text-white">
                    <span className="truncate">{g.away_team} @ {g.home_team}</span>
                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[.08em] ${g.source === "auto" ? "bg-[#00D632]/15 text-[#00D632]" : "bg-white/[.06] text-[#a1a1aa]"}`}>{g.source}</span>
                    {g.undone && <span className="text-[10px] font-bold uppercase text-[#EF4444]">undone</span>}
                  </div>
                  <div className="truncate text-[11px] text-[#71717a]">
                    {(g.items || []).map((it) => `${it.label || it.player} → ${it.result}`).join(" · ") || "—"} · {g.created_at ? new Date(g.created_at).toLocaleString() : "—"}
                  </div>
                </div>
                <button data-testid={`grade-undo-${g.id}`} disabled={g.undone || busy === "undo" + g.id}
                  onClick={() => undo(g.id)}
                  className="shrink-0 rounded-lg px-2.5 py-[7px] text-[12px] font-bold text-[#EF4444] hover:bg-[rgba(239,68,68,.08)] disabled:opacity-40"
                  style={{ boxShadow: "inset 0 0 0 1px rgba(239,68,68,.45)" }}>
                  Undo
                </button>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

/* small colored grade-control pill (AD-05b / AD-M9) — Over=green fill, others=neutral #18181C */
function PropPill({ tone, testid, className, children, ...p }) {
  const green = tone === "green";
  return (
    <button
      data-testid={testid}
      className={`rounded-[7px] px-[9px] py-[6px] text-[11px] font-bold disabled:opacity-50 ${green ? "text-[#00D632]" : "text-[#a1a1aa] hover:text-white"} ${className || ""}`}
      style={{ background: green ? "rgba(0,214,50,.14)" : "#18181C" }}
      {...p}
    >{children}</button>
  );
}


function EventsTab({ onChange, stats }) {
  const { confirm } = useConfirm();
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState({
    event_type: "match", sport: "basketball", league: "", title: "", home_team: "", away_team: "", start_time: "", max_bet: "", one_per_user: false, parent_event_id: "",
    outcomes: [{ label: "", odds: "" }, { label: "", odds: "" }],
  });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [expo, setExpo] = useState({}); // eventId -> exposure data (or "loading")
  const [drill, setDrill] = useState({}); // `${eventId}:${outcomeId}` -> bettor list (or "loading")
  const [expanded, setExpanded] = useState({}); // eventId -> bool (row detail open)
  const [filter, setFilter] = useState("all"); // all | live | open | paused | settle
  const [query, setQuery] = useState("");

  const toggleExposure = async (id) => {
    if (expo[id] && expo[id] !== "loading") { setExpo((p) => ({ ...p, [id]: undefined })); return; }
    setExpo((p) => ({ ...p, [id]: "loading" }));
    try {
      const { data } = await api.get(`/admin/events/${id}/exposure`);
      setExpo((p) => ({ ...p, [id]: data }));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setExpo((p) => ({ ...p, [id]: undefined }));
    }
  };

  const toggleDrill = async (eventId, outcomeId) => {
    const key = `${eventId}:${outcomeId}`;
    if (drill[key] && drill[key] !== "loading") { setDrill((p) => ({ ...p, [key]: undefined })); return; }
    setDrill((p) => ({ ...p, [key]: "loading" }));
    try {
      const { data } = await api.get(`/admin/events/${eventId}/bets?outcome_id=${outcomeId}`);
      setDrill((p) => ({ ...p, [key]: data }));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setDrill((p) => ({ ...p, [key]: undefined }));
    }
  };

  const money = (n) => `$${(n || 0).toFixed(2)}`;
  const coins = (n) => `${(n || 0).toFixed(2)}`;

  const load = () => api.get("/events").then(({ data }) => setEvents(data));
  useEffect(() => { load(); }, []);

  const setOutcome = (i, key, val) => {
    const outcomes = [...form.outcomes];
    outcomes[i][key] = val;
    setForm({ ...form, outcomes });
  };

  const create = async () => {
    const outright = form.event_type === "outright" || form.event_type === "prop";
    if (!form.league || !form.start_time) { toast.error("Fill league and start time"); return; }
    if (outright ? !form.title : (!form.home_team || !form.away_team)) {
      toast.error(outright ? "Enter a question/title" : "Fill both teams"); return;
    }
    const outcomes = form.outcomes
      .filter((o) => o.label && o.odds)
      .map((o) => ({ label: o.label, odds: parseFloat(o.odds) }));
    if (outcomes.length < 2) { toast.error("Add at least 2 outcomes with odds"); return; }
    setBusy(true);
    try {
      await api.post("/events", { ...form, max_bet: form.max_bet ? parseFloat(form.max_bet) : null, outcomes });
      toast.success("Event created");
      setOpen(false);
      setForm({ event_type: "match", sport: "basketball", league: "", title: "", home_team: "", away_team: "", start_time: "", max_bet: "", one_per_user: false, parent_event_id: "", outcomes: [{ label: "", odds: "" }, { label: "", odds: "" }] });
      await load(); onChange();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const settle = async (eventId, outcomeId) => {
    try {
      await api.post(`/events/${eventId}/settle`, { winning_outcome_id: outcomeId });
      toast.success("Event settled & bets paid out");
      await load(); onChange();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const settleScore = async (eventId, home, away) => {
    if (home === "" || away === "" || isNaN(+home) || isNaN(+away)) { toast.error("Enter both final scores"); return; }
    try {
      const { data } = await api.post(`/admin/events/${eventId}/settle-score`, { home_score: parseInt(home, 10), away_score: parseInt(away, 10) });
      toast.success(`Graded ${data.settled_bets} bets — ${data.won} won, ${data.lost} lost, ${data.push} push`);
      await load(); onChange();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const unsettle = async (eventId) => {
    if (!(await confirm({ title: "Undo settlement", body: "Payouts will be clawed back and bets/parlays reopened.", confirmText: "Undo", danger: true }))) return;
    try {
      const { data } = await api.post(`/admin/events/${eventId}/unsettle`);
      toast.success(`Reopened ${data.reversed_bets} bets, ${data.reversed_parlays} parlays`);
      await load(); onChange();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    await api.delete(`/events/${id}`);
    toast.success("Event deleted");
    await load(); onChange();
  };

  const togglePause = async (e) => {
    try {
      await api.post(`/events/${e.id}/${e.status === "paused" ? "resume" : "pause"}`);
      toast.success(e.status === "paused" ? "Line resumed" : "Line paused");
      await load(); onChange();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const removeOutcome = async (eventId, outcomeId, label) => {
    try {
      await api.post(`/events/${eventId}/remove-outcome`, { winning_outcome_id: outcomeId });
      toast.success(`${label} removed — bets on it marked lost`);
      await load(); onChange();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  // ── Derived display state for the reskinned table ──────────────
  const statusMeta = (e) => {
    const s = String(e.status || "").toLowerCase();
    if (s === "live") return "LIVE";
    if (s === "paused") return "PAUSED";
    if (s === "settled") return "SETTLED";
    if (s === "closed") return "SETTLED";
    return "OPEN";
  };
  const eventName = (e) => (["outright", "prop"].includes(e.event_type) ? e.title : `${e.home_team} vs ${e.away_team}`);
  const startLabel = (e) => {
    if (!e.start_time) return "—";
    const d = new Date(e.start_time);
    return isNaN(d.getTime()) ? "—" : d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  };
  // BETS / HANDLE / LIABILITY are only known once exposure is loaded for a row.
  const exp = (e) => (expo[e.id] && expo[e.id] !== "loading" ? expo[e.id] : null);
  const betsVal = (e) => { const x = exp(e); return x ? x.totals.bet_count : "—"; };
  const handleVal = (e) => { const x = exp(e); return x ? money(x.totals.stake_cash) : "—"; };
  const liabilityVal = (e) => { const x = exp(e); return x ? money(x.totals.payout_cash) : "—"; };
  const liabilityColor = (e) => { const x = exp(e); return x && (x.totals.payout_cash || 0) > 0 ? "#EF4444" : "#00D632"; };
  const isExpanded = (e) => !!expanded[e.id];
  const toggleRow = (e) => { setExpanded((p) => ({ ...p, [e.id]: !p[e.id] })); if (!expo[e.id]) toggleExposure(e.id); };

  const counts = {
    all: events.length,
    live: events.filter((e) => String(e.status).toLowerCase() === "live").length,
    open: events.filter((e) => String(e.status).toLowerCase() === "open").length,
    paused: events.filter((e) => String(e.status).toLowerCase() === "paused").length,
    settle: events.filter((e) => ["closed"].includes(String(e.status).toLowerCase())).length,
  };
  const filtered = events.filter((e) => {
    const s = String(e.status).toLowerCase();
    if (filter === "live" && s !== "live") return false;
    if (filter === "open" && s !== "open") return false;
    if (filter === "paused" && s !== "paused") return false;
    if (filter === "settle" && s !== "closed") return false;
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      if (!`${eventName(e)} ${e.sport} ${e.league}`.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const st = stats || {};
  const dash = (v) => (stats == null || v == null ? "—" : v);
  const GRID = "90px minmax(0,1.6fr) 1fr 90px 90px 110px 210px";

  // Shared per-event detail (exposure + settle/score/unsettle/remove) — same handlers as before.
  const EventDetail = ({ e }) => (
    <div className="space-y-3 border-t border-[#1A1A1E] bg-[#0C0C0E] px-3.5 py-3.5" data-testid={`admin-event-${e.id}`}>
      {expo[e.id] && expo[e.id] !== "loading" && (
        <div className="rounded-lg border border-[#1F1F23] bg-[#111114] p-3" data-testid={`exposure-panel-${e.id}`}>
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-[#a1a1aa]">Money on this {e.event_type === "outright" ? "outright" : "line"} · {expo[e.id].totals.bet_count} bets</p>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-[#a1a1aa]">
                <th className="py-1">Option</th>
                <th className="py-1 text-right">Bets</th>
                <th className="py-1 text-right">Coins staked</th>
                <th className="py-1 text-right">Cash staked</th>
                <th className="py-1 text-right">Max payout ($)</th>
              </tr>
            </thead>
            <tbody>
              {expo[e.id].outcomes.map((o) => (
                <React.Fragment key={o.outcome_id}>
                  <tr data-testid={`exposure-row-${e.id}-${o.outcome_id}`}
                    onClick={() => o.bet_count > 0 && toggleDrill(e.id, o.outcome_id)}
                    className={`border-t border-[#27272A] ${o.bet_count > 0 ? "cursor-pointer hover:bg-[#00B829]/5" : ""}`}>
                    <td className="py-1.5 font-medium">
                      {o.bet_count > 0 && <span className="mr-1 text-[#a1a1aa]">{drill[`${e.id}:${o.outcome_id}`] && drill[`${e.id}:${o.outcome_id}`] !== "loading" ? "▾" : "▸"}</span>}
                      {o.label}
                    </td>
                    <td className="py-1.5 text-right tabular-nums">{o.bet_count}</td>
                    <td className="py-1.5 text-right tabular-nums text-[#00D632]">{coins(o.stake_coins)}</td>
                    <td className="py-1.5 text-right tabular-nums">{money(o.stake_cash)}</td>
                    <td className="py-1.5 text-right tabular-nums text-[#a1a1aa]">{money(o.payout_cash)}</td>
                  </tr>
                  {drill[`${e.id}:${o.outcome_id}`] === "loading" && (
                    <tr><td colSpan={5} className="py-2 text-center"><Icon name="refresh" size={12} className="inline animate-spin text-[#00D632]" /></td></tr>
                  )}
                  {drill[`${e.id}:${o.outcome_id}`] && drill[`${e.id}:${o.outcome_id}`] !== "loading" && (
                    <tr data-testid={`drill-panel-${e.id}-${o.outcome_id}`}>
                      <td colSpan={5} className="bg-[#141417] px-2 py-2">
                        <div className="rounded-md border border-[#27272A]">
                          {drill[`${e.id}:${o.outcome_id}`].map((b) => (
                            <div key={b.id} className="flex items-center justify-between border-b border-[#27272A] px-3 py-1.5 text-xs last:border-0">
                              <span className="font-medium">{b.username}</span>
                              <span className="flex items-center gap-3 tabular-nums">
                                <span className={b.currency === "cash" ? "text-white" : "text-[#00D632]"}>
                                  {b.currency === "cash" ? money(b.stake) : `${coins(b.stake)} PCoins`}
                                </span>
                                <span className="text-[#a1a1aa]">@ {b.odds?.toFixed(2)}</span>
                                <span className={`rounded px-1.5 py-0.5 text-[10px] uppercase ${b.status === "won" ? "bg-[#00D632]/15 text-[#00D632]" : b.status === "lost" ? "bg-[#EF4444]/15 text-[#EF4444]" : "bg-[#1C1C21] text-[#a1a1aa]"}`}>{b.status}</span>
                              </span>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              <tr className="border-t-2 border-white/10 font-bold">
                <td className="py-1.5">Total</td>
                <td className="py-1.5 text-right tabular-nums">{expo[e.id].totals.bet_count}</td>
                <td className="py-1.5 text-right tabular-nums text-[#00D632]">{coins(expo[e.id].totals.stake_coins)}</td>
                <td className="py-1.5 text-right tabular-nums">{money(expo[e.id].totals.stake_cash)}</td>
                <td className="py-1.5 text-right tabular-nums text-[#a1a1aa]">{money(expo[e.id].totals.payout_cash)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      {expo[e.id] === "loading" && (
        <div className="flex justify-center py-3"><Icon name="refresh" size={16} className="animate-spin text-[#00D632]" /></div>
      )}
      {(e.status === "open" || e.status === "paused" || e.status === "live" || e.status === "closed") ? (
        <div className="space-y-3">
          {e.event_type === "outright" && (
            <div className="flex flex-wrap gap-2">
              <span className="self-center text-xs text-[#a1a1aa]">Remove eliminated:</span>
              {e.outcomes.map((o) => (
                <Button key={o.id} size="sm" variant="outline" data-testid={`remove-outcome-${e.id}-${o.id}`}
                  onClick={() => removeOutcome(e.id, o.id, o.label)} className="rounded-full border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/10">
                  <Icon name="x" size={12} className="mr-1" /> {o.label}
                </Button>
              ))}
            </div>
          )}
          {e.source === "live" && e.event_type !== "outright" ? (
            <ScoreSettleForm event={e} onSettle={settleScore} />
          ) : (
            <div className="flex flex-wrap gap-2">
              <span className="self-center text-xs text-[#a1a1aa]">Settle winner:</span>
              {e.outcomes.map((o) => (
                <Button key={o.id} size="sm" variant="outline" data-testid={`settle-${e.id}-${o.id}`}
                  onClick={() => settle(e.id, o.id)} className="rounded-full">
                  {o.label} ({o.odds.toFixed(2)})
                </Button>
              ))}
            </div>
          )}
        </div>
      ) : e.status === "settled" ? (
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-[#00D632]">
            {e.home_score != null ? `Final: ${e.away_team} ${e.away_score} – ${e.home_score} ${e.home_team}` : `Winner: ${e.outcomes.find((o) => o.id === e.winning_outcome_id)?.label}`}
          </p>
          <Button size="sm" variant="outline" data-testid={`unsettle-${e.id}`}
            onClick={() => unsettle(e.id)} className="rounded-full border-surface-border text-[#00D632] hover:bg-[#00B829]/5">
            Undo settle
          </Button>
        </div>
      ) : null}
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Page header — Events auto-published by the live odds engine; this view grades & settles */}
      <PageHeader
        title="Events"
        meta={`${counts.open} open · ${counts.live} live · auto-pulled by the odds engine · grade & settle below`}
      />

      {/* Stat tiles (5) */}
      <StatGrid cols={5}>
        <StatTile icon="account" label="USERS" value={dash(st.total_users)} />
        <StatTile icon="sportsbook" label="OPEN EVENTS" value={dash(st.open_events)} />
        <StatTile icon="transfer" label="PENDING CASH" value={dash(st.pending_transactions)} valueColor="#E4E4E7" />
        <StatTile icon="mybets" label="PENDING BETS" value={dash(st.pending_bets)} />
        <StatTile icon="support" label="OPEN SUPPORT" value={dash(st.open_support)} valueColor={(st.open_support || 0) > 0 ? "#EF4444" : "#fff"} iconColor={(st.open_support || 0) > 0 ? "#EF4444" : "#a1a1aa"} />
      </StatGrid>

      {/* Filter chips + search */}
      <div className="flex flex-wrap items-center gap-1.5">
        <Chip active={filter === "all"} onClick={() => setFilter("all")}>All</Chip>
        <Chip active={filter === "live"} onClick={() => setFilter("live")}>Live {counts.live}</Chip>
        <Chip active={filter === "open"} onClick={() => setFilter("open")}>Open {counts.open}</Chip>
        <Chip active={filter === "paused"} onClick={() => setFilter("paused")}>Paused {counts.paused}</Chip>
        <Chip active={filter === "settle"} onClick={() => setFilter("settle")}>Needs settle {counts.settle}</Chip>
        <SearchBox className="ml-auto w-full sm:w-[240px]" placeholder="Search events…" value={query} onChange={(ev) => setQuery(ev.target.value)} data-testid="events-search" />
      </div>

      {/* ── Desktop table (lg and up) ─────────────────────────────── */}
      <div className="hidden lg:block">
        <DataTable
          cols={GRID}
          header={[
            "STATUS", "EVENT", "START",
            { label: "BETS", right: true }, { label: "HANDLE", right: true },
            { label: "LIABILITY", right: true }, { label: "ACTIONS", right: true },
          ]}
        >
          {filtered.length === 0 ? (
            <EmptyRow>No events match this filter.</EmptyRow>
          ) : (
            filtered.map((e) => (
              <React.Fragment key={e.id}>
                <TRow cols={GRID}>
                  <StatusPill status={statusMeta(e)} />
                  <div className="min-w-0">
                    <div className="truncate text-[13px] font-semibold">{eventName(e)}</div>
                    <div className="text-[11px] font-medium text-[#71717a]">{e.sport} · {e.league}</div>
                  </div>
                  <span className="text-[#a1a1aa]">{startLabel(e)}</span>
                  <span className="text-right tabular-nums">{betsVal(e)}</span>
                  <span className="text-right tabular-nums">{handleVal(e)}</span>
                  <span className="text-right font-display text-[13px] font-extrabold tabular-nums" style={{ color: exp(e) ? liabilityColor(e) : "#71717a" }}>{liabilityVal(e)}</span>
                  <div className="flex justify-end gap-1.5">
                    <RowBtn data-testid={`exposure-toggle-${e.id}`} onClick={() => toggleRow(e)}>{isExpanded(e) ? "Hide" : "Edit"}</RowBtn>
                    {(e.status === "open" || e.status === "paused") && (
                      <RowBtn data-testid={`pause-${e.id}`} onClick={() => togglePause(e)}>{e.status === "paused" ? "Resume" : "Pause"}</RowBtn>
                    )}
                    <RowBtn tone="green" data-testid={`settle-open-${e.id}`} onClick={() => toggleRow(e)}>Settle</RowBtn>
                    <RowBtn data-testid={`delete-event-${e.id}`} onClick={() => remove(e.id)}>×</RowBtn>
                  </div>
                </TRow>
                {isExpanded(e) && <EventDetail e={e} />}
              </React.Fragment>
            ))
          )}
        </DataTable>
      </div>

      {/* ── Mobile stacked cards (<lg) ────────────────────────────── */}
      <div className="flex flex-col gap-3 lg:hidden">
        {filtered.length === 0 ? (
          <Panel className="px-3.5 py-8 text-center text-[13px] text-[#52525b]">No events match this filter.</Panel>
        ) : (
          filtered.map((e) => (
            <Panel key={e.id} className="flex flex-col gap-2.5 bg-[#111114] p-3">
              <div className="flex items-center gap-2">
                <StatusPill status={statusMeta(e)} />
                <span className="min-w-0 flex-1 truncate text-[14px] font-bold">{eventName(e)}</span>
                <span className="text-[11px] font-medium text-[#71717a]">{startLabel(e)}</span>
              </div>
              <div className="grid grid-cols-3 gap-1.5 text-[11px] font-medium text-[#71717a]">
                <div>Bets<div className="font-display text-[14px] font-extrabold text-white">{betsVal(e)}</div></div>
                <div>Handle<div className="font-display text-[14px] font-extrabold text-white">{handleVal(e)}</div></div>
                <div>Liability<div className="font-display text-[14px] font-extrabold" style={{ color: exp(e) ? liabilityColor(e) : "#71717a" }}>{liabilityVal(e)}</div></div>
              </div>
              <div className="flex gap-1.5">
                <RowBtn className="h-11 flex-1" data-testid={`exposure-toggle-m-${e.id}`} onClick={() => toggleRow(e)}>{isExpanded(e) ? "Hide" : "Edit"}</RowBtn>
                {(e.status === "open" || e.status === "paused") && (
                  <RowBtn className="h-11 flex-1" data-testid={`pause-m-${e.id}`} onClick={() => togglePause(e)}>{e.status === "paused" ? "Resume" : "Pause"}</RowBtn>
                )}
                <RowBtn tone="green" className="h-11 flex-1" data-testid={`settle-open-m-${e.id}`} onClick={() => toggleRow(e)}>Settle</RowBtn>
              </div>
              {isExpanded(e) && (
                <div className="rounded-[12px] border border-[#1F1F23]">
                  <EventDetail e={e} />
                </div>
              )}
            </Panel>
          ))
        )}
      </div>

      {/* Create-event dialog — unchanged behavior, opened by "+ New event" */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-[#141417] text-white border-[#27272A]">
          <DialogHeader><DialogTitle className="font-display tracking-tight">Create event</DialogTitle></DialogHeader><DialogDescription className="sr-only">Create a new sportsbook event with its markets and odds.</DialogDescription>
          <div className="space-y-3">
            <div>
              <Label>Prediction type</Label>
              <div className="mt-1.5 grid grid-cols-3 gap-2">
                <button type="button" data-testid="type-match" onClick={() => setForm({ ...form, event_type: "match" })}
                  className={`rounded-lg border px-2 py-2 text-xs font-semibold transition-colors ${form.event_type === "match" ? "border-[#00D632] bg-[#00D632]/10" : "border-[#27272A] bg-[#141417] text-[#a1a1aa]"}`}>
                  Match
                </button>
                <button type="button" data-testid="type-outright" onClick={() => setForm({ ...form, event_type: "outright" })}
                  className={`rounded-lg border px-2 py-2 text-xs font-semibold transition-colors ${form.event_type === "outright" ? "border-[#00D632] bg-[#00D632]/10" : "border-[#27272A] bg-[#141417] text-[#a1a1aa]"}`}>
                  Outright
                </button>
                <button type="button" data-testid="type-prop" onClick={() => setForm({ ...form, event_type: "prop" })}
                  className={`rounded-lg border px-2 py-2 text-xs font-semibold transition-colors ${form.event_type === "prop" ? "border-[#00D632] bg-[#00D632]/10" : "border-[#27272A] bg-[#141417] text-[#a1a1aa]"}`}>
                  Player Prop
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Sport</Label>
                <Select value={form.sport} onValueChange={(v) => setForm({ ...form, sport: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="event-sport-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141417] text-white border-[#27272A]">
                    {SPORTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>League</Label>
                <Input className="mt-1.5" data-testid="event-league-input" value={form.league} onChange={(e) => setForm({ ...form, league: e.target.value })} placeholder="NBA" />
              </div>
            </div>
            {(form.event_type === "outright" || form.event_type === "prop") ? (
              <div>
                <Label>{form.event_type === "prop" ? "Player prop (name — stat)" : "Question / Title"}</Label>
                <Input className="mt-1.5" data-testid="event-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={form.event_type === "prop" ? "Gerrit Cole — Strikeouts" : "Who will win the NBA Finals?"} />
                {form.event_type === "prop" && (
                  <div className="mt-3">
                    <Label>Attach to game</Label>
                    <select data-testid="prop-parent-select" name="parent_event_id" value={form.parent_event_id}
                      onChange={(e) => setForm({ ...form, parent_event_id: e.target.value })}
                      className="mt-1.5 w-full rounded-md border border-[#27272A] bg-[#141417] px-3 py-2 text-sm">
                      <option value="">Standalone — not tied to a game</option>
                      {events.filter((ev) => ev.event_type === "match" && (ev.status === "open" || ev.status === "paused")).map((ev) => (
                        <option key={ev.id} value={ev.id}>{ev.home_team} vs {ev.away_team} ({ev.league})</option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-[#a1a1aa]">Props tied to a game show up inside that game on the sportsbook.</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Home team</Label>
                  <Input className="mt-1.5" data-testid="event-home-input" value={form.home_team} onChange={(e) => setForm({ ...form, home_team: e.target.value })} placeholder="Celtics" />
                </div>
                <div>
                  <Label>Away team</Label>
                  <Input className="mt-1.5" data-testid="event-away-input" value={form.away_team} onChange={(e) => setForm({ ...form, away_team: e.target.value })} placeholder="Lakers" />
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Start time</Label>
                <Input type="datetime-local" className="mt-1.5" data-testid="event-time-input" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
              </div>
              <div>
                <Label>Max bet ($) — optional</Label>
                <Input type="number" min="0" step="0.01" className="mt-1.5" data-testid="event-maxbet-input" value={form.max_bet} onChange={(e) => setForm({ ...form, max_bet: e.target.value })} placeholder="No limit" />
              </div>
            </div>
            <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-[#27272A] px-3 py-2.5" data-testid="one-per-user-toggle">
              <input type="checkbox" checked={form.one_per_user} onChange={(e) => setForm({ ...form, one_per_user: e.target.checked })} className="h-4 w-4 accent-[#00D632]" />
              <span className="text-sm font-medium">Limit to one bet per player</span>
            </label>
            <div>
              <Label>{form.event_type === "outright" ? "Options & odds (add all teams/choices)" : form.event_type === "prop" ? "Lines & odds (e.g. Over 6.5 / Under 6.5)" : "Outcomes & odds"}</Label>
              <div className="mt-1.5 space-y-2">
                {form.outcomes.map((o, i) => (
                  <div key={i} className="flex gap-2">
                    <Input data-testid={`outcome-label-${i}`} value={o.label} onChange={(e) => setOutcome(i, "label", e.target.value)} placeholder={form.event_type === "outright" ? `Option ${i + 1} (e.g. Boston Celtics)` : `Outcome ${i + 1} (e.g. Celtics win)`} />
                    <Input data-testid={`outcome-odds-${i}`} type="number" step="0.01" min="1" className="w-24" value={o.odds} onChange={(e) => setOutcome(i, "odds", e.target.value)} placeholder="1.90" />
                    {form.outcomes.length > 2 && (
                      <Button variant="ghost" size="icon" onClick={() => setForm({ ...form, outcomes: form.outcomes.filter((_, x) => x !== i) })}><Icon name="x" size={16} /></Button>
                    )}
                  </div>
                ))}
                <Button variant="outline" size="sm" data-testid="add-outcome-button" onClick={() => setForm({ ...form, outcomes: [...form.outcomes, { label: "", odds: "" }] })}>
                  <Icon name="plus" size={12} className="mr-1" /> Add option
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="create-event-submit" onClick={create} disabled={busy} className="w-full rounded-full bg-[#00D632] font-bold text-[#0A0A0A] hover:bg-[#00B829]">
              {busy ? "Creating..." : "Create event"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ScoreSettleForm({ event, onSettle }) {
  const [away, setAway] = useState("");
  const [home, setHome] = useState("");
  const majors = ["basketball", "football", "baseball", "hockey"];
  const autoNote = majors.includes(event.sport)
    ? "Auto-settles from live scores when the game ends. Use this to grade manually if needed."
    : "This sport isn't auto-settled — enter the final score to grade & pay all markets.";
  return (
    <div className="rounded-lg border border-[#27272A] bg-[#1C1C21] p-3" data-testid={`score-settle-${event.id}`}>
      <p className="mb-2 text-xs text-[#a1a1aa]">{autoNote}</p>
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <Label className="text-[11px] text-[#a1a1aa]">{event.away_team} · Away</Label>
          <Input type="number" min="0" value={away} data-testid={`score-away-${event.id}`}
            onChange={(e) => setAway(e.target.value)} placeholder="0" className="h-8 w-24" />
        </div>
        <span className="pb-1.5 text-[#a1a1aa]">–</span>
        <div>
          <Label className="text-[11px] text-[#a1a1aa]">{event.home_team} · Home</Label>
          <Input type="number" min="0" value={home} data-testid={`score-home-${event.id}`}
            onChange={(e) => setHome(e.target.value)} placeholder="0" className="h-8 w-24" />
        </div>
        <Button size="sm" data-testid={`score-settle-btn-${event.id}`}
          onClick={() => onSettle(event.id, home, away)}
          className="rounded-full bg-[#00D632] font-bold text-[#0A0A0A] hover:bg-[#00B829]">
          Grade &amp; pay
        </Button>
      </div>
    </div>
  );
}


/* -------- Cash (deposits/withdrawals) -------- */
/* Initials from a username (Manrope avatar glyph). */
function txnInitials(name) {
  const s = String(name || "").trim();
  if (!s) return "—";
  const parts = s.split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return s.slice(0, 2).toUpperCase();
}

/* AD-02a / AD-M3 — Redemption requests queue.
   Reskin only: same /admin/transactions data, same approve/reject endpoints,
   same handlers, same data-testids. Desktop = grid rows (≥lg); mobile = stacked cards.
   NOTE: transaction records carry no KYC-status field, so the KYC chip is omitted
   (honest fallback — not fabricated). */
/* Redemption/withdrawal lifecycle → chip tone.
   Progress states (approved/sent/paid) use positive/neutral tones; only the
   true-negative states (rejected/failed) are red. "paid" is a success, NOT red. */
const TXN_STATUS_CHIP = {
  pending:  { bg: "#26262B",            fg: "#d4d4d8", glyph: "•", label: "Pending" },
  approved: { bg: "rgba(0,214,50,.12)", fg: "#00D632", glyph: "✓", label: "Approved" },
  sent:     { bg: "rgba(59,130,246,.14)", fg: "#60A5FA", glyph: "→", label: "Payout sent" },
  paid:     { bg: "rgba(0,214,50,.16)", fg: "#00D632", glyph: "✓", label: "Paid" },
  rejected: { bg: "rgba(239,68,68,.15)", fg: "#EF4444", glyph: "✕", label: "Rejected" },
  failed:   { bg: "rgba(239,68,68,.15)", fg: "#EF4444", glyph: "!", label: "Failed" },
};
function TxnStatusChip({ status, className }) {
  const s = String(status || "").toLowerCase();
  const m = TXN_STATUS_CHIP[s] || { bg: "#18181C", fg: "#a1a1aa", glyph: "•", label: status || "—" };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-semibold ${className || ""}`} style={{ background: m.bg, color: m.fg }} data-testid="txn-status-chip">
      <span aria-hidden="true" className="text-[10px] leading-none">{m.glyph}</span>{m.label}
    </span>
  );
}
/* Mask a payout destination (account/handle) for display — keep the tail only. */
function maskDetail(v) {
  const s = String(v || "").trim();
  if (!s) return "";
  if (s.length <= 4) return `••${s.slice(-2)}`;
  return `••••${s.slice(-4)}`;
}

function CashTab({ onChange, isSuperAdmin }) {
  const [txns, setTxns] = useState([]);
  const [error, setError] = useState("");
  const [rowBusy, setRowBusy] = useState({}); // txnId -> action string in flight
  const load = () => api.get("/admin/transactions").then(({ data }) => { setTxns(data); setError(""); }).catch((e) => setError(apiError(e.response?.data?.detail) || "Failed to load transactions"));
  useEffect(() => { load(); }, []);

  // All money actions below require super_admin on the backend; mirror that gate
  // in the UI so a non-super admin sees a disabled control + hint, not a 403.
  const act = async (id, action, body) => {
    if (!isSuperAdmin) return;
    if (rowBusy[id]) return; // one pending action per row — repeat taps are ignored
    setRowBusy((p) => ({ ...p, [id]: action }));
    try {
      await api.post(`/admin/transactions/${id}/${action}`, body || {});
      const msg = {
        approve: "Approved", reject: "Rejected",
        "payout-sent": "Marked payout sent", "payout-paid": "Marked paid", "payout-failed": "Marked failed — reserve refunded",
      }[action] || "Updated";
      toast.success(msg);
      await load(); onChange();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setRowBusy((p) => { const n = { ...p }; delete n[id]; return n; }); }
  };

  const markSent = (t) => {
    const reference = window.prompt("Payout reference (bank/processor confirmation ID) — recorded on this payout:");
    if (reference === null) return;
    act(t.id, "payout-sent", { reference: reference.trim() });
  };
  const markPaid = (t) => act(t.id, "payout-paid");
  const markFailed = (t) => {
    const reason = window.prompt("Reason this payout failed (shown in history; refunds the reserve):");
    if (reason === null) return;
    act(t.id, "payout-failed", { reason: reason.trim() });
  };

  // Header pill reflects REAL pending count + summed pending amount from live data.
  const pending = txns.filter((t) => t.status === "pending");
  const pendingTotal = pending.reduce((s, t) => s + (Number(t.amount) || 0), 0);
  const pill = `${pending.length} pending · $${pendingTotal.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const fmtTs = (v) => (v ? new Date(v).toLocaleString() : null);

  // Lifecycle action controls for a withdrawal, honoring status + role.
  // Copy never claims automatic delivery — marking "sent"/"paid" is a manual
  // operator record of a payout made outside this app.
  const LifecycleActions = ({ t, mobile }) => {
    if (!isSuperAdmin) {
      return <span className="text-[11px] font-medium text-[#71717a]" data-testid={`txn-role-hint-${t.id}`}>Super admin required to act</span>;
    }
    const busy = rowBusy[t.id];
    const btn = (key, label, onClick, tone) => (
      <button
        key={key}
        data-testid={`txn-${key}-${t.id}${mobile ? "-m" : ""}`}
        disabled={!!busy}
        onClick={onClick}
        className={`rounded-lg px-3 py-[7px] font-display text-[12px] font-extrabold disabled:opacity-50 ${mobile ? "flex h-[44px] flex-1 items-center justify-center" : ""} ${
          tone === "green" ? "bg-[#00D632] text-[#0A0A0A] hover:bg-[#00c02d]"
          : tone === "red" ? "text-[#EF4444]"
          : "bg-[#18181C] text-white"
        }`}
        style={tone === "red" ? { boxShadow: "inset 0 0 0 1px rgba(239,68,68,.5)" } : tone === "blue" ? { background: "rgba(59,130,246,.14)", color: "#60A5FA", boxShadow: "inset 0 0 0 1px rgba(59,130,246,.4)" } : undefined}
      >{busy === labelKey(key) ? "…" : label}</button>
    );
    // map button key -> action key used for busy comparison
    function labelKey(k) {
      return { reject: "reject", approve: "approve", sent: "payout-sent", paid: "payout-paid", failed: "payout-failed" }[k];
    }
    const s = t.status;
    if (s === "pending") {
      return (
        <>
          {btn("reject", "Reject", () => act(t.id, "reject"), "red")}
          {btn("approve", "Approve", () => act(t.id, "approve"), "green")}
        </>
      );
    }
    // approved → can mark payout sent / failed; withdrawals only.
    if (s === "approved" && t.type === "withdraw") {
      return (
        <>
          {btn("sent", "Mark sent", () => markSent(t), "blue")}
          {btn("failed", "Failed", () => markFailed(t), "red")}
        </>
      );
    }
    // sent → can mark paid / failed.
    if (s === "sent") {
      return (
        <>
          {btn("paid", "Mark paid", () => markPaid(t), "green")}
          {btn("failed", "Failed", () => markFailed(t), "red")}
        </>
      );
    }
    return null;
  };

  return (
    <div className="space-y-3">
      {error && <div data-testid="cash-error" className="rounded-lg border border-[#EF4444]/40 bg-[#EF4444]/10 p-3 text-sm text-[#EF4444]">{error}</div>}
      {!isSuperAdmin && (
        <div data-testid="cash-readonly-note" className="rounded-lg border border-[#27272A] bg-[#141417] p-3 text-[12px] text-[#a1a1aa]">
          Read-only view. Approving, rejecting, and recording payouts requires the <span className="font-semibold text-[#d4d4d8]">super admin</span> role.
        </div>
      )}
      <Panel className="overflow-hidden">
        <div className="border-b border-[#1F1F23] px-[18px] py-4">
          <CardHead title="Redemption requests" count={pill} />
        </div>
        {txns.length === 0 ? (
          <EmptyRow>No deposit or withdrawal requests.</EmptyRow>
        ) : (
          txns.map((t) => {
            const method = t.type === "withdraw" ? (t.payout_method || "—") : "Deposit";
            const dateStr = new Date(t.created_at).toLocaleString();
            const amount = `$${(Number(t.amount) || 0).toFixed(2)}`;
            const masked = maskDetail(t.payout_detail);
            const sentAt = fmtTs(t.sent_at), paidAt = fmtTs(t.paid_at);
            // Secondary line: payout destination + reference + timestamps + failure reason.
            const meta = [
              t.type === "deposit" ? "Deposit" : "Withdraw",
              dateStr,
            ].filter(Boolean).join(" · ");
            const payoutLine = t.type === "withdraw" ? [
              method !== "—" ? method : null,
              masked ? `acct ${masked}` : null,
              t.payout_reference ? `ref ${t.payout_reference}` : null,
              sentAt ? `sent ${sentAt}` : null,
              paidAt ? `paid ${paidAt}` : null,
            ].filter(Boolean).join(" · ") : null;
            return (
              <div key={t.id} data-testid={`admin-txn-${t.id}`} className="border-t border-[#1A1A1E]">
                {/* Desktop grid row (≥lg) */}
                <div
                  className="hidden items-center gap-3 px-[18px] py-3 lg:grid"
                  style={{ gridTemplateColumns: "34px minmax(0,1fr) 110px 100px 230px" }}
                >
                  <span className="flex h-[34px] w-[34px] items-center justify-center rounded-full bg-[#1C1C21] font-display text-[11px] font-extrabold text-[#d4d4d8]">{txnInitials(t.username)}</span>
                  <div className="min-w-0">
                    <div className="truncate text-[14px] font-bold text-white">{t.username}</div>
                    <div className="mt-0.5 truncate text-[12px] font-medium text-[#71717a]">{meta}</div>
                    {payoutLine && <div className="mt-0.5 truncate text-[11px] font-medium text-[#52525b]" data-testid={`txn-payout-meta-${t.id}`}>{payoutLine}</div>}
                    {t.fail_reason && <div className="mt-0.5 truncate text-[11px] font-medium text-[#EF4444]" data-testid={`txn-fail-reason-${t.id}`}>Failed: {t.fail_reason}</div>}
                  </div>
                  <span className="truncate text-[12px] font-medium text-[#a1a1aa]" data-testid={t.type === "withdraw" ? `txn-method-${t.id}` : undefined}>{method}</span>
                  <span className="text-right font-display text-[16px] font-extrabold tabular-nums">{amount}</span>
                  <div className="flex items-center justify-end gap-1.5">
                    <TxnStatusChip status={t.status} />
                    <LifecycleActions t={t} />
                  </div>
                </div>
                {/* Mobile stacked card (<lg) */}
                <div className="flex flex-col gap-2.5 px-[14px] py-3 lg:hidden">
                  <div className="flex items-center gap-2.5">
                    <span className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-[#1C1C21] font-display text-[11px] font-extrabold text-[#d4d4d8]">{txnInitials(t.username)}</span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[14px] font-bold text-white">{t.username}</div>
                      <div className="mt-0.5 truncate text-[12px] font-medium text-[#71717a]">{method} · {dateStr}</div>
                    </div>
                    <span className="font-display text-[20px] font-extrabold tabular-nums">{amount}</span>
                  </div>
                  {payoutLine && <div className="truncate text-[11px] font-medium text-[#52525b]" data-testid={`txn-payout-meta-${t.id}-m`}>{payoutLine}</div>}
                  {t.fail_reason && <div className="truncate text-[11px] font-medium text-[#EF4444]" data-testid={`txn-fail-reason-${t.id}-m`}>Failed: {t.fail_reason}</div>}
                  <div className="flex items-center gap-2">
                    <TxnStatusChip status={t.status} className="self-start" />
                  </div>
                  <div className="flex gap-2">
                    <LifecycleActions t={t} mobile />
                  </div>
                </div>
              </div>
            );
          })
        )}
      </Panel>
    </div>
  );
}

/* Small in-tab sub-navigation, used to bundle related admin views under one tab. */
function AdminSubNav({ views, value, onChange }) {
  return (
    <div className="mb-5 flex flex-wrap gap-2 border-b border-[#27272A] pb-3">
      {views.map(([k, l]) => (
        <button key={k} data-testid={`subnav-${k}`} onClick={() => onChange(k)}
          className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors ${value === k ? "bg-[#00D632] text-[#0A0A0A]" : "border border-[#27272A] bg-[#141417] text-[#a1a1aa] hover:bg-[#1C1C21]"}`}>{l}</button>
      ))}
    </div>
  );
}

/* All Bets tab — bundles the unified feed, the casino-only log, and the bettor lookup. */
function AllBetsTab() {
  const [view, setView] = useState("all");
  return (
    <div>
      <AdminSubNav value={view} onChange={setView}
        views={[["all", "All bets"], ["casino", "Casino log"], ["lookup", "Bettor lookup"]]} />
      {view === "all" && <AllBetsFeed />}
      {view === "casino" && <CasinoLogTab />}
      {view === "lookup" && <BetsTab />}
    </div>
  );
}

/* Users tab — bundles user management and KYC review. */
function UsersKycTab() {
  const [view, setView] = useState("users");
  return (
    <div>
      <AdminSubNav value={view} onChange={setView} views={[["users", "Users"], ["kyc", "KYC review"]]} />
      {view === "users" ? <UsersTab /> : <KycTab />}
    </div>
  );
}


/* Casino tab — bundles the live-dealer tables (super admin) and casino providers. */
function CasinoAdminTab({ isSuperAdmin }) {
  const [view, setView] = useState(isSuperAdmin ? "tables" : "providers");
  const views = isSuperAdmin ? [["tables", "Live tables"], ["providers", "Providers"]] : [["providers", "Providers"]];
  return (
    <div>
      <AdminSubNav value={view} onChange={setView} views={views} />
      {view === "tables" && isSuperAdmin && <LiveTablesTab />}
      {view === "providers" && <ProviderAdminPanel />}
    </div>
  );
}

/* Finance tab — bundles House P/L and cash deposits / withdrawals. */
function FinanceTab({ onChange }) {
  const [view, setView] = useState("pl");
  return (
    <div>
      <AdminSubNav value={view} onChange={setView}
        views={[["pl", "House P/L"], ["cash", "Deposits / Withdrawals"]]} />
      {view === "pl" ? <HouseTab /> : <CashTab onChange={onChange} />}
    </div>
  );
}

/* -------- Users -------- */
function UsersTab({ isSuperAdmin }) {
  const { confirm, promptText } = useConfirm();
  const [users, setUsers] = useState([]);
  const [amt, setAmt] = useState("");
  const [fundCur, setFundCur] = useState("coins");
  const [fundMode, setFundMode] = useState("add"); // "add" | "subtract"
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resetBusy, setResetBusy] = useState({});
  const [q, setQ] = useState("");
  const [bannedIps, setBannedIps] = useState([]);
  const [newIp, setNewIp] = useState("");
  const [activityUser, setActivityUser] = useState(null); // user whose detail dialog is open
  const [activity, setActivity] = useState(null);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const openActivity = async (u) => {
    setActivityUser(u); setActivity(null); setLoadingActivity(true);
    try { const { data } = await api.get(`/admin/users/${u.id}/activity`); setActivity(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); setActivityUser(null); }
    finally { setLoadingActivity(false); }
  };
  const load = () => api.get("/admin/users").then(({ data }) => setUsers(data));
  const loadIps = () => api.get("/admin/banned-ips").then(({ data }) => setBannedIps(data)).catch(() => {});
  useEffect(() => { load(); loadIps(); }, []);

  const sendReset = async (u) => {
    if (!u.email) { toast.error("This user has no email on file"); return; }
    setResetBusy((p) => ({ ...p, [u.id]: true }));
    try {
      const { data } = await api.post(`/admin/users/${u.id}/send-reset`);
      toast.success(data.message || "Reset link sent");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setResetBusy((p) => ({ ...p, [u.id]: false })); }
  };

  const banUser = async (u) => {
    const reason = await promptText({ title: `Ban ${u.username}?`, body: "This blocks their account and their known IP(s).", placeholder: "Optional reason", confirmText: "Ban user" });
    if (reason === null) return;
    try {
      const { data } = await api.post(`/admin/users/${u.id}/ban`, { reason, ban_ips: true });
      toast.success(`${u.username} banned${data.banned_ips?.length ? ` · ${data.banned_ips.length} IP(s) blocked` : ""}`);
      await load(); await loadIps();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const unbanUser = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/unban`);
      toast.success(`${u.username} unbanned`);
      await load(); await loadIps();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const toggleGeoExempt = async (u) => {
    const next = !u.geo_exempt;
    if (next && !(await confirm({ title: "Allow geo-block bypass?", body: `Allow ${u.username} to bypass the state geo-block? Use this only for trusted/testing accounts.`, confirmText: "Allow bypass" }))) return;
    try {
      await api.post(`/admin/users/${u.id}/geo-exempt`, { ban_ips: next, reason: next ? "Owner-approved geo exemption" : "" });
      toast.success(next ? `${u.username} can now bypass the state block` : `Geo exemption removed for ${u.username}`);
      await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const blockIp = async () => {
    if (!newIp.trim()) { toast.error("Enter an IP address"); return; }
    try {
      await api.post("/admin/banned-ips", { ip: newIp.trim(), reason: "Manual block" });
      toast.success(`Blocked ${newIp.trim()}`); setNewIp(""); await loadIps();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const unblockIp = async (ip) => {
    try { await api.delete(`/admin/banned-ips/${encodeURIComponent(ip)}`); await loadIps(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const adjustFunds = async () => {
    if (!isSuperAdmin) { toast.error("Super admin required to adjust balances"); return; }
    const a = parseFloat(amt);
    if (!a || a <= 0) { toast.error("Enter a valid amount"); return; }
    const subtract = fundMode === "subtract";
    setBusy(true);
    try {
      const endpoint = subtract ? "subtract-funds" : "add-funds";
      await api.post(`/admin/users/${target.id}/${endpoint}`, { amount: a, currency: fundCur });
      const verb = subtract ? "Removed" : "Added";
      const prep = subtract ? "from" : "to";
      toast.success(`${verb} ${fundCur === "cash" ? "$" : ""}${a.toFixed(2)} ${fundCur} ${prep} ${target.username}`);
      setTarget(null); setAmt(""); setFundMode("add");
      await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const filtered = q.trim() ? users.filter((u) => `${u.username} ${u.email} ${u.last_ip || ""}`.toLowerCase().includes(q.trim().toLowerCase())) : users;

  // Role → VIP-column badge. Real role mapped to the mockup's colored chip
  // (admin = neutral grey, super_admin/owner = green, player = muted). No fabricated VIP tiers.
  const roleBadge = (role) => {
    if (role === "super_admin") return { label: "SUPER", color: "#00D632" };
    if (role === "owner") return { label: "PLATFORM", color: "#00D632" };
    if (role === "admin") return { label: "ADMIN", color: "#d4d4d8" };
    return { label: "PLAYER", color: "#71717a" };
  };
  // KYC status → colored label (real kyc_status; honest "—" when absent).
  const kycBadge = (s) => {
    const v = (s || "").toLowerCase();
    if (v === "approved" || v === "verified") return { label: "VERIFIED", color: "#00D632" };
    if (v === "pending" || v === "review") return { label: "PENDING", color: "#EAB308" };
    if (v === "rejected" || v === "denied") return { label: "REJECTED", color: "#EF4444" };
    return { label: "—", color: "#52525b" };
  };
  const initials = (name) => {
    const s = String(name || "").trim();
    if (!s) return "—";
    const parts = s.split(/[\s._-]+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return s.slice(0, 2).toUpperCase();
  };
  const COLS = "minmax(0,1.4fr) 88px 108px 96px 96px 190px";

  return (
    <div className="space-y-3">
      {!isSuperAdmin && (
        <div data-testid="users-readonly-note" className="rounded-lg border border-[#27272A] bg-[#141417] p-3 text-[12px] text-[#a1a1aa]">
          Adjusting a player’s balance requires the <span className="font-semibold text-[#d4d4d8]">super admin</span> role. Other moderation tools remain available.
        </div>
      )}
      {/* Banned IPs (full-site block) — real capability, restrained kit Panel (single red = the heading only). */}
      <Panel className="p-4" data-testid="banned-ips-card">
        <p className="mb-1 flex items-center gap-1.5 font-display text-[14px] font-extrabold text-[#EF4444]"><Icon name="shield-alert" size={16} /> Banned IPs (full-site block)</p>
        <p className="mb-3 text-[11px] text-[#a1a1aa]">A banned IP is blocked from the whole site (admins/owner are exempt). Banning a player auto-blocks their known IPs.</p>
        <div className="mb-3 flex gap-2">
          <SearchBox data-testid="block-ip-input" value={newIp} onChange={(e) => setNewIp(e.target.value)} placeholder="e.g. 203.0.113.5" className="h-[38px] flex-1" />
          <BtnDanger data-testid="block-ip-btn" onClick={blockIp} className="h-[38px] px-4">Block IP</BtnDanger>
        </div>
        <div className="space-y-1" data-testid="banned-ip-list">
          {bannedIps.length === 0 ? <p className="text-[11px] text-[#a1a1aa]">No banned IPs.</p> : bannedIps.map((b) => (
            <div key={b.ip} data-testid={`banned-ip-${b.ip}`} className="flex items-center justify-between rounded-[10px] border border-[#1F1F23] bg-[#141417] px-2.5 py-1.5 text-xs">
              <span className="min-w-0 truncate"><b className="tabular-nums text-white">{b.ip}</b> <span className="text-[#a1a1aa]">· {b.reason || "blocked"}</span></span>
              <button data-testid={`unblock-ip-${b.ip}`} onClick={() => unblockIp(b.ip)} className="ml-2 shrink-0 text-[#a1a1aa] hover:text-white"><Icon name="x" size={14} /></button>
            </div>
          ))}
        </div>
      </Panel>

      {/* AD-03b Users panel — desktop table (≥lg). */}
      <Panel className="hidden overflow-hidden lg:block">
        <div className="flex items-center gap-2.5 border-b border-[#1F1F23] px-[18px] py-4">
          <span className="font-display text-[18px] font-extrabold">Users</span>
          <span className="text-[12px] font-medium text-[#71717a]">{users.length.toLocaleString("en-US")} total</span>
          <SearchBox data-testid="user-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search username / email" className="ml-auto w-[240px]" />
        </div>
        <div className="grid gap-2.5 bg-[#111114] px-[18px] py-2.5 text-[10px] font-semibold tracking-[.14em] text-[#71717a]" style={{ gridTemplateColumns: COLS }}>
          <span>USER</span>
          <span>ROLE</span>
          <span className="text-right">COINS</span>
          <span className="text-right">CASH</span>
          <span>KYC</span>
          <span className="text-right">ACTIONS</span>
        </div>
        {filtered.length === 0 ? (
          <EmptyRow>{q.trim() ? "No players match your search." : "No players yet."}</EmptyRow>
        ) : filtered.map((u) => {
          const rb = roleBadge(u.role);
          const kb = kycBadge(u.kyc_status);
          const canModerate = u.role !== "admin" && u.role !== "owner";
          return (
            <div
              key={u.id}
              data-testid={`admin-user-${u.id}`}
              className={`grid items-center gap-2.5 border-t border-[#1A1A1E] px-[18px] py-2.5 ${u.banned ? "bg-[#EF4444]/[.05]" : ""}`}
              style={{ gridTemplateColumns: COLS }}
            >
              <div className="flex min-w-0 items-center gap-2.5">
                <span className="flex h-[34px] w-[34px] flex-none items-center justify-center rounded-full bg-[#1C1C21] font-display text-[11px] font-extrabold text-[#d4d4d8]">{initials(u.username)}</span>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <button data-testid={`user-activity-${u.id}`} onClick={() => openActivity(u)} className="truncate text-[13px] font-bold text-white hover:text-[#00D632] hover:underline underline-offset-2" title="View activity">{u.username}</button>
                    {u.banned && <span data-testid={`banned-badge-${u.id}`} className="flex-none rounded-md bg-[#EF4444]/20 px-1.5 py-0.5 text-[10px] font-extrabold tracking-[.06em] text-[#EF4444]">BANNED</span>}
                    {u.geo_exempt && <span data-testid={`geo-exempt-badge-${u.id}`} className="flex-none rounded-md bg-white/[.06] px-1.5 py-0.5 text-[10px] font-extrabold tracking-[.06em] text-[#a1a1aa]">GEO</span>}
                  </div>
                  <div className="truncate text-[11px] font-medium text-[#71717a]">{u.email || "—"}{u.last_ip ? <span className="text-[#52525b]"> · {u.last_ip}</span> : null}</div>
                </div>
              </div>
              <span className="text-[12px] font-bold" style={{ color: rb.color }}>{rb.label}</span>
              <span className="text-right font-display text-[13px] font-bold tabular-nums text-white">{(u.coins || 0).toFixed(2)}</span>
              <span className="text-right font-display text-[13px] font-bold tabular-nums text-white">${(u.cash || 0).toFixed(2)}</span>
              <span className="text-[11px] font-bold" style={{ color: kb.color }}>{kb.label}</span>
              <div className="flex items-center justify-end gap-1.5">
                <RowBtn data-testid={`add-funds-${u.id}`} disabled={!isSuperAdmin} onClick={() => { setFundMode("add"); setAmt(""); setTarget(u); }} title={isSuperAdmin ? "Adjust funds" : "Super admin required to adjust balances"}>Credit</RowBtn>
                <RowBtn data-testid={`send-reset-${u.id}`} disabled={resetBusy[u.id]} onClick={() => sendReset(u)} title="Send password reset">{resetBusy[u.id] ? "…" : "Reset"}</RowBtn>
                {canModerate && (
                  <RowBtn data-testid={`geo-exempt-${u.id}`} onClick={() => toggleGeoExempt(u)} tone={u.geo_exempt ? "green" : undefined} title={u.geo_exempt ? "Remove geo bypass" : "Grant geo bypass"}>Geo</RowBtn>
                )}
                {canModerate && (u.banned ? (
                  <RowBtn data-testid={`unban-${u.id}`} tone="green" onClick={() => unbanUser(u)} title="Unban">Unban</RowBtn>
                ) : (
                  <RowBtn data-testid={`ban-${u.id}`} onClick={() => banUser(u)} className="!text-[#EF4444]" style={{ background: "rgba(239,68,68,.10)", boxShadow: "inset 0 0 0 1px rgba(239,68,68,.4)" }} title="Ban">Ban</RowBtn>
                ))}
              </div>
            </div>
          );
        })}
      </Panel>

      {/* AD-M6 Users — mobile stacked cards (<lg). */}
      <div className="space-y-3 lg:hidden">
        <div className="flex items-center gap-2.5">
          <span className="font-display text-[18px] font-extrabold">Users</span>
          <span className="text-[12px] font-medium text-[#71717a]">{users.length.toLocaleString("en-US")} total</span>
        </div>
        <SearchBox data-testid="user-search-m" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search username or email" className="h-[46px] w-full text-[14px]" />
        {filtered.length === 0 ? (
          <Panel className="px-4 py-8 text-center text-[13px] text-[#52525b]">{q.trim() ? "No players match your search." : "No players yet."}</Panel>
        ) : filtered.map((u) => {
          const rb = roleBadge(u.role);
          const kb = kycBadge(u.kyc_status);
          const canModerate = u.role !== "admin" && u.role !== "owner";
          return (
            <Panel key={u.id} data-testid={`admin-user-${u.id}-m`} className={`bg-[#111114] p-3 ${u.banned ? "border-[#EF4444]/40" : ""}`}>
              <div className="flex items-center gap-2.5">
                <span className="h-2.5 w-2.5 flex-none rounded-full" style={{ background: rb.color }} />
                <div className="min-w-0 flex-1">
                  <button data-testid={`user-activity-${u.id}-m`} onClick={() => openActivity(u)} className="block truncate text-left text-[15px] font-bold text-white" title="View activity">{u.username}</button>
                  <div className="truncate text-[12px] font-medium text-[#71717a]">{u.email || "—"}</div>
                </div>
                <span className="flex-none rounded-full bg-[#1C1C21] px-2 py-0.5 text-[11px] font-bold" style={{ color: rb.color }}>{rb.label}</span>
              </div>
              {(u.banned || u.geo_exempt) && (
                <div className="mt-2 flex gap-1.5">
                  {u.banned && <span data-testid={`banned-badge-${u.id}-m`} className="rounded-md bg-[#EF4444]/20 px-1.5 py-0.5 text-[10px] font-extrabold tracking-[.06em] text-[#EF4444]">BANNED</span>}
                  {u.geo_exempt && <span data-testid={`geo-exempt-badge-${u.id}-m`} className="rounded-md bg-white/[.06] px-1.5 py-0.5 text-[10px] font-extrabold tracking-[.06em] text-[#a1a1aa]">GEO-EXEMPT</span>}
                </div>
              )}
              <div className="mt-2.5 grid grid-cols-3 gap-2 border-t border-[#1F1F23] py-2.5">
                <div className="min-w-0">
                  <div className="text-[10px] font-semibold tracking-[.12em] text-[#71717a]">COINS</div>
                  <div className="mt-0.5 truncate font-display text-[14px] font-extrabold text-white">{(u.coins || 0).toFixed(2)}</div>
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] font-semibold tracking-[.12em] text-[#71717a]">CASH</div>
                  <div className="mt-0.5 truncate font-display text-[14px] font-extrabold text-white">${(u.cash || 0).toFixed(2)}</div>
                </div>
                <div>
                  <div className="text-[10px] font-semibold tracking-[.12em] text-[#71717a]">KYC</div>
                  <div className="mt-0.5 font-display text-[14px] font-extrabold" style={{ color: kb.color }}>{kb.label}</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                <RowBtn data-testid={`add-funds-${u.id}-m`} disabled={!isSuperAdmin} onClick={() => { setFundMode("add"); setAmt(""); setTarget(u); }} title={isSuperAdmin ? "Adjust funds" : "Super admin required"} className="flex-1 !py-2.5 text-center">Credit</RowBtn>
                <RowBtn data-testid={`user-history-${u.id}-m`} onClick={() => openActivity(u)} className="flex-1 !py-2.5 text-center">History</RowBtn>
                <RowBtn data-testid={`send-reset-${u.id}-m`} disabled={resetBusy[u.id]} onClick={() => sendReset(u)} className="flex-1 !py-2.5 text-center">{resetBusy[u.id] ? "…" : "Reset"}</RowBtn>
                {canModerate && (
                  <RowBtn data-testid={`geo-exempt-${u.id}-m`} onClick={() => toggleGeoExempt(u)} tone={u.geo_exempt ? "green" : undefined} className="flex-1 !py-2.5 text-center">Geo</RowBtn>
                )}
                {canModerate && (u.banned ? (
                  <RowBtn data-testid={`unban-${u.id}-m`} tone="green" onClick={() => unbanUser(u)} className="flex-1 !py-2.5 text-center">Unban</RowBtn>
                ) : (
                  <RowBtn data-testid={`ban-${u.id}-m`} onClick={() => banUser(u)} className="flex-1 !py-2.5 text-center !text-[#EF4444]" style={{ background: "rgba(239,68,68,.10)", boxShadow: "inset 0 0 0 1px rgba(239,68,68,.4)" }}>Ban</RowBtn>
                ))}
              </div>
            </Panel>
          );
        })}
      </div>

      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent className="bg-[#0C0C0E] text-white border-[#1F1F23]">
          <DialogHeader><DialogTitle className="font-display tracking-tight">{fundMode === "subtract" ? "Remove funds from" : "Add funds to"} {target?.username}</DialogTitle></DialogHeader><DialogDescription className="sr-only">Credit or debit coins or cash on this user's account.</DialogDescription>
          <div className="space-y-3">
            <div>
              <Label>Action</Label>
              <div className="mt-1.5 grid grid-cols-2 gap-2">
                <button onClick={() => setFundMode("add")} data-testid="fund-mode-add"
                  className={`rounded-lg border px-3 py-2 text-sm font-semibold ${fundMode === "add" ? "border-[#00D632] bg-[#00D632]/10 text-[#00D632]" : "border-[#27272A] text-[#a1a1aa]"}`}>Add</button>
                <button onClick={() => setFundMode("subtract")} data-testid="fund-mode-subtract"
                  className={`rounded-lg border px-3 py-2 text-sm font-semibold ${fundMode === "subtract" ? "border-[#EF4444] bg-[#EF4444]/10 text-[#EF4444]" : "border-[#27272A] text-[#a1a1aa]"}`}>Subtract</button>
              </div>
            </div>
            <div>
              <Label>Type</Label>
              <div className="mt-1.5 grid grid-cols-2 gap-2">
                <button onClick={() => setFundCur("coins")} data-testid="fund-cur-coins"
                  className={`rounded-lg border px-3 py-2 text-sm font-semibold ${fundCur === "coins" ? "border-[#00D632] bg-[#00D632]/10 text-[#00D632]" : "border-[#27272A] text-[#a1a1aa]"}`}>Free PCoins</button>
                <button onClick={() => setFundCur("cash")} data-testid="fund-cur-cash"
                  className={`rounded-lg border px-3 py-2 text-sm font-semibold ${fundCur === "cash" ? "border-[#00D632] bg-[#00D632] text-[#0A0A0A]" : "border-[#27272A] text-[#a1a1aa]"}`}>PCash</button>
              </div>
            </div>
            <div>
              <Label>Amount</Label>
              <Input type="number" min="0" step="0.01" value={amt} data-testid="add-funds-amount" onChange={(e) => setAmt(e.target.value)} placeholder="0.00" className="mt-1.5" />
              {fundMode === "subtract" && target && (
                <p className="mt-1.5 text-[11px] text-[#a1a1aa]">Current balance: {(target.coins || 0).toFixed(2)} PCoins · ${(target.cash || 0).toFixed(2)} PCash. You can’t remove more than they hold.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            {fundMode === "subtract" ? (
              <Button data-testid="confirm-subtract-funds" onClick={adjustFunds} disabled={busy} className="w-full rounded-full bg-[#EF4444] font-bold text-white hover:bg-[#EF4444]/90">
                {busy ? "Removing..." : "Remove funds"}
              </Button>
            ) : (
              <Button data-testid="confirm-add-funds" onClick={adjustFunds} disabled={busy} className="w-full rounded-full bg-[#00D632] font-bold text-[#0A0A0A] hover:bg-[#00B829]">
                {busy ? "Adding..." : "Add funds"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!activityUser} onOpenChange={(o) => !o && (setActivityUser(null), setActivity(null))}>
        <DialogContent className="max-h-[85vh] overflow-auto bg-[#0C0C0E] text-white border-[#1F1F23] sm:max-w-3xl" data-testid="user-activity-dialog">
          <DialogHeader><DialogTitle className="font-display tracking-tight">{activityUser?.username} — activity</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Games played and transactions for this user.</DialogDescription>
          {loadingActivity || !activity ? (
            <div className="flex justify-center py-12"><Icon name="refresh" size={24} className="animate-spin text-[#00D632]" /></div>
          ) : (
            <div className="space-y-5 text-sm">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div><p className="text-[10px] uppercase tracking-wider text-[#a1a1aa]">PCoins</p><p className="font-bold tabular-nums text-[#00D632]">{activity.user.coins.toFixed(2)}</p></div>
                <div><p className="text-[10px] uppercase tracking-wider text-[#a1a1aa]">PCash</p><p className="font-bold tabular-nums">${activity.user.cash.toFixed(2)}</p></div>
                <div><p className="text-[10px] uppercase tracking-wider text-[#a1a1aa]">Role · KYC</p><p className="font-medium">{activity.user.role} · {activity.user.kyc_status || "none"}</p></div>
                <div><p className="text-[10px] uppercase tracking-wider text-[#a1a1aa]">Last IP</p><p className="truncate">{activity.user.last_ip || "—"}</p></div>
              </div>
              <p className="truncate text-xs text-[#a1a1aa]">{activity.user.email} · joined {activity.user.created_at ? new Date(activity.user.created_at).toLocaleDateString() : "—"}</p>

              <div>
                <p className="mb-1 text-xs font-bold uppercase tracking-[0.15em] text-[#a1a1aa]">Games played ({activity.counts.casino})</p>
                {activity.games_summary.length === 0 ? <p className="text-xs text-[#71717a]">No games played.</p> : (
                  <div className="flex flex-wrap gap-2">
                    {activity.games_summary.map((g) => (
                      <span key={g.game} className="rounded-full border border-[#27272A] bg-[#0F0F12] px-2.5 py-1 text-xs capitalize">{(g.game || "").replace(/_/g, " ")} · <b className="text-white">{g.plays}</b></span>
                    ))}
                  </div>
                )}
              </div>

              {activity.casino.length > 0 && (
                <div>
                  <p className="mb-1 text-xs font-bold uppercase tracking-[0.15em] text-[#a1a1aa]">Recent casino plays</p>
                  <div className="max-h-48 overflow-auto rounded-lg border border-[#27272A]">
                    <table className="w-full text-xs"><tbody>
                      {activity.casino.slice(0, 100).map((h, i) => (
                        <tr key={i} className="border-t border-[#27272A] first:border-0">
                          <td className="px-2 py-1 capitalize">{(h.game || "").replace(/_/g, " ")}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{h.currency === "cash" ? `$${h.bet.toFixed(2)}` : `${h.bet.toFixed(0)}c`}</td>
                          <td className="px-2 py-1 text-right tabular-nums text-[#a1a1aa]">→ {h.currency === "cash" ? `$${h.payout.toFixed(2)}` : `${h.payout.toFixed(0)}c`}</td>
                          <td className="px-2 py-1 text-right text-[10px] text-[#71717a]">{h.created_at ? new Date(h.created_at).toLocaleString() : ""}</td>
                        </tr>
                      ))}
                    </tbody></table>
                  </div>
                </div>
              )}

              {(activity.sportsbook.length > 0 || activity.live.length > 0) && (
                <div>
                  <p className="mb-1 text-xs font-bold uppercase tracking-[0.15em] text-[#a1a1aa]">Bets ({activity.counts.sportsbook + activity.counts.live})</p>
                  <div className="max-h-40 overflow-auto rounded-lg border border-[#27272A]">
                    <table className="w-full text-xs"><tbody>
                      {activity.sportsbook.slice(0, 60).map((b, i) => (
                        <tr key={`s${i}`} className="border-t border-[#27272A] first:border-0"><td className="px-2 py-1">SB · {b.status}</td><td className="px-2 py-1 truncate">{typeof b.selection === "string" ? b.selection : "—"}</td><td className="px-2 py-1 text-right tabular-nums">{b.currency === "cash" ? `$${b.stake.toFixed(2)}` : `${b.stake.toFixed(0)}c`}</td></tr>
                      ))}
                      {activity.live.slice(0, 60).map((b, i) => (
                        <tr key={`l${i}`} className="border-t border-[#27272A]"><td className="px-2 py-1">Baccarat · {b.result || "open"}</td><td className="px-2 py-1">{b.side}</td><td className="px-2 py-1 text-right tabular-nums">{b.currency === "cash" ? `$${b.amount.toFixed(2)}` : `${b.amount.toFixed(0)}c`}</td></tr>
                      ))}
                    </tbody></table>
                  </div>
                </div>
              )}

              <div>
                <p className="mb-1 text-xs font-bold uppercase tracking-[0.15em] text-[#a1a1aa]">Transactions ({activity.counts.transactions})</p>
                {activity.transactions.length === 0 ? <p className="text-xs text-[#71717a]">No transactions.</p> : (
                  <div className="max-h-48 overflow-auto rounded-lg border border-[#27272A]">
                    <table className="w-full text-xs"><tbody>
                      {activity.transactions.slice(0, 150).map((t, i) => (
                        <tr key={i} className="border-t border-[#27272A] first:border-0">
                          <td className="px-2 py-1 capitalize">{(t.type || "").replace(/_/g, " ")}</td>
                          <td className="px-2 py-1 text-[#a1a1aa]">{t.status}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{t.currency === "cash" ? `$${t.amount.toFixed(2)}` : `${t.amount.toFixed(0)}c`}</td>
                          <td className="px-2 py-1 truncate text-[10px] text-[#71717a]">{t.note || ""}</td>
                        </tr>
                      ))}
                    </tbody></table>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* -------- Bets -------- */
/* -------- Global Audit Log -------- */
const AUDIT_LABEL = {
  balance_add: "Balance added", balance_subtract: "Balance removed", balance_set: "Balance set",
  user_ban: "User banned", user_unban: "User unbanned", geo_exempt_set: "Geo bypass granted",
  geo_exempt_remove: "Geo bypass removed", role_change: "Role changed",
};

/* Action → colored dot tone (AD-05e): bans red, credits/grants green, else neutral */
const AUDIT_TONE = {
  user_ban: "#EF4444",
  geo_exempt_remove: "#EF4444",
  balance_subtract: "#EF4444",
  balance_add: "#00D632",
  user_unban: "#00D632",
  geo_exempt_set: "#00D632",
  role_change: "#a1a1aa",
  balance_set: "#a1a1aa",
};

function AuditTab() {
  const [rows, setRows] = useState([]);
  const [auditRows, setAuditRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const load = () => {
    setLoading(true);
    Promise.allSettled([api.get("/admin/audit-log"), api.get("/admin/adjustments")]).then(([a, b]) => {
      if (a.status === "fulfilled") setAuditRows(a.value.data);
      if (b.status === "fulfilled") setRows(b.value.data);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  const money = (c, n) => c === "cash" ? `$${(n || 0).toFixed(2)}` : `${(n || 0).toFixed(2)} PCoins`;

  /* Human summary from action + detail (missing fields → "—", never fabricated) */
  const dmoney = (c, n) => (n == null ? null : (c === "cash" ? `$${Number(n).toFixed(2)} cash` : `${Number(n).toFixed(2)} PCoins`));
  const auditSummary = (r) => {
    const d = r.detail || {};
    switch (r.action) {
      case "balance_add": { const m = dmoney(d.currency, d.amount); return m ? `+${m}` : "Balance added"; }
      case "balance_subtract": { const m = dmoney(d.currency, d.amount); return m ? `−${m}` : "Balance removed"; }
      case "balance_set": { const m = dmoney(d.currency, d.new_value); return m ? `Set to ${m}` : "Balance set"; }
      case "role_change": {
        if (d.from && d.to) return `${d.from} → ${d.to}`;
        if (d.to) return `→ ${d.to}`;
        return "Role changed";
      }
      case "user_ban": return d.reason ? `Reason: ${d.reason}` : "User banned";
      case "user_unban": return "User unbanned";
      case "geo_exempt_set": return d.reason ? `Reason: ${d.reason}` : "Geo bypass granted";
      case "geo_exempt_remove": return "Geo bypass removed";
      default: {
        const keys = Object.keys(d);
        if (!keys.length) return "";
        return keys.map((k) => `${k}: ${typeof d[k] === "object" ? JSON.stringify(d[k]) : d[k]}`).join(" · ");
      }
    }
  };

  const filtered = q.trim() ? rows.filter((r) => `${r.username} ${r.admin_email} ${r.note}`.toLowerCase().includes(q.trim().toLowerCase())) : rows;
  const auditFiltered = q.trim() ? auditRows.filter((r) => `${r.action} ${r.actor} ${r.target || ""}`.toLowerCase().includes(q.trim().toLowerCase())) : auditRows;

  const adjCols = "1.3fr 1.1fr 1fr 1.1fr 1.2fr 1.4fr";
  const adjHeader = ["When", "User", "Change", "Balance", "Admin", "Reason"];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <span className="font-display text-[18px] font-extrabold">Audit log</span>
          <p className="text-[12px] font-medium text-[#71717a]">Bans, role changes, geo bypass and balance edits — who did what, newest first.</p>
        </div>
        <div className="ml-auto flex gap-2">
          <SearchBox data-testid="audit-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by action, admin, user…" className="w-64" />
          <BtnGhost data-testid="audit-refresh" onClick={load}>Refresh</BtnGhost>
        </div>
      </div>

      {/* Admin & security actions — AD-05e dot+summary list (reflows to stacked rows on mobile) */}
      <Panel className="flex flex-col gap-0 p-[18px]" data-testid="audit-log-panel">
        <div className="mb-1 flex items-center gap-2.5">
          <span className="font-display text-[16px] font-extrabold">Admin &amp; security actions</span>
          {!loading && <span className="rounded-full bg-white/[.06] px-2 py-[3px] text-[11px] font-bold text-[#E4E4E7]">{auditFiltered.length}</span>}
        </div>
        {loading ? (
          <Loading />
        ) : auditFiltered.length === 0 ? (
          <div data-testid="audit-log-empty"><EmptyRow>No admin actions logged{q ? " match your filter" : " yet"}.</EmptyRow></div>
        ) : (
          auditFiltered.map((r) => {
            const summary = auditSummary(r);
            const tone = AUDIT_TONE[r.action] || "#71717a";
            return (
              <div
                key={r.id}
                data-testid={`audit-log-row-${r.id}`}
                className="flex flex-col gap-2 border-b border-[#1A1A1E] py-2.5 last:border-b-0 sm:flex-row sm:items-start sm:gap-2.5"
              >
                <span className="mt-[6px] hidden h-2 w-2 flex-none rounded-full sm:block" style={{ background: tone }} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className="rounded-md px-2 py-[3px] text-[10px] font-extrabold uppercase tracking-[.06em]"
                      style={{ background: `${tone}22`, color: tone }}
                    >
                      {AUDIT_LABEL[r.action] || (r.action || "").replace(/_/g, " ") || "action"}
                    </span>
                    {r.target ? <span className="truncate text-[12px] font-semibold text-white">{r.target}</span> : null}
                    {summary ? <span className="truncate text-[12px] font-medium text-[#d4d4d8]">{summary}</span> : null}
                  </div>
                  <div className="mt-0.5 text-[11px] font-medium text-[#71717a]">
                    {r.actor}{r.actor_role ? ` · ${r.actor_role}` : ""} · {r.ts ? new Date(r.ts).toLocaleString() : "—"}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </Panel>

      {/* Balance adjustment log — desktop DataTable + mobile stacked cards */}
      <div className="flex flex-col gap-3">
        <div>
          <span className="font-display text-[16px] font-extrabold">Balance adjustment log</span>
          <p className="text-[12px] font-medium text-[#71717a]">Every admin credit/debit across all users, newest first.</p>
        </div>

        {/* Desktop */}
        <Panel className="hidden overflow-hidden bg-[#111114] p-0 md:block">
          <DataTable cols={adjCols} header={adjHeader} className="rounded-none border-0">
            {loading ? (
              <Loading />
            ) : filtered.length === 0 ? (
              <div data-testid="audit-adjust-empty"><EmptyRow>No balance adjustments{q ? " match your filter" : " yet"}.</EmptyRow></div>
            ) : (
              filtered.map((r) => (
                <TRow key={r.id} cols={adjCols} data-testid={`audit-row-${r.id}`}>
                  <span className="text-[12px] text-[#a1a1aa]">{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</span>
                  <span className="truncate font-semibold text-white">{r.username || "—"}</span>
                  <span className="tabular-nums font-display font-extrabold" style={{ color: r.direction === "credit" ? "#00D632" : "#EF4444" }}>
                    {r.direction === "credit" ? "+" : "−"}{money(r.currency, r.amount)}
                  </span>
                  <span className="tabular-nums text-[12px] text-[#a1a1aa]">
                    {r.old_value != null && r.new_value != null ? `${r.old_value.toFixed(2)} → ${r.new_value.toFixed(2)}` : "—"}
                  </span>
                  <span className="truncate text-[12px] text-[#a1a1aa]">{r.admin_email || "—"}</span>
                  <span className="truncate text-[12px] italic text-[#71717a]">{r.note || "—"}</span>
                </TRow>
              ))
            )}
          </DataTable>
        </Panel>

        {/* Mobile: stacked label:value cards (AD-M12) */}
        <div className="flex flex-col gap-3 md:hidden">
          {loading ? (
            <Loading />
          ) : filtered.length === 0 ? (
            <div data-testid="audit-adjust-empty-m"><EmptyRow>No balance adjustments{q ? " match your filter" : " yet"}.</EmptyRow></div>
          ) : (
            filtered.map((r) => (
              <Panel key={r.id} className="flex flex-col gap-2.5 bg-[#111114] p-3" data-testid={`audit-row-m-${r.id}`}>
                <div className="flex items-center gap-2.5">
                  <span className="flex-1 truncate font-display text-[15px] font-bold text-white">{r.username || "—"}</span>
                  <span className="font-display text-[16px] font-extrabold tabular-nums" style={{ color: r.direction === "credit" ? "#00D632" : "#EF4444" }}>
                    {r.direction === "credit" ? "+" : "−"}{money(r.currency, r.amount)}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 border-t border-[#1F1F23] pt-2.5">
                  <div className="min-w-0">
                    <div className="text-[10px] font-semibold tracking-[.12em] text-[#71717a]">BALANCE</div>
                    <div className="mt-0.5 truncate text-[13px] tabular-nums text-[#a1a1aa]">
                      {r.old_value != null && r.new_value != null ? `${r.old_value.toFixed(2)} → ${r.new_value.toFixed(2)}` : "—"}
                    </div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] font-semibold tracking-[.12em] text-[#71717a]">WHEN</div>
                    <div className="mt-0.5 truncate text-[12px] text-[#a1a1aa]">{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] font-semibold tracking-[.12em] text-[#71717a]">ADMIN</div>
                    <div className="mt-0.5 truncate text-[12px] text-[#a1a1aa]">{r.admin_email || "—"}</div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] font-semibold tracking-[.12em] text-[#71717a]">REASON</div>
                    <div className="mt-0.5 truncate text-[12px] italic text-[#71717a]">{r.note || "—"}</div>
                  </div>
                </div>
              </Panel>
            ))
          )}
        </div>
      </div>
    </div>
  );
}


/* -------- Owner controls -------- */
function OwnerTab() {
  const { confirm } = useConfirm();
  const [pct, setPct] = useState("");
  const [savedPct, setSavedPct] = useState(null);
  const [edges, setEdges] = useState({});       // {dice, coinflip, mines, crash} as strings
  const [savedEdges, setSavedEdges] = useState({});
  const [savingEdges, setSavingEdges] = useState(false);
  const [houseGames, setHouseGames] = useState({});   // game -> {cash profit, plays}
  const [bonus, setBonus] = useState({ id: "", amt: "", pt: "1", restrict: "all" });
  const [rake, setRake] = useState({ id: "", amt: "", cur: "cash" });
  const [admins, setAdmins] = useState([]);
  const [geoExempt, setGeoExempt] = useState([]);
  const [staffId, setStaffId] = useState("");
  const [bets, setBets] = useState([]);
  const [loadingBets, setLoadingBets] = useState(true);
  const [q, setQ] = useState("");

  const loadConfig = () => api.get("/owner/config").then(({ data }) => {
    setSavedPct(data.referral_pct); setPct(String(data.referral_pct));
    const g = data.house_edge_games_pct || {};
    setSavedEdges(g);
    setEdges(Object.fromEntries(Object.entries(g).map(([k, v]) => [k, String(v)])));
  }).catch(() => {});
  const loadBets = () => { setLoadingBets(true); api.get("/owner/all-bets").then(({ data }) => setBets(data)).catch(() => {}).finally(() => setLoadingBets(false)); };
  const [promos, setPromos] = useState([]);
  const [pf, setPf] = useState({ promo_type: "deposit_match", title: "", description: "", match_pct: "100", max_bonus: "", min_deposit: "10", playthrough: "1", restrict: "all", expires_at: "", bet_min: "25", bonus_amount: "300", free_bet_denom: "25" });
  const loadPromos = () => api.get("/owner/promotions").then(({ data }) => setPromos(data)).catch(() => {});
  const loadAdmins = () => api.get("/owner/admins").then(({ data }) => setAdmins(data)).catch(() => {});
  const loadGeoExempt = () => api.get("/admin/users").then(({ data }) => setGeoExempt((data || []).filter((u) => u.geo_exempt))).catch(() => {});
  const revokeGeo = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/geo-exempt`, { ban_ips: false, reason: "" });
      toast.success(`Geo bypass removed for ${u.username}`);
      loadGeoExempt();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const loadHouse = () => api.get("/admin/house").then(({ data }) => {
    const m = {};
    (data?.cash?.by_game || []).forEach((g) => { m[g.game] = { profit: g.profit, plays: g.plays }; });
    setHouseGames(m);
  }).catch(() => {});
  useEffect(() => { loadConfig(); loadBets(); loadPromos(); loadAdmins(); loadHouse(); loadGeoExempt(); }, []);

  const setRole = async (identifier, makeAdmin) => {
    if (!identifier.trim()) { toast.error("Enter a player email or username"); return; }
    try {
      const { data } = await api.post("/owner/set-role", { identifier: identifier.trim(), make_admin: makeAdmin });
      toast.success(`${data.username} is now ${data.role === "admin" ? "an admin" : "a player"}`);
      setStaffId(""); loadAdmins();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const createPromo = async () => {
    if (!pf.title.trim()) { toast.error("Enter a title"); return; }
    try {
      await api.post("/owner/promotions", {
        title: pf.title.trim(), description: pf.description.trim(), promo_type: pf.promo_type,
        match_pct: parseFloat(pf.match_pct) || 0, max_bonus: parseFloat(pf.max_bonus) || 0,
        min_deposit: parseFloat(pf.min_deposit) || 0, playthrough_mult: Math.min(20, Math.max(0, parseFloat(pf.playthrough) || 0)),
        bet_min: parseFloat(pf.bet_min) || 0, bonus_amount: parseFloat(pf.bonus_amount) || 0,
        free_bet_denom: parseFloat(pf.free_bet_denom) || 0,
        restrict: pf.restrict, expires_at: pf.expires_at ? new Date(pf.expires_at + "T23:59:59").toISOString() : null, active: true,
      });
      toast.success("Promotion created");
      setPf({ promo_type: "deposit_match", title: "", description: "", match_pct: "100", max_bonus: "", min_deposit: "10", playthrough: "1", restrict: "all", expires_at: "", bet_min: "25", bonus_amount: "300", free_bet_denom: "25" });
      loadPromos();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const togglePromo = async (id) => { try { await api.post(`/owner/promotions/${id}/toggle`); loadPromos(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const deletePromo = async (id) => { if (!(await confirm({ title: "Delete promotion", body: "Delete this promotion?", confirmText: "Delete", danger: true }))) return; try { await api.delete(`/owner/promotions/${id}`); loadPromos(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };

  const savePct = async () => {
    const v = parseFloat(pct);
    if (isNaN(v) || v < 0 || v > 100) { toast.error("Enter 0-100"); return; }
    try { const { data } = await api.post("/owner/referral-pct", { pct: v }); setSavedPct(data.referral_pct); toast.success(`Referral reward set to ${data.referral_pct}%`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const saveEdges = async () => {
    const games = ["dice", "coinflip", "mines", "crash", "hilo"];
    for (const g of games) {
      const v = parseFloat(edges[g]);
      if (isNaN(v) || v < 0 || v > 50) { toast.error(`${g}: enter 0-50`); return; }
    }
    setSavingEdges(true);
    try {
      for (const g of games) {
        const v = parseFloat(edges[g]);
        if (String(v) !== String(savedEdges[g])) {
          await api.post("/owner/house-edge", { pct: v, game: g });
        }
      }
      toast.success("House edges saved (PCash play)");
      loadConfig();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSavingEdges(false); }
  };
  const grantBonus = async () => {
    const amt = parseFloat(bonus.amt);
    if (!bonus.id.trim() || isNaN(amt) || amt <= 0) { toast.error("Enter player + amount"); return; }
    try { const { data } = await api.post("/owner/grant-bonus", { identifier: bonus.id.trim(), amount: amt, playthrough_mult: Math.min(20, Math.max(0, parseFloat(bonus.pt) || 0)), restrict: bonus.restrict }); toast.success(`Gave $${amt} bonus to ${data.username}`); setBonus({ id: "", amt: "", pt: "1", restrict: "all" }); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const giveRake = async () => {
    const amt = parseFloat(rake.amt);
    if (!rake.id.trim() || isNaN(amt) || amt <= 0) { toast.error("Enter player + amount"); return; }
    try { const { data } = await api.post("/owner/rakeback", { identifier: rake.id.trim(), amount: amt, currency: rake.cur }); toast.success(`Rakeback $${amt} (${data.currency}) to ${data.username}`); setRake({ id: "", amt: "", cur: "cash" }); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const money = (c, n) => (c === "coins" ? `${(n || 0).toFixed(2)} PCoins` : `$${(n || 0).toFixed(2)}`);
  const KIND = { casino: "bg-[#00D632]/15 text-[#00D632]", live_baccarat: "bg-white/5 text-[#00D632]", sportsbook: "bg-white/5 text-[#a1a1aa]" };
  const filtered = q.trim() ? bets.filter((b) => `${b.user} ${b.game} ${b.kind}`.toLowerCase().includes(q.trim().toLowerCase())) : bets;

  /* AD-05f/AD-M13 settings-row: label (flex-1) on the left, control(s) on the right.
     `top` = draw the #1F1F23 divider above the row (every row except the first in a group). */
  const SRow = ({ label, hint, top = true, children, ...rest }) => (
    <div
      className={`flex flex-col gap-2 py-2.5 sm:flex-row sm:items-center sm:gap-3 ${top ? "border-t border-[#1F1F23]" : ""}`}
      {...rest}
    >
      <div className="min-w-0 sm:flex-1">
        <span className="text-[13px] font-semibold text-white">{label}</span>
        {hint && <p className="mt-0.5 text-[11px] font-medium leading-tight text-[#71717a]">{hint}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-2 sm:shrink-0 sm:justify-end">{children}</div>
    </div>
  );
  /* boxed input pill: bg #0A0A0A + inset ring #26262B, Manrope 12px bold (AD-05f value box) */
  const pill = "h-9 rounded-lg border-0 bg-[#0A0A0A] px-3 font-display text-[12px] font-bold text-white placeholder:font-medium placeholder:text-[#52525b] focus:outline-none focus:ring-1 focus:ring-[#00D632]/40";
  const pillRing = { boxShadow: "inset 0 0 0 1px #26262B" };
  const selPill = "h-9 rounded-lg border-0 bg-[#0A0A0A] px-2.5 font-display text-[12px] font-bold text-white focus:outline-none focus:ring-1 focus:ring-[#00D632]/40";
  const seg = (on) => `rounded-lg px-2.5 py-[7px] text-[11px] font-bold uppercase ${on ? "bg-[#00D632]/12 text-[#00D632]" : "text-[#a1a1aa]"}`;
  const segStyle = (on) => ({ boxShadow: on ? "inset 0 0 0 1px rgba(0,214,50,.4)" : "inset 0 0 0 1px #26262B" });

  return (
    <div className="space-y-5">
      {/* Owner-only banner (AD-M13 header note, applied to both breakpoints) */}
      <div className="flex items-center gap-2.5 rounded-[14px] border border-[#1F1F23] bg-[#111114] px-3.5 py-2.5">
        <Icon name="audit" size={16} style={{ color: "#E4E4E7" }} />
        <span className="text-[13px] font-semibold text-[#E4E4E7]">Platform controls · changes are audit-logged</span>
      </div>

      {/* Economics — referral %, per-game house edges, rakeback (AD-05f settings rows) */}
      <Panel className="flex flex-col gap-0 p-[18px]" data-testid="owner-econ-card">
        <div className="flex items-center gap-2">
          <Icon name="pnl" size={16} style={{ color: "#E4E4E7" }} />
          <span className="font-display text-[16px] font-extrabold text-[#E4E4E7]">Economics</span>
        </div>

        {/* Referral reward % — /owner/referral-pct */}
        <SRow
          data-testid="owner-referral-row"
          label="Referral reward"
          hint={savedPct != null ? `Currently paying ${savedPct}% to the referrer.` : "Percent paid to the referrer."}
        >
          <div className="flex items-center rounded-lg bg-[#0A0A0A] pr-2.5" style={pillRing}>
            <input data-testid="owner-referral-pct" type="number" min="0" max="100" step="0.5" value={pct} onChange={(e) => setPct(e.target.value)} placeholder="0" className={`${pill} w-[72px]`} style={{ boxShadow: "none" }} />
            <span className="font-display text-[12px] font-bold text-[#a1a1aa]">%</span>
          </div>
          <BtnPrimary data-testid="owner-referral-save" onClick={savePct} className="h-9 px-3">Save</BtnPrimary>
        </SRow>

        {/* Per-game house edge — /owner/house-edge (PCash play only) */}
        <SRow
          data-testid="owner-edge-row"
          label="Casino house edge"
          hint="Applies to PCash play only; PCoins play stays fair (0%). Table games keep their built-in edge. P/L = house profit from cash play."
        >
          <div className="flex flex-col items-end gap-2" data-testid="owner-edge-card">
            {[["dice", "Dice"], ["coinflip", "Coin Flip"], ["mines", "Mines"], ["crash", "Crash"], ["hilo", "HiLo"]].map(([g, label]) => (
              <div key={g} className="flex items-center gap-2">
                <span className="w-16 shrink-0 text-right text-[12px] font-semibold text-[#a1a1aa]">{label}</span>
                <div className="flex items-center rounded-lg bg-[#0A0A0A] pr-2.5" style={pillRing}>
                  <input data-testid={`owner-edge-${g}`} type="number" min="0" max="50" step="0.5"
                    value={edges[g] ?? ""} onChange={(e) => setEdges((s) => ({ ...s, [g]: e.target.value }))}
                    placeholder="0" className={`${pill} w-[64px]`} style={{ boxShadow: "none" }} />
                  <span className="font-display text-[12px] font-bold text-[#a1a1aa]">%</span>
                </div>
                <span className="w-[104px] text-right text-[11px] leading-tight" data-testid={`owner-edge-pl-${g}`}>
                  {houseGames[g] ? (
                    <>
                      <b className={houseGames[g].profit >= 0 ? "text-[#00D632]" : "text-[#EF4444]"}>{houseGames[g].profit >= 0 ? "+" : "−"}${Math.abs(houseGames[g].profit).toFixed(2)}</b>
                      <span className="block text-[#71717a]">{houseGames[g].plays.toLocaleString()} plays</span>
                    </>
                  ) : <span className="text-[#71717a]">no cash plays</span>}
                </span>
              </div>
            ))}
            <BtnPrimary data-testid="owner-edge-save" disabled={savingEdges} onClick={saveEdges} className="h-9 px-3">{savingEdges ? "Saving…" : "Save house edges"}</BtnPrimary>
          </div>
        </SRow>

        {/* Rakeback — /owner/rakeback */}
        <SRow
          data-testid="owner-rake-row"
          label="Rakeback (high rollers)"
          hint="Credit a player any amount, as cash or bonus."
        >
          <div className="flex flex-col items-stretch gap-2 sm:items-end" data-testid="owner-rake-card">
            <input data-testid="owner-rake-id" value={rake.id} onChange={(e) => setRake((s) => ({ ...s, id: e.target.value }))} placeholder="Player email or username" className={`${pill} w-full sm:w-[240px]`} style={pillRing} />
            <div className="flex flex-wrap items-center justify-end gap-2">
              <input data-testid="owner-rake-amt" type="number" value={rake.amt} onChange={(e) => setRake((s) => ({ ...s, amt: e.target.value }))} placeholder="Amount ($)" className={`${pill} w-[120px]`} style={pillRing} />
              {["cash", "bonus"].map((c) => (
                <button key={c} data-testid={`owner-rake-cur-${c}`} onClick={() => setRake((s) => ({ ...s, cur: c }))} className={seg(rake.cur === c)} style={segStyle(rake.cur === c)}>{c}</button>
              ))}
              <BtnPrimary data-testid="owner-rake-give" onClick={giveRake} className="h-9 px-3">Give</BtnPrimary>
            </div>
          </div>
        </SRow>
      </Panel>

      {/* Bonuses — grant bonus / free funds (/owner/grant-bonus) */}
      <Panel className="flex flex-col gap-0 p-[18px]" data-testid="owner-bonus-card">
        <div className="flex items-center gap-2">
          <Icon name="sparkles" size={16} style={{ color: "#E4E4E7" }} />
          <span className="font-display text-[16px] font-extrabold text-[#E4E4E7]">Bonuses</span>
        </div>
        <SRow
          data-testid="owner-bonus-row"
          label="Grant bonus / free funds"
          hint="Playthrough × (wager bonus × N to unlock as cash, up to 20×), or 0 = free play that unlocks on the first wager."
        >
          <div className="flex flex-col items-stretch gap-2 sm:items-end">
            <input data-testid="owner-bonus-id" value={bonus.id} onChange={(e) => setBonus((s) => ({ ...s, id: e.target.value }))} placeholder="Player email or username" className={`${pill} w-full sm:w-[240px]`} style={pillRing} />
            <div className="flex flex-wrap items-center justify-end gap-2">
              <input data-testid="owner-bonus-amt" type="number" value={bonus.amt} onChange={(e) => setBonus((s) => ({ ...s, amt: e.target.value }))} placeholder="Amount ($)" className={`${pill} w-[120px]`} style={pillRing} />
              <input data-testid="owner-bonus-pt" type="number" min="0" max="20" value={bonus.pt} onChange={(e) => setBonus((s) => ({ ...s, pt: e.target.value }))} placeholder="Playthrough × (0-20)" className={`${pill} w-[150px]`} style={pillRing} />
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <select data-testid="owner-bonus-restrict" name="bonus_restrict" value={bonus.restrict} onChange={(e) => setBonus((s) => ({ ...s, restrict: e.target.value }))} className={`${selPill} w-full sm:w-[240px]`} style={pillRing}>
                <option value="all">Usable everywhere</option>
                <option value="live_casino">Live Casino only (Blackjack & Baccarat)</option>
              </select>
              <BtnPrimary data-testid="owner-bonus-grant" onClick={grantBonus} className="h-9 px-3">Give bonus</BtnPrimary>
            </div>
          </div>
        </SRow>
      </Panel>

      {/* Promotions — list + create + toggle + delete (/owner/promotions) */}
      <Panel className="flex flex-col gap-0 p-[18px]" data-testid="owner-promos-card">
        <div className="flex items-center gap-2">
          <Icon name="promotions" size={16} style={{ color: "#E4E4E7" }} />
          <span className="font-display text-[16px] font-extrabold text-[#E4E4E7]">Promotions</span>
          <span className="rounded-full bg-white/[.06] px-2 py-[3px] text-[11px] font-bold text-[#E4E4E7]">{promos.length}</span>
        </div>

        {/* Create promotion — presented as one settings row with its fields boxed */}
        <SRow
          data-testid="promo-create-row"
          label="Create promotion"
          hint="Deposit match applies to coin purchases. Bet & Get gives a new player a one-time bonus after their first qualifying PCash bet (e.g. Bet $25, Get $300)."
        >
          <div className="flex w-full flex-col items-stretch gap-2 sm:w-auto sm:min-w-[320px]">
            <select data-testid="promo-type" name="promo_type" value={pf.promo_type} onChange={(e) => setPf((s) => ({ ...s, promo_type: e.target.value }))} className={`${selPill} w-full`} style={pillRing}>
              <option value="deposit_match">Deposit Match (on purchase)</option>
              <option value="bet_and_get">Bet &amp; Get (first bet bonus)</option>
            </select>
            <input data-testid="promo-title" value={pf.title} onChange={(e) => setPf((s) => ({ ...s, title: e.target.value }))} placeholder={pf.promo_type === "bet_and_get" ? "Title e.g. Bet $25 Get $300" : "Title e.g. 100% Match"} className={`${pill} w-full`} style={pillRing} />
            <input data-testid="promo-desc" value={pf.description} onChange={(e) => setPf((s) => ({ ...s, description: e.target.value }))} placeholder="Short description" className={`${pill} w-full`} style={pillRing} />
            <div className="grid grid-cols-2 gap-2">
              {pf.promo_type === "bet_and_get" ? (
                <>
                  <input data-testid="promo-betmin" type="number" value={pf.bet_min} onChange={(e) => setPf((s) => ({ ...s, bet_min: e.target.value }))} placeholder="Qualifying bet $ (e.g. 25)" className={`${pill} w-full`} style={pillRing} />
                  <input data-testid="promo-bonusamt" type="number" value={pf.bonus_amount} onChange={(e) => setPf((s) => ({ ...s, bonus_amount: e.target.value }))} placeholder="Total free bets $ (e.g. 300)" className={`${pill} w-full`} style={pillRing} />
                  <input data-testid="promo-freebetdenom" type="number" min="0" value={pf.free_bet_denom} onChange={(e) => setPf((s) => ({ ...s, free_bet_denom: e.target.value }))} placeholder="Each free bet $ (e.g. 25)" className={`${pill} w-full`} style={pillRing} />
                </>
              ) : (
                <>
                  <input data-testid="promo-pct" type="number" value={pf.match_pct} onChange={(e) => setPf((s) => ({ ...s, match_pct: e.target.value }))} placeholder="Match %" className={`${pill} w-full`} style={pillRing} />
                  <input data-testid="promo-max" type="number" value={pf.max_bonus} onChange={(e) => setPf((s) => ({ ...s, max_bonus: e.target.value }))} placeholder="Max $ (0=none)" className={`${pill} w-full`} style={pillRing} />
                  <input data-testid="promo-min" type="number" value={pf.min_deposit} onChange={(e) => setPf((s) => ({ ...s, min_deposit: e.target.value }))} placeholder="Min deposit $" className={`${pill} w-full`} style={pillRing} />
                  <input data-testid="promo-playthrough" type="number" min="0" max="20" value={pf.playthrough} onChange={(e) => setPf((s) => ({ ...s, playthrough: e.target.value }))} placeholder="Playthrough × (0-20)" className={`${pill} w-full`} style={pillRing} />
                </>
              )}
            </div>
            <select data-testid="promo-restrict" name="promo_restrict" value={pf.restrict} onChange={(e) => setPf((s) => ({ ...s, restrict: e.target.value }))} className={`${selPill} w-full`} style={pillRing}>
              <option value="all">Bonus usable everywhere</option>
              <option value="live_casino">Bonus for Live Casino only (Blackjack & Baccarat)</option>
            </select>
            <div className="flex items-center gap-2">
              <label className="whitespace-nowrap text-[12px] font-semibold text-[#a1a1aa]">Expires</label>
              <input data-testid="promo-expires" type="date" value={pf.expires_at} onChange={(e) => setPf((s) => ({ ...s, expires_at: e.target.value }))} className={`${pill} w-full`} style={pillRing} />
            </div>
            <BtnPrimary data-testid="promo-create" onClick={createPromo} className="h-9 justify-center px-3">Create promotion</BtnPrimary>
          </div>
        </SRow>

        {/* Live promotions list */}
        {promos.length === 0 ? (
          <div className="border-t border-[#1F1F23]"><EmptyRow>No promotions yet.</EmptyRow></div>
        ) : (
          <div data-testid="promo-list">
            {promos.map((p) => (
              <div key={p.id} data-testid={`promo-${p.id}`} className="flex flex-col gap-2 border-t border-[#1F1F23] py-2.5 sm:flex-row sm:items-center sm:gap-3">
                <div className="min-w-0 sm:flex-1">
                  <p className="text-[13px] font-semibold text-white">{p.title} {p.promo_type === "bet_and_get" ? <span className="text-[#00D632]">Bet ${p.bet_min} → {p.free_bet_denom > 0 ? `${Math.max(1, Math.round((p.bonus_amount || 0) / p.free_bet_denom))} × $${p.free_bet_denom} free bets` : `$${p.bonus_amount} free bet`}</span> : <><span className="text-[#00D632]">{p.match_pct}%</span>{p.max_bonus ? <span className="text-[#a1a1aa]"> up to ${p.max_bonus}</span> : null}</>}</p>
                  <p className="truncate text-[11px] font-medium text-[#71717a]">{p.description || (p.promo_type === "bet_and_get" ? `First PCash bet ≥ $${p.bet_min}` : `Min deposit $${p.min_deposit}`)} · {(p.playthrough_mult ?? 1) > 0 ? `${p.playthrough_mult ?? 1}× playthrough` : "No playthrough"}{p.restrict === "live_casino" ? " · Live Casino only" : ""}{p.expires_at ? ` · Expires ${new Date(p.expires_at).toLocaleDateString()}` : ""}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <button data-testid={`promo-toggle-${p.id}`} onClick={() => togglePromo(p.id)} className={`rounded-lg px-2.5 py-[7px] text-[11px] font-bold uppercase ${p.active ? "bg-[#00D632]/12 text-[#00D632]" : "text-[#a1a1aa]"}`} style={{ boxShadow: p.active ? "inset 0 0 0 1px rgba(0,214,50,.4)" : "inset 0 0 0 1px #26262B" }}>{p.active ? "Active" : "Off"}</button>
                  <BtnDanger data-testid={`promo-delete-${p.id}`} onClick={() => deletePromo(p.id)}><Icon name="x" size={14} /></BtnDanger>
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      {/* Staff / Admins — set-role + admin list (/owner/set-role, /owner/admins) */}
      <Panel className="flex flex-col gap-0 p-[18px]" data-testid="owner-staff-card">
        <div className="flex items-center gap-2">
          <Icon name="account" size={16} style={{ color: "#E4E4E7" }} />
          <span className="font-display text-[16px] font-extrabold text-[#E4E4E7]">Staff &amp; admins</span>
          <span className="rounded-full bg-white/[.06] px-2 py-[3px] text-[11px] font-bold text-[#E4E4E7]">{admins.length}</span>
        </div>

        <SRow
          data-testid="owner-staff-row"
          label="Manage admin access"
          hint="Promote a player to admin (full admin panel access) or remove admin rights."
        >
          <div className="flex flex-col items-stretch gap-2 sm:items-end">
            <input data-testid="owner-staff-id" value={staffId} onChange={(e) => setStaffId(e.target.value)} placeholder="Player email or username" className={`${pill} w-full sm:w-[240px]`} style={pillRing} />
            <div className="flex flex-wrap items-center justify-end gap-2">
              <BtnGhost data-testid="owner-make-admin" onClick={() => setRole(staffId, true)} className="h-9 px-3">Make admin</BtnGhost>
              <BtnDanger data-testid="owner-remove-admin" onClick={() => setRole(staffId, false)} className="h-9 px-3">Remove admin</BtnDanger>
            </div>
          </div>
        </SRow>

        <div data-testid="owner-admin-list">
          {admins.length === 0 ? (
            <div className="border-t border-[#1F1F23]"><EmptyRow>No staff yet.</EmptyRow></div>
          ) : admins.map((a) => (
            <div key={a.id} data-testid={`owner-admin-${a.id}`} className="flex items-center gap-3 border-t border-[#1F1F23] py-2.5">
              <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-white">{a.username} <span className="font-medium text-[#71717a]">· {a.email}</span></span>
              <span className="shrink-0 rounded-lg bg-[#0A0A0A] px-2.5 py-[5px] font-display text-[11px] font-bold uppercase" style={pillRing}><span className={a.role === "owner" ? "text-[#00D632]" : "text-[#a1a1aa]"}>{a.role}</span></span>
            </div>
          ))}
        </div>
      </Panel>

      {/* Geo-block exemptions — list + revoke (/admin/users, /admin/users/{id}/geo-exempt) */}
      <Panel className="flex flex-col gap-0 p-[18px]" data-testid="owner-geo-exempt-card">
        <div className="flex items-center gap-2">
          <Icon name="shield" size={16} style={{ color: "#E4E4E7" }} />
          <span className="font-display text-[16px] font-extrabold text-[#E4E4E7]">Geo-block exemptions</span>
          <span className="rounded-full bg-white/[.06] px-2 py-[3px] text-[11px] font-bold text-[#E4E4E7]" data-testid="geo-exempt-count">{geoExempt.length}</span>
        </div>
        <p className="mt-1 text-[11px] font-medium text-[#71717a]">Accounts allowed to bypass the state geo-block (e.g. trusted/QA testers). Grant a bypass from the <b>Users</b> tab; revoke here or there.</p>
        {geoExempt.length === 0 ? (
          <div className="mt-2 border-t border-[#1F1F23]" data-testid="geo-exempt-empty"><EmptyRow>No accounts are currently geo-exempt.</EmptyRow></div>
        ) : (
          <div className="mt-1" data-testid="geo-exempt-list">
            {geoExempt.map((u) => (
              <div key={u.id} data-testid={`geo-exempt-row-${u.id}`} className="flex items-center gap-3 border-t border-[#1F1F23] py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-semibold text-white">{u.username} <span className="font-medium text-[#71717a]">· {u.email}</span></p>
                  {u.last_ip && <p className="text-[10px] font-medium text-[#52525b]">Last IP {u.last_ip}</p>}
                </div>
                <BtnDanger data-testid={`geo-exempt-revoke-${u.id}`} onClick={() => revokeGeo(u)} className="h-9 shrink-0 px-3"><Icon name="x" size={12} className="mr-1" /> Revoke</BtnDanger>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

/* -------- Casino Game Log (player wins/losses) -------- */
function CasinoLogTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const load = () => { setLoading(true); api.get("/admin/casino-history").then(({ data }) => setRows(data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  const money = (c, n) => c === "cash" ? `$${(n || 0).toFixed(2)}` : `${(n || 0).toFixed(2)} PCoins`;
  // Prettify game keys: live_blackjack → "Live Blackjack"
  const gameLabel = (g) => (g || "").replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
  const RESULT = {
    win: { bg: "rgba(0,214,50,.15)", fg: "#00D632" }, blackjack: { bg: "rgba(0,214,50,.15)", fg: "#00D632" }, even_money: { bg: "rgba(0,214,50,.15)", fg: "#00D632" },
    lose: { bg: "rgba(239,68,68,.15)", fg: "#EF4444" }, push: { bg: "#27272A", fg: "#a1a1aa" }, surrender: { bg: "rgba(255,255,255,.05)", fg: "#a1a1aa" },
    refund: { bg: "rgba(255,255,255,.05)", fg: "#a1a1aa" },
  };
  const resultStyle = (r) => RESULT[r] || { bg: "#27272A", fg: "#a1a1aa" };
  const netColor = (n) => (n > 0 ? "#00D632" : n < 0 ? "#EF4444" : "#a1a1aa");
  const netStr = (r) => `${r.net > 0 ? "+" : r.net < 0 ? "−" : ""}${money(r.currency, Math.abs(r.net || 0))}`;
  const filtered = q.trim() ? rows.filter((r) => `${r.username} ${r.game} ${r.result}`.toLowerCase().includes(q.trim().toLowerCase())) : rows;
  const totals = filtered.reduce((a, r) => {
    const c = r.currency === "cash" ? "cash" : "coins";
    a[c].bet += r.bet || 0; a[c].net += r.net || 0; return a;
  }, { cash: { bet: 0, net: 0 }, coins: { bet: 0, net: 0 } });

  const cols = "1.4fr 1.1fr 1fr 90px 90px 100px 84px";
  const header = [
    "When", "Player", "Game",
    { label: "Bet", right: true }, { label: "Payout", right: true }, { label: "Won / Lost", right: true }, "Result",
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <span className="font-display text-[18px] font-extrabold">Casino log</span>
          <p className="text-[12px] font-medium text-[#71717a]">Every player's stake, result and win/loss across all casino games (incl. Live Blackjack &amp; mid-hand refunds), newest first.</p>
        </div>
        <div className="ml-auto flex gap-2">
          <SearchBox data-testid="casinolog-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by player, game, result…" className="w-56" />
          <BtnGhost data-testid="casinolog-refresh" onClick={load}>Refresh</BtnGhost>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
        <StatTile label="Cash · wagered / player net" iconColor="#00D632"
          value={<span className="text-[18px]"><span className="text-white">${totals.cash.bet.toFixed(2)}</span> <span className="text-[13px] font-semibold text-[#71717a]">wagered</span></span>} />
        <StatTile label="Coins · wagered / player net" iconColor="#00D632"
          value={<span className="text-[18px]"><span className="text-white">{totals.coins.bet.toFixed(2)}</span> <span className="text-[13px] font-semibold text-[#71717a]">wagered</span></span>} />
      </div>
      <div className="-mt-2 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
        <p className="px-1 text-[12px] font-bold tabular-nums" style={{ color: netColor(totals.cash.net) }} data-testid="casinolog-total-cash">
          players {totals.cash.net >= 0 ? "+" : "−"}${Math.abs(totals.cash.net).toFixed(2)} <span className="font-normal text-[#71717a]">(house {totals.cash.net <= 0 ? "+" : "−"}${Math.abs(totals.cash.net).toFixed(2)})</span>
        </p>
        <p className="px-1 text-[12px] font-bold tabular-nums" style={{ color: netColor(totals.coins.net) }} data-testid="casinolog-total-coins">
          players {totals.coins.net >= 0 ? "+" : "−"}{Math.abs(totals.coins.net).toFixed(2)}
        </p>
      </div>

      {/* Desktop: DataTable (AD-05d) */}
      <Panel className="hidden overflow-hidden bg-[#111114] p-0 md:block">
        <DataTable cols={cols} header={header} className="rounded-none border-0">
          {loading ? (
            <Loading />
          ) : filtered.length === 0 ? (
            <div data-testid="casinolog-empty"><EmptyRow>No casino plays{q ? " match your filter" : " yet"}.</EmptyRow></div>
          ) : (
            filtered.map((r) => {
              const rs = resultStyle(r.result);
              return (
                <TRow key={r.id} cols={cols} data-testid={`casinolog-row-${r.id}`}>
                  <span className="text-[12px] text-[#a1a1aa]">{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</span>
                  <span className="font-semibold text-white">{r.username || "—"}</span>
                  <span className="truncate text-[#a1a1aa]">{r.game ? gameLabel(r.game) : "—"}</span>
                  <span className="text-right tabular-nums">{money(r.currency, r.bet)}</span>
                  <span className="text-right tabular-nums text-[#a1a1aa]">{money(r.currency, r.payout)}</span>
                  <span className="text-right font-display font-extrabold tabular-nums" style={{ color: netColor(r.net) }} data-testid={`casinolog-net-${r.id}`}>{netStr(r)}</span>
                  <StatusPill status={r.result || "—"} bg={rs.bg} fg={rs.fg} />
                </TRow>
              );
            })
          )}
        </DataTable>
      </Panel>

      {/* Mobile: stacked label:value cards (AD-M11) */}
      <div className="flex flex-col gap-3 md:hidden">
        {loading ? (
          <Loading />
        ) : filtered.length === 0 ? (
          <div data-testid="casinolog-empty-m"><EmptyRow>No casino plays{q ? " match your filter" : " yet"}.</EmptyRow></div>
        ) : (
          filtered.map((r) => (
            <Panel key={r.id} className="flex flex-col gap-2.5 bg-[#111114] p-3" data-testid={`casinolog-row-m-${r.id}`}>
              <div className="flex items-center gap-2.5">
                <Icon name="casino" size={16} className="text-[#00D632]" />
                <span className="flex-1 truncate font-display text-[15px] font-bold text-white">{r.game ? gameLabel(r.game) : "—"}</span>
                <span className="font-display text-[16px] font-extrabold tabular-nums" style={{ color: netColor(r.net) }} data-testid={`casinolog-net-m-${r.id}`}>{netStr(r)}</span>
              </div>
              <div className="grid grid-cols-2 gap-2 border-t border-[#1F1F23] pt-2.5">
                <div className="min-w-0">
                  <div className="text-[10px] font-semibold tracking-[.12em] text-[#71717a]">PLAYER</div>
                  <div className="mt-0.5 truncate font-display text-[14px] font-extrabold text-white">{r.username || "—"}</div>
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] font-semibold tracking-[.12em] text-[#71717a]">BET</div>
                  <div className="mt-0.5 truncate font-display text-[14px] font-extrabold text-white">{money(r.currency, r.bet)}</div>
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] font-semibold tracking-[.12em] text-[#71717a]">PAYOUT</div>
                  <div className="mt-0.5 truncate font-display text-[14px] font-extrabold text-[#a1a1aa]">{money(r.currency, r.payout)}</div>
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] font-semibold tracking-[.12em] text-[#71717a]">WHEN</div>
                  <div className="mt-0.5 truncate text-[12px] text-[#a1a1aa]">{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</div>
                </div>
              </div>
              <div>
                <StatusPill status={r.result || "—"} bg={resultStyle(r.result).bg} fg={resultStyle(r.result).fg} />
              </div>
            </Panel>
          ))
        )}
      </div>
    </div>
  );
}

/* Unified "every bet placed" feed (Casino + Live Baccarat + Sportsbook) — lives on
   the All Bets tab. Moved here from the Platform screen. */
function AllBetsFeed() {
  const [bets, setBets] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | pending | won | lost | voided
  const [loading, setLoading] = useState(true);
  const load = () => { setLoading(true); api.get("/owner/all-bets").then(({ data }) => setBets(data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  const KIND = { casino: "bg-[#00D632]/15 text-[#00D632]", live_baccarat: "bg-white/5 text-[#00D632]", sportsbook: "bg-white/5 text-[#a1a1aa]" };
  const money = (c, n) => c === "cash" ? `$${(n || 0).toFixed(2)}` : `${(n || 0).toFixed(2)} PCoins`;
  // status pill colouring keyed off the heterogeneous `result` field (casino + baccarat + sportsbook)
  const RESULT = {
    won: { bg: "rgba(0,214,50,.15)", fg: "#00D632" }, win: { bg: "rgba(0,214,50,.15)", fg: "#00D632" }, blackjack: { bg: "rgba(0,214,50,.15)", fg: "#00D632" }, even_money: { bg: "rgba(0,214,50,.15)", fg: "#00D632" },
    lost: { bg: "rgba(239,68,68,.15)", fg: "#EF4444" }, lose: { bg: "rgba(239,68,68,.15)", fg: "#EF4444" },
    pending: { bg: "rgba(0,214,50,.12)", fg: "#00D632" },
    void: { bg: "#26262B", fg: "#d4d4d8" }, voided: { bg: "#26262B", fg: "#d4d4d8" }, push: { bg: "#26262B", fg: "#a1a1aa" }, surrender: { bg: "rgba(255,255,255,.05)", fg: "#a1a1aa" }, refund: { bg: "rgba(255,255,255,.05)", fg: "#a1a1aa" },
  };
  const resultStyle = (r) => RESULT[String(r || "").toLowerCase()] || { bg: "#26262B", fg: "#a1a1aa" };
  // buckets map many raw result values onto the mockup's filter chips
  const bucket = (r) => {
    const v = String(r || "").toLowerCase();
    if (["won", "win", "blackjack", "even_money"].includes(v)) return "won";
    if (["lost", "lose"].includes(v)) return "lost";
    if (["void", "voided", "cancelled", "canceled", "refund"].includes(v)) return "voided";
    if (v === "pending") return "pending";
    return "other";
  };
  const counts = bets.reduce((a, b) => { const k = bucket(b.result); a[k] = (a[k] || 0) + 1; return a; }, {});
  const byStatus = filter === "all" ? bets : bets.filter((b) => bucket(b.result) === filter);
  const filtered = q.trim() ? byStatus.filter((b) => `${b.user} ${b.game} ${b.kind}`.toLowerCase().includes(q.trim().toLowerCase())) : byStatus;

  const cols = "1.4fr 1fr 110px 1.2fr 90px 90px 84px";
  const header = [
    "When", "Player", "Product", "Game",
    { label: "Bet", right: true }, { label: "Payout", right: true }, "Result",
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <span className="font-display text-[18px] font-extrabold">All bets</span>
          <p className="text-[12px] font-medium text-[#71717a]">Every bet placed across all products — Casino, Live Baccarat and Sportsbook, newest first.</p>
        </div>
        <div className="ml-auto flex gap-2">
          <SearchBox data-testid="owner-allbets-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by player, game…" className="w-56" />
          <BtnGhost data-testid="owner-allbets-refresh" onClick={load}>Refresh</BtnGhost>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Chip active={filter === "all"} onClick={() => setFilter("all")} data-testid="owner-allbets-filter-all">All {bets.length}</Chip>
        <Chip active={filter === "pending"} onClick={() => setFilter("pending")} data-testid="owner-allbets-filter-pending">Pending {counts.pending || 0}</Chip>
        <Chip active={filter === "won"} onClick={() => setFilter("won")} data-testid="owner-allbets-filter-won">Won {counts.won || 0}</Chip>
        <Chip active={filter === "lost"} onClick={() => setFilter("lost")} data-testid="owner-allbets-filter-lost">Lost {counts.lost || 0}</Chip>
        <Chip active={filter === "voided"} onClick={() => setFilter("voided")} data-testid="owner-allbets-filter-voided">Voided {counts.voided || 0}</Chip>
      </div>

      {/* Desktop: DataTable (AD-05c) */}
      <Panel className="hidden overflow-hidden bg-[#111114] p-0 md:block">
        <DataTable cols={cols} header={header} className="rounded-none border-0">
          {loading ? (
            <Loading />
          ) : filtered.length === 0 ? (
            <div data-testid="owner-allbets-empty"><EmptyRow>No bets{q || filter !== "all" ? " match your filter" : " yet"}.</EmptyRow></div>
          ) : (
            filtered.slice(0, 500).map((b, i) => {
              const rs = resultStyle(b.result);
              return (
                <TRow key={i} cols={cols} data-testid={`owner-allbets-row-${i}`}>
                  <span className="text-[12px] text-[#a1a1aa]">{b.created_at ? new Date(b.created_at).toLocaleString() : "—"}</span>
                  <span className="truncate font-semibold text-white">{b.user || "—"}</span>
                  <span><span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${KIND[b.kind] || "bg-[#26262B] text-[#a1a1aa]"}`}>{(b.kind || "—").replace(/_/g, " ")}</span></span>
                  <span className="truncate capitalize text-[#a1a1aa]">{b.game ? b.game.replace(/_/g, " ") : "—"}</span>
                  <span className="text-right tabular-nums">{money(b.currency, b.bet)}</span>
                  <span className="text-right tabular-nums text-[#a1a1aa]">{money(b.currency, b.payout)}</span>
                  <StatusPill status={b.result || "—"} bg={rs.bg} fg={rs.fg} />
                </TRow>
              );
            })
          )}
        </DataTable>
      </Panel>

      {/* Mobile: stacked label:value cards (AD-M10) */}
      <div className="flex flex-col gap-3 md:hidden">
        {loading ? (
          <Loading />
        ) : filtered.length === 0 ? (
          <div data-testid="owner-allbets-empty-m"><EmptyRow>No bets{q || filter !== "all" ? " match your filter" : " yet"}.</EmptyRow></div>
        ) : (
          filtered.slice(0, 500).map((b, i) => {
            const rs = resultStyle(b.result);
            return (
              <Panel key={i} className="flex flex-col gap-2.5 bg-[#111114] p-3" data-testid={`owner-allbets-row-m-${i}`}>
                <div className="flex items-center gap-2.5">
                  <span className="flex-1 truncate font-display text-[15px] font-bold text-white">{b.user || "—"}</span>
                  <StatusPill status={b.result || "—"} bg={rs.bg} fg={rs.fg} />
                </div>
                <div className="grid grid-cols-2 gap-2 border-t border-[#1F1F23] pt-2.5">
                  <div className="min-w-0">
                    <div className="text-[10px] font-semibold tracking-[.12em] text-[#71717a]">PICK</div>
                    <div className="mt-0.5 truncate font-display text-[14px] font-extrabold capitalize text-white">{b.game ? b.game.replace(/_/g, " ") : "—"}</div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] font-semibold tracking-[.12em] text-[#71717a]">STAKE</div>
                    <div className="mt-0.5 truncate font-display text-[14px] font-extrabold text-white">{money(b.currency, b.bet)}</div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] font-semibold tracking-[.12em] text-[#71717a]">PAYOUT</div>
                    <div className="mt-0.5 truncate font-display text-[14px] font-extrabold text-[#a1a1aa]">{money(b.currency, b.payout)}</div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] font-semibold tracking-[.12em] text-[#71717a]">PRODUCT</div>
                    <div className="mt-0.5 truncate text-[12px] capitalize text-[#a1a1aa]">{(b.kind || "—").replace(/_/g, " ")}</div>
                  </div>
                </div>
                <div className="text-[11px] text-[#71717a]">{b.created_at ? new Date(b.created_at).toLocaleString() : "—"}</div>
              </Panel>
            );
          })
        )}
      </div>
    </div>
  );
}

function BetsTab() {
  const [bets, setBets] = useState([]);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null); // null = showing all bets
  const [searching, setSearching] = useState(false);
  const [editing, setEditing] = useState({}); // user_id -> {coins, cash} | undefined
  const [savingBal, setSavingBal] = useState({});
  const [history, setHistory] = useState({}); // user_id -> list | "loading" | undefined

  const toggleHistory = async (uid) => {
    if (history[uid] && history[uid] !== "loading") { setHistory((p) => ({ ...p, [uid]: undefined })); return; }
    setHistory((p) => ({ ...p, [uid]: "loading" }));
    try {
      const { data } = await api.get(`/admin/users/${uid}/adjustments`);
      setHistory((p) => ({ ...p, [uid]: data }));
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); setHistory((p) => ({ ...p, [uid]: undefined })); }
  };
  useEffect(() => { api.get("/admin/bets").then(({ data }) => setBets(data)); }, []);
  const STYLE = { won: "text-[#00D632]", lost: "text-[#EF4444]", pending: "text-[#00D632]" };
  const money = (c, n) => c === "cash" ? `$${(n || 0).toFixed(2)}` : `${(n || 0).toFixed(2)} PCoins`;

  const search = async (e) => {
    e?.preventDefault();
    const q = query.trim();
    if (q.length < 2) { toast.error("Type at least 2 characters"); return; }
    setSearching(true);
    try {
      const { data } = await api.get(`/admin/bettor-search?q=${encodeURIComponent(q)}`);
      setResults(data.results);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSearching(false); }
  };
  const clear = () => { setQuery(""); setResults(null); };

  const startEdit = (u) => setEditing((p) => ({ ...p, [u.user_id]: { coins: String(u.coins), cash: String(u.cash), note: "" } }));
  const cancelEdit = (uid) => setEditing((p) => ({ ...p, [uid]: undefined }));
  const saveBal = async (uid) => {
    const e = editing[uid];
    const coins = parseFloat(e.coins), cash = parseFloat(e.cash);
    if (isNaN(coins) || isNaN(cash) || coins < 0 || cash < 0) { toast.error("Enter valid, non-negative amounts"); return; }
    setSavingBal((p) => ({ ...p, [uid]: true }));
    try {
      const { data } = await api.post(`/admin/users/${uid}/set-balance`, { coins, cash, note: e.note || "" });
      toast.success("Balance updated");
      setResults((prev) => prev.map((r) => r.user_id === uid ? { ...r, coins: data.coins, cash: data.cash } : r));
      cancelEdit(uid);
      if (history[uid] && history[uid] !== "loading") {
        const { data: h } = await api.get(`/admin/users/${uid}/adjustments`);
        setHistory((p) => ({ ...p, [uid]: h }));
      }
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSavingBal((p) => ({ ...p, [uid]: false })); }
  };

  return (
    <div className="space-y-4">
      <form onSubmit={search} className="flex gap-2">
        <Input data-testid="bettor-search-input" value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder="Search a user by username or email..." className="max-w-sm" />
        <Button type="submit" data-testid="bettor-search-button" disabled={searching}
          className="rounded-full bg-[#00D632] font-bold text-[#0A0A0A] hover:bg-[#00B829]">
          {searching ? "Searching..." : "Search"}
        </Button>
        {results !== null && (
          <Button type="button" variant="outline" data-testid="bettor-search-clear" onClick={clear} className="rounded-full">Clear</Button>
        )}
      </form>

      {results !== null ? (
        results.length === 0 ? (
          <p className="text-sm text-[#a1a1aa]" data-testid="bettor-search-empty">No users match "{query}".</p>
        ) : (
          <div className="space-y-4">
            {results.map((u) => (
              <div key={u.user_id} data-testid={`bettor-result-${u.user_id}`} className="rounded-xl border border-[#27272A] bg-[#141417] p-4">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="font-display font-bold tracking-tight">{u.username} <span className="text-xs font-normal text-[#a1a1aa]">· {u.email}</span></p>
                    {editing[u.user_id] ? (
                      <div className="mt-2 flex flex-wrap items-end gap-2" data-testid={`edit-balance-${u.user_id}`}>
                        <div>
                          <label className="text-[10px] uppercase tracking-[0.15em] text-[#a1a1aa]">Coins</label>
                          <Input type="number" min="0" step="0.01" data-testid={`edit-coins-${u.user_id}`} value={editing[u.user_id].coins}
                            onChange={(ev) => setEditing((p) => ({ ...p, [u.user_id]: { ...p[u.user_id], coins: ev.target.value } }))}
                            className="h-8 w-28 text-sm" />
                        </div>
                        <div>
                          <label className="text-[10px] uppercase tracking-[0.15em] text-[#a1a1aa]">Cash ($)</label>
                          <Input type="number" min="0" step="0.01" data-testid={`edit-cash-${u.user_id}`} value={editing[u.user_id].cash}
                            onChange={(ev) => setEditing((p) => ({ ...p, [u.user_id]: { ...p[u.user_id], cash: ev.target.value } }))}
                            className="h-8 w-28 text-sm" />
                        </div>
                        <div className="min-w-[180px] flex-1">
                          <label className="text-[10px] uppercase tracking-[0.15em] text-[#a1a1aa]">Reason</label>
                          <Input type="text" data-testid={`edit-note-${u.user_id}`} value={editing[u.user_id].note}
                            onChange={(ev) => setEditing((p) => ({ ...p, [u.user_id]: { ...p[u.user_id], note: ev.target.value } }))}
                            placeholder="e.g. promo credit, correction..." className="h-8 text-sm" />
                        </div>
                        <Button size="sm" disabled={savingBal[u.user_id]} data-testid={`save-balance-${u.user_id}`} onClick={() => saveBal(u.user_id)}
                          className="h-8 rounded-full bg-[#00D632] font-bold text-[#0A0A0A] hover:bg-[#00B829]">{savingBal[u.user_id] ? "..." : "Save"}</Button>
                        <Button size="sm" variant="outline" onClick={() => cancelEdit(u.user_id)} className="h-8 rounded-full">Cancel</Button>
                      </div>
                    ) : (
                      <p className="text-xs text-[#a1a1aa]">
                        Balance: {u.coins.toFixed(2)} coins · ${u.cash.toFixed(2)} cash · KYC: {u.kyc_status}
                        <button data-testid={`edit-balance-btn-${u.user_id}`} onClick={() => startEdit(u)}
                          className="ml-2 font-semibold text-[#00D632] underline underline-offset-2">Edit balance</button>
                        <button data-testid={`history-btn-${u.user_id}`} onClick={() => toggleHistory(u.user_id)}
                          className="ml-2 font-semibold text-[#a1a1aa] underline underline-offset-2">Adjustment history</button>
                      </p>
                    )}
                  </div>
                  <div className="text-right text-xs text-[#a1a1aa]">
                    <p><b className="text-white">{u.summary.total_bets}</b> bets · {u.summary.pending} pending</p>
                    <p>Staked: <span className="text-[#00D632]">{u.summary.staked_coins.toFixed(2)} PCoins</span> · ${u.summary.staked_cash.toFixed(2)}</p>
                    <p>Won: <span className="text-[#00D632]">{u.summary.won_coins.toFixed(2)} PCoins</span> · ${u.summary.won_cash.toFixed(2)}</p>
                  </div>
                </div>
                {history[u.user_id] === "loading" && (
                  <div className="mb-3 flex justify-center py-2"><Icon name="refresh" size={16} className="animate-spin text-[#00D632]" /></div>
                )}
                {history[u.user_id] && history[u.user_id] !== "loading" && (
                  <div className="mb-3 rounded-lg border border-[#27272A] bg-[#141417] p-3" data-testid={`history-panel-${u.user_id}`}>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-[#a1a1aa]">Admin balance adjustments</p>
                    {history[u.user_id].length === 0 ? (
                      <p className="text-xs text-[#a1a1aa]">No admin adjustments yet.</p>
                    ) : (
                      <div className="space-y-1">
                        {history[u.user_id].map((h) => (
                          <div key={h.id} className="flex items-start justify-between gap-3 text-xs">
                            <span className="min-w-0 flex-1 text-[#a1a1aa]">
                              {h.created_at ? new Date(h.created_at).toLocaleString() : "—"} · {h.admin_email || "admin"}
                              {h.note && <span className="block italic text-[#71717a]">"{h.note}"</span>}
                            </span>
                            <span className="flex items-center gap-2 tabular-nums whitespace-nowrap">
                              <span className={h.direction === "credit" ? "text-[#00D632]" : "text-[#EF4444]"}>
                                {h.direction === "credit" ? "+" : "−"}{(h.amount || 0).toFixed(2)} {h.currency}
                              </span>
                              {h.old_value != null && h.new_value != null && (
                                <span className="text-[#a1a1aa]">({h.old_value.toFixed(2)} → {h.new_value.toFixed(2)})</span>
                              )}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {u.bets.length === 0 ? (
                  <p className="text-xs text-[#a1a1aa]">This user hasn't placed any bets.</p>
                ) : (
                  <div className="rounded-lg border border-[#27272A]">
                    {u.bets.map((b) => (
                      <div key={b.id} className="flex items-center justify-between gap-3 border-b border-[#27272A] px-3 py-2 text-xs last:border-0">
                        <span className="min-w-0 flex-1 truncate">{b.event_label} — <span className="font-medium">{b.outcome_label}</span></span>
                        <span className="flex items-center gap-3 tabular-nums whitespace-nowrap">
                          <span className={b.currency === "cash" ? "text-white" : "text-[#00D632]"}>{money(b.currency, b.stake)}</span>
                          <span className="text-[#a1a1aa]">@ {b.odds?.toFixed(2)}</span>
                          <span className={`font-semibold uppercase ${STYLE[b.status]}`}>{b.status}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      ) : (
        <div className="space-y-2">
          {bets.length === 0 && <p className="text-sm text-[#a1a1aa]">No bets placed yet.</p>}
          {bets.map((b) => (
            <div key={b.id} className="flex items-center justify-between rounded-lg border border-[#27272A] bg-[#141417] p-3 text-sm">
              <div>
                <span className="font-medium">{b.username}</span> · {b.event_label} — {b.outcome_label}
              </div>
              <div className="flex items-center gap-4">
                <span className="tabular-nums text-[#a1a1aa]">${b.stake.toFixed(2)} @ {b.odds.toFixed(2)}</span>
                <span className={`font-semibold uppercase ${STYLE[b.status]}`}>{b.status}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* -------- Support -------- */
function SupportTab({ onChange }) {
  const [tickets, setTickets] = useState([]);
  const [replies, setReplies] = useState({});
  const [showClosed, setShowClosed] = useState(false);
  const [loading, setLoading] = useState(true);
  const load = () => api.get("/admin/support").then(({ data }) => setTickets(data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const sendReply = async (id) => {
    const reply = (replies[id] || "").trim();
    if (!reply) { toast.error("Type a reply"); return; }
    try {
      await api.post(`/admin/support/${id}/reply`, { reply });
      toast.success("Reply sent");
      setReplies({ ...replies, [id]: "" });
      await load(); onChange();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const close = async (id) => {
    await api.post(`/admin/support/${id}/close`);
    toast.success("Ticket closed");
    await load(); onChange();
  };

  const openTickets = tickets.filter((t) => t.status === "open");
  const answeredTickets = tickets.filter((t) => t.status === "answered");
  const closedTickets = tickets.filter((t) => t.status === "closed");

  /* Round 34px avatar — Manrope initials on #1C1C21 (AD-05e ticket identity) */
  const initials = (name) => {
    const n = (name || "").trim();
    if (!n) return "—";
    const parts = n.split(/[\s_.-]+/).filter(Boolean);
    return ((parts[0]?.[0] || "") + (parts[1]?.[0] || (parts[0]?.[1] || ""))).toUpperCase() || "—";
  };
  const dash = (v) => (v == null || v === "" ? "—" : v);
  const when = (iso) => { if (!iso) return "—"; const d = new Date(iso); return isNaN(d) ? "—" : d.toLocaleString(); };

  /* status indicator token: open = red, answered = green, closed = muted */
  const STATUS = {
    open: { bg: "rgba(239,68,68,.14)", fg: "#EF4444", dot: "#EF4444", label: "OPEN" },
    answered: { bg: "rgba(0,214,50,.12)", fg: "#00D632", dot: "#00D632", label: "REPLIED" },
    closed: { bg: "#1C1C21", fg: "#a1a1aa", dot: "#52525b", label: "CLOSED" },
  };

  const renderTicket = (t) => {
    const s = STATUS[t.status] || STATUS.closed;
    return (
      <div key={t.id} data-testid={`admin-ticket-${t.id}`}
        className="flex flex-col gap-3 rounded-[14px] border border-[#1F1F23] bg-[#111114] p-3.5">
        {/* head: avatar · user + subject · status pill + time */}
        <div className="flex items-start gap-3">
          <span className="flex h-[34px] w-[34px] flex-none items-center justify-center rounded-full bg-[#1C1C21] font-display text-[12px] font-extrabold text-[#d4d4d8]">
            {initials(t.username)}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <span className="truncate text-[13px] font-bold text-white">{dash(t.username)}</span>
              <span className="truncate text-[11px] text-[#71717a]">{dash(t.email)}</span>
            </div>
            <p className="mt-0.5 truncate text-[13px] font-semibold text-[#E4E4E7]">{dash(t.subject)}</p>
          </div>
          <div className="flex flex-none flex-col items-end gap-1.5">
            <span className="flex items-center gap-1.5 rounded-md px-2 py-[3px] text-[10px] font-extrabold tracking-[.08em]"
              style={{ background: s.bg, color: s.fg }}>
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: s.dot }} />
              {s.label}
            </span>
            <span className="text-[10px] text-[#71717a]">{when(t.created_at)}</span>
          </div>
        </div>

        {/* message preview */}
        <p className="rounded-[10px] bg-[#0A0A0A] p-2.5 text-[12px] leading-relaxed text-[#a1a1aa]"
          style={{ boxShadow: "inset 0 0 0 1px #1F1F23" }}>{dash(t.message)}</p>

        {/* prior reply, if any */}
        {t.reply && (
          <div className="rounded-[10px] p-2.5" style={{ background: "rgba(0,214,50,.08)", boxShadow: "inset 0 0 0 1px rgba(0,214,50,.25)" }}>
            <span className="text-[10px] font-bold uppercase tracking-[.12em] text-[#00D632]">Your reply{t.replied_at ? ` · ${when(t.replied_at)}` : ""}</span>
            <p className="mt-1 text-[12px] text-[#d4d4d8]">{t.reply}</p>
          </div>
        )}

        {/* reply action — open + replied (not closed) */}
        {t.status !== "closed" && (
          <div className="flex flex-col gap-2">
            <Textarea rows={2} data-testid={`reply-input-${t.id}`} value={replies[t.id] || ""}
              onChange={(e) => setReplies({ ...replies, [t.id]: e.target.value })}
              placeholder="Type a reply to the user…"
              className="min-h-[64px] resize-none rounded-[10px] border-0 bg-[#0A0A0A] text-[13px] text-white placeholder:text-[#52525b] focus-visible:ring-0 focus-visible:ring-offset-0"
              style={{ boxShadow: "inset 0 0 0 1px #26262B" }} />
            <div className="flex flex-wrap gap-2">
              <BtnPrimary data-testid={`send-reply-${t.id}`} onClick={() => sendReply(t.id)}>
                <Icon name="send" size={13} /> Send reply
              </BtnPrimary>
              <BtnGhost data-testid={`close-ticket-${t.id}`} onClick={() => close(t.id)}>Close</BtnGhost>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Panel className="flex flex-col gap-4 p-[18px]">
      <CardHead title="Support" count={loading ? undefined : openTickets.length + answeredTickets.length + closedTickets.length} />

      {loading ? (
        <Loading>Loading tickets…</Loading>
      ) : (
        <>
          {/* Needs attention (open) */}
          <section data-testid="support-needs-attention" className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-[#EF4444]" />
              <h3 className="font-display text-[15px] font-extrabold tracking-tight">Needs attention</h3>
              <span className="rounded-full px-2 py-0.5 text-[11px] font-bold" style={{ background: "rgba(239,68,68,.15)", color: "#EF4444" }}>{openTickets.length}</span>
            </div>
            <div className="flex flex-col gap-3">
              {openTickets.length === 0
                ? <div className="rounded-[10px] border border-dashed border-[#1F1F23] px-3.5 py-6 text-center text-[13px] text-[#52525b]">Nothing waiting on you — all caught up.</div>
                : openTickets.map(renderTicket)}
            </div>
          </section>

          {/* Replied — awaiting close (answered) */}
          {answeredTickets.length > 0 && (
            <section data-testid="support-in-progress" className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-[#00D632]" />
                <h3 className="font-display text-[15px] font-extrabold tracking-tight">Replied — awaiting close</h3>
                <span className="rounded-full px-2 py-0.5 text-[11px] font-bold" style={{ background: "rgba(0,214,50,.15)", color: "#00D632" }}>{answeredTickets.length}</span>
              </div>
              <div className="flex flex-col gap-3">{answeredTickets.map(renderTicket)}</div>
            </section>
          )}

          {/* Closed — collapsible */}
          <section data-testid="support-closed" className="flex flex-col gap-3">
            <button onClick={() => setShowClosed((v) => !v)} data-testid="support-toggle-closed" className="flex items-center gap-2 text-left">
              <Icon name={showClosed ? "chevron-down" : "arrow-right"} size={15} className="text-[#71717a]" />
              <h3 className="font-display text-[15px] font-extrabold tracking-tight text-[#a1a1aa]">Closed tickets</h3>
              <span className="rounded-full px-2 py-0.5 text-[11px] font-bold text-[#a1a1aa]" style={{ background: "#1C1C21" }}>{closedTickets.length}</span>
            </button>
            {showClosed && (
              <div className="flex flex-col gap-3">
                {closedTickets.length === 0
                  ? <div className="px-3.5 py-4 text-[13px] text-[#52525b]">No closed tickets.</div>
                  : closedTickets.map(renderTicket)}
              </div>
            )}
          </section>
        </>
      )}
    </Panel>
  );
}


/* -------- Live Odds (The Odds API) -------- */
const LIVE_SPORTS = [
  ["nfl", "NFL"], ["ncaaf", "NCAAF"], ["nba", "NBA"], ["ncaab", "NCAAB"], ["wnba", "WNBA"],
  ["mlb", "MLB"], ["npb", "NPB"], ["nhl", "NHL"], ["soccer", "Soccer"], ["mma", "MMA"], ["tennis", "Tennis"],
];


/* -------- KYC Review -------- */
/* Striped honest placeholder box (matches AD-02b) — shown when no real doc URL,
   or when the document image can't load (falls back gracefully). */
function KycDocSlot({ label, src, testid, alt }) {
  const [err, setErr] = useState(false);
  const stripe = { background: "repeating-linear-gradient(135deg,#111114 0 12px,#141417 12px 24px)" };
  if (src && !err) {
    return (
      <a href={src} target="_blank" rel="noreferrer" className="block">
        <img src={src} alt={alt} data-testid={testid} onError={() => setErr(true)}
          className="h-[170px] w-full rounded-[14px] border border-[#1F1F23] bg-[#111114] object-contain" />
      </a>
    );
  }
  return (
    <div data-testid={testid}
      className="flex h-[170px] items-center justify-center rounded-[14px] border border-[#1F1F23] text-[11px] font-medium tracking-[.1em] text-[#52525b]"
      style={stripe}>
      {label}
    </div>
  );
}

function KycTab() {
  const [filter, setFilter] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState({});
  const [idx, setIdx] = useState(0);            // which applicant in the queue is under review
  const [reasons, setReasons] = useState({});   // inline reject reason per applicant
  const [loadError, setLoadError] = useState(""); // inline error when a refresh fails
  const token = localStorage.getItem("pb_token");

  const load = async (f) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/kyc?status=${f}`);
      setRows(data);
      setIdx(0);
      setLoadError("");
    } catch (e) {
      // A failed refresh must NOT leave the previous filter's rows on screen as if
      // they were current — clear them and surface an inline error instead.
      const msg = apiError(e.response?.data?.detail) || "Failed to load KYC submissions";
      setRows([]);
      setLoadError(msg);
      toast.error(msg);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(filter); /* eslint-disable-next-line */ }, [filter]);

  const decide = async (userId, action) => {
    let reason = "";
    if (action === "reject") {
      // Inline reason from the review card (optional per the mockup); fall back to a default if blank.
      reason = (reasons[userId] || "").trim() || "Documents were unclear. Please resubmit.";
    }
    setBusy((p) => ({ ...p, [userId]: true }));
    try {
      await api.post(`/admin/kyc/${userId}/${action}`, action === "reject" ? { reason } : {});
      toast.success(action === "approve" ? "Approved" : "Rejected");
      await load(filter);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy((p) => ({ ...p, [userId]: false }));
    }
  };

  const img = (path) => (path ? `${API}/kyc/file/${path}?auth=${token}` : null);
  const FILTERS = [["pending", "Pending"], ["approved", "Approved"], ["rejected", "Rejected"], ["all", "All"]];

  const total = rows.length;
  const safeIdx = Math.min(idx, Math.max(0, total - 1));
  const r = rows[safeIdx];
  const initials = (r?.username || "?").slice(0, 2).toUpperCase();
  const joined = r?.submitted_at
    ? new Date(r.submitted_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : "—";
  const sub = [`DOB ${r?.dob || "—"}`, "—" /* state: not on record */, `joined ${joined}`].join(" · ");

  return (
    <div>
      {/* status filter chips above the review card */}
      <div className="mb-4 flex flex-wrap gap-2">
        {FILTERS.map(([k, l]) => (
          <Chip key={k} active={filter === k} data-testid={`kyc-filter-${k}`} onClick={() => setFilter(k)}>{l}</Chip>
        ))}
      </div>

      {loadError && (
        <div data-testid="kyc-load-error" className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-[#EF4444]/40 bg-[#EF4444]/10 p-3 text-sm text-[#EF4444]">
          <span>{loadError}</span>
          <button type="button" onClick={() => load(filter)} className="shrink-0 rounded-md border border-[#EF4444]/40 px-2.5 py-1 text-[12px] font-semibold hover:bg-[#EF4444]/10">Retry</button>
        </div>
      )}

      {loading ? (
        <Loading>Loading…</Loading>
      ) : loadError ? null : total === 0 ? (
        <Panel className="p-6 text-center text-[13px] text-[#52525b]">No {filter === "all" ? "" : filter} submissions.</Panel>
      ) : r ? (
        <Panel data-testid={`kyc-row-${r.user_id}`} className="w-full max-w-[560px] p-[18px]">
          <div className="flex flex-col gap-[14px]">
            {/* header: title + queue count + position */}
            <CardHead
              title="KYC review"
              count={filter === "pending" ? `${total} in queue` : `${total}`}
              right={
                <div className="flex items-center gap-2 text-[12px] font-medium text-[#71717a]">
                  {total > 1 && (
                    <>
                      <button type="button" data-testid="kyc-prev" disabled={safeIdx === 0}
                        onClick={() => setIdx((i) => Math.max(0, i - 1))}
                        className="rounded-md px-1.5 py-0.5 text-[#a1a1aa] hover:text-white disabled:opacity-30">‹</button>
                      <span data-testid="kyc-position">{safeIdx + 1} of {total}</span>
                      <button type="button" data-testid="kyc-next" disabled={safeIdx >= total - 1}
                        onClick={() => setIdx((i) => Math.min(total - 1, i + 1))}
                        className="rounded-md px-1.5 py-0.5 text-[#a1a1aa] hover:text-white disabled:opacity-30">›</button>
                    </>
                  )}
                  {total <= 1 && <span data-testid="kyc-position">1 of {total}</span>}
                </div>
              }
            />

            {/* applicant identity */}
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-[#1C1C21] font-display text-[13px] font-extrabold">{initials}</span>
              <div className="min-w-0">
                <div className="truncate text-[15px] font-bold">{r.username || "—"} <span className="font-medium text-[#71717a]">· {r.email || "—"}</span></div>
                <div className="truncate text-[12px] font-medium text-[#71717a]">{sub}</div>
              </div>
              <span className={`ml-auto flex-none rounded-md px-2 py-[3px] text-[10px] font-extrabold tracking-[.08em] ${r.status === "approved" ? "bg-[#00D632]/15 text-[#00D632]" : r.status === "rejected" ? "bg-[#EF4444]/15 text-[#EF4444]" : "bg-[#18181C] text-[#a1a1aa]"}`}>{String(r.status || "").toUpperCase()}</span>
            </div>

            {/* document thumbnails */}
            <div className="grid grid-cols-2 gap-2.5">
              <KycDocSlot label="[ ID DOCUMENT ]" alt="ID document" src={img(r.id_path)} testid={`kyc-id-img-${r.user_id}`} />
              <KycDocSlot label="[ SELFIE ]" alt="Selfie" src={img(r.selfie_path)} testid={`kyc-selfie-img-${r.user_id}`} />
            </div>

            {/* reject reason (optional) */}
            {r.status !== "approved" && (
              <div>
                <div className="mb-1.5 text-[11px] font-semibold tracking-[.14em] text-[#71717a]">REJECT REASON (OPTIONAL)</div>
                <input
                  data-testid={`kyc-reason-${r.user_id}`}
                  value={reasons[r.user_id] || ""}
                  onChange={(e) => setReasons((p) => ({ ...p, [r.user_id]: e.target.value }))}
                  placeholder="e.g. Glare on ID photo"
                  className="h-[42px] w-full rounded-[10px] bg-[#0A0A0A] px-3 text-[13px] font-medium text-white placeholder:text-[#52525b] focus:outline-none focus:ring-1 focus:ring-[#00D632]/40"
                  style={{ boxShadow: "inset 0 0 0 1px #26262B" }}
                />
              </div>
            )}

            {/* action row */}
            {r.status !== "approved" && (
              <div className="grid grid-cols-2 gap-2.5">
                <BtnDanger className="h-[46px] w-full rounded-[12px] text-[14px]" disabled={busy[r.user_id]}
                  data-testid={`kyc-reject-${r.user_id}`} onClick={() => decide(r.user_id, "reject")}>Reject</BtnDanger>
                <BtnPrimary className="h-[46px] w-full justify-center rounded-[12px] text-[14px]" disabled={busy[r.user_id]}
                  data-testid={`kyc-approve-${r.user_id}`} onClick={() => decide(r.user_id, "approve")}>Approve &amp; notify</BtnPrimary>
              </div>
            )}
          </div>
        </Panel>
      ) : null}
    </div>
  );
}



/* -------- One live blackjack table: full dealing panel (Option B: one per table) -------- */
const BJ_SUIT = { S: "♠", H: "♥", D: "♦", C: "♣", "?": "" };
const BJ_RANKS_UI = ["A", "2", "3", "4", "5", "6", "7", "8", "9", "10", "J", "Q", "K"];

function bjDecision(h, isActive, awaitingReason) {
  if (awaitingReason === "hit") return { label: "HIT", cls: "bg-surface-5 text-white" };
  if (awaitingReason === "double") return { label: "DOUBLE", cls: "bg-surface-5 text-white" };
  if (awaitingReason === "initial" || awaitingReason === "split") return { label: "DEALING", cls: "bg-[#27272A] text-[#a1a1aa]" };
  switch (h.status) {
    case "bust": return { label: "BUST", cls: "bg-[#EF4444] text-white" };
    case "surrender": return { label: "SURRENDER", cls: "bg-surface-5 text-white" };
    case "blackjack": return { label: "BLACKJACK", cls: "bg-surface-5 text-white" };
    case "even_money": return { label: "EVEN MONEY", cls: "bg-surface-5 text-white" };
    case "stand": return { label: "STAND", cls: "bg-[#00D632] text-[#0A0A0A]" };
    case "double": return { label: "DOUBLE", cls: "bg-surface-5 text-white" };
    case "playing":
      if (isActive) return h.cards && h.cards.length > 2
        ? { label: "HIT", cls: "bg-surface-5 text-white" }
        : { label: "DECIDING…", cls: "bg-surface-5 text-white animate-pulse" };
      return { label: "WAITING", cls: "bg-transparent text-[#52525B] border border-[#3f3f46]" };
    default: return null;
  }
}

/* KeyRevealField — masked-by-default secret row (design-system styled).
   Reveal toggles visibility per-field; Copy writes the raw value. No value is
   ever logged or rendered until the operator clicks Reveal. */
function SecretField({ labelText, value, fieldKey, show, setShow, onCopy }) {
  const visible = !!show[fieldKey];
  const masked = value ? "•".repeat(Math.min(28, String(value).length)) : "—";
  return (
    <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap" data-testid={`bj-secret-${fieldKey}`}>
      <span className="w-full shrink-0 text-[10px] font-semibold uppercase tracking-[.12em] text-[#71717a] sm:w-40">{labelText}</span>
      <code
        className="min-w-0 flex-1 truncate rounded-[9px] px-2.5 py-1.5 font-mono text-[11px] text-white"
        style={{ background: "#0A0A0A", boxShadow: "inset 0 0 0 1px #26262B" }}
      >{visible ? (value || "—") : masked}</code>
      <RowBtn type="button" tone={visible ? "green" : undefined} onClick={() => setShow((s) => ({ ...s, [fieldKey]: !s[fieldKey] }))}>
        {visible ? "Hide" : "Reveal"}
      </RowBtn>
      <RowBtn type="button" onClick={() => onCopy(value, labelText)} disabled={!value} className="disabled:opacity-40">
        Copy
      </RowBtn>
    </div>
  );
}

function BlackjackTablePanel({ tableId, label, accent, focused, onFocus }) {
  const { confirm } = useConfirm();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [bj, setBj] = useState(null);
  const [settleSel, setSettleSel] = useState({});
  const [selRank, setSelRank] = useState("A");
  const [selSuit, setSelSuit] = useState("S");
  const [cfgForm, setCfgForm] = useState({ name: "", min_bet: "", max_bet: "", stream_url: "", operator_stream_url: "", stream_delay_ms: "0", video_provider: "auto", fallback_stream_url: "", live_input_id: "" });
  const [showCfg, setShowCfg] = useState(false);
  // Super-admin live stream keys (Twitch-style reveal/rotate). Never fetched on load — only on click.
  const [streamKeys, setStreamKeys] = useState(null);   // revealed credentials for this table
  const [streamBusy, setStreamBusy] = useState(false);
  const [showKeys, setShowKeys] = useState({});         // { fieldName: bool } masked-by-default toggles
  const [twitchFb, setTwitchFb] = useState("");         // editable Twitch-fallback username

  const copyStreamValue = async (value, labelText) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${labelText} copied`);
    } catch {
      toast.error("Copy failed — select and copy manually");
    }
  };
  const revealStreamKeys = async () => {
    setStreamBusy(true);
    try {
      const { data } = await api.post("/admin/bj/stream-credentials", { table: tableId });
      setStreamKeys(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setStreamBusy(false); }
  };
  const rotateStreamKeys = async () => {
    if (!(await confirm({ title: "Rotate stream keys", body: "This generates NEW keys for this table. Any active OBS broadcast must restart with the new key.", confirmText: "Rotate keys" }))) return;
    setStreamBusy(true);
    try {
      const { data } = await api.post("/admin/bj/stream-rotate", { table: tableId });
      setStreamKeys(data);
      toast.success("New stream keys generated — restart OBS with them");
    } catch (e) {
      if (e.response?.status === 501) toast.error("Rotation not configured yet — a streaming API credentials are needed.");
      else toast.error(apiError(e.response?.data?.detail));
    } finally { setStreamBusy(false); }
  };

  const loadBj = () => api.get(`/admin/bj/state?table=${tableId}`).then(({ data }) => setBj(data)).catch(() => {});
  useEffect(() => { loadBj(); const id = setInterval(loadBj, 2000); return () => clearInterval(id); }, [tableId]);
  useEffect(() => {
    api.get("/admin/bj/config").then(({ data }) => {
      const c = data.find((t) => t.table_id === tableId);
      if (c) {
        setCfgForm({ name: c.name, min_bet: String(c.min_bet), max_bet: String(c.max_bet), stream_url: c.stream_url || "", operator_stream_url: c.operator_stream_url || "", stream_delay_ms: String(c.stream_delay_ms || 0), video_provider: c.video_provider || "auto", fallback_stream_url: c.fallback_stream_url || "", live_input_id: c.live_input_id || "" });
        setTwitchFb((/twitch\.tv\/([^/?#]+)/i.exec(c.fallback_stream_url || "") || [])[1] || "");
      }
    }).catch(() => {});
  }, [tableId]);

  const saveBjConfig = async () => {
    const mn = parseFloat(cfgForm.min_bet), mx = parseFloat(cfgForm.max_bet);
    if (!cfgForm.name.trim()) { toast.error("Enter a table name"); return; }
    if (isNaN(mn) || isNaN(mx) || mn <= 0 || mx <= mn) { toast.error("Max must be greater than min (both > 0)"); return; }
    try {
      const delay = Math.max(0, Math.min(30000, parseInt(cfgForm.stream_delay_ms || "0", 10) || 0));
      const { data } = await api.post(`/admin/bj/config?table=${tableId}`, { name: cfgForm.name.trim(), min_bet: mn, max_bet: mx, stream_url: cfgForm.stream_url.trim(), operator_stream_url: cfgForm.operator_stream_url.trim(), stream_delay_ms: delay, video_provider: cfgForm.video_provider || "auto", fallback_stream_url: cfgForm.fallback_stream_url.trim(), live_input_id: cfgForm.live_input_id.trim() });
      toast.success(`Saved ${data.name} · $${data.min_bet}–$${data.max_bet}`); loadBj();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const saveTwitchFallback = async () => {
    const u = twitchFb.trim().replace(/^@/, "").split("/").pop();
    const fallbackUrl = u ? `https://www.twitch.tv/${u}` : "";
    try {
      await api.post(`/admin/bj/config?table=${tableId}`, {
        name: cfgForm.name.trim() || label, min_bet: parseFloat(cfgForm.min_bet) || 1, max_bet: parseFloat(cfgForm.max_bet) || 250,
        stream_url: cfgForm.stream_url.trim(), operator_stream_url: cfgForm.operator_stream_url.trim(),
        stream_delay_ms: parseInt(cfgForm.stream_delay_ms || "0", 10) || 0,
        video_provider: cfgForm.video_provider || "webrtc",
        fallback_stream_url: fallbackUrl, live_input_id: cfgForm.live_input_id.trim(),
      });
      setCfgForm((s) => ({ ...s, fallback_stream_url: fallbackUrl }));
      toast.success(u ? `Twitch fallback set to ${u}` : "Twitch fallback cleared");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const bjAction = async (path, body) => {
    const commandId = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    try {
      const { data } = await api.post(
        `${path}?table=${tableId}`,
        body,
        { headers: { "X-Command-ID": commandId } },
      );
      setBj(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const addCard = (seat) => bjAction("/admin/bj/card", { rank: selRank, suit: selSuit, seat });
  const submitCard = () => {
    if (!bj) return;
    if (bj.status === "dealing") {
      const players = bj.seats.filter((x) => x.occupied && x.hands.some((h) => h.bet > 0));
      const firstPass = players.find((x) => (x.hands?.[0]?.cards?.length || 0) === 0);
      if (firstPass) return addCard(firstPass.seat);
      if (bj.dealer.length < 1) return addCard(-1);
      const secondPass = players.find((x) => (x.hands?.[0]?.cards?.length || 0) === 1);
      if (secondPass) return addCard(secondPass.seat);
      return;
    }
    if (bj.status === "acting" && bj.awaiting) return addCard(bj.awaiting.seat);
    if (bj.status === "dealer") return addCard(-1);
  };
  // Keyboard dealing shortcuts only fire for the focused panel to avoid cross-table conflicts.
  useEffect(() => {
    if (!focused || !bj || !["dealing", "acting", "dealer"].includes(bj.status)) return;
    const onKey = (e) => {
      const tag = (e.target.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;
      const k = (e.key || "").toUpperCase();
      const rankMap = { "1": "A", A: "A", "2": "2", "3": "3", "4": "4", "5": "5", "6": "6", "7": "7", "8": "8", "9": "9", "0": "10", T: "10", J: "J", Q: "Q", K: "K" };
      if (rankMap[k]) setSelRank(rankMap[k]);
      else if (["S", "H", "D", "C"].includes(k)) setSelSuit(k);
      else if (e.key === "Enter") { e.preventDefault(); submitCard(); }
      else if (e.key === "Backspace") { e.preventDefault(); bjAction("/admin/bj/undo"); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [focused, bj, selRank, selSuit]);

  const seated = bj ? bj.seats.filter((s) => s.occupied).length : 0;
  const betting = bj ? bj.seats.filter((s) => s.occupied && s.hands && s.hands.some((h) => h.bet > 0)).length : 0;

  const isOn = bj && bj.status !== "off";
  // AD-04 phase tabs — derived honestly from the real bj.status (no fabrication).
  const BJ_PHASES = [
    { key: "open", label: "Betting open" },
    { key: "dealing", label: "Dealing" },
    { key: "acting", label: "Player decisions" },
    { key: "settling", label: "Settling" },
  ];
  const activePhase = !bj ? null
    : bj.status === "open" ? "open"
    : bj.status === "dealing" ? "dealing"
    : (bj.status === "acting" || bj.status === "insurance") ? "acting"
    : (bj.status === "dealer" || bj.status === "settled") ? "settling"
    : null;
  // AD-04 STREAM status — honest: derived from whether a public stream URL is configured + table on/off.
  const streamConfigured = !!(cfgForm.stream_url && cfgForm.stream_url.trim());
  const streamDotColor = !isOn ? "#52525b" : streamConfigured ? "#00D632" : "#a1a1aa";
  const streamLabel = !isOn ? "Table offline" : streamConfigured ? "Stream configured" : "No stream set";
  const twitchOn = !!(cfgForm.fallback_stream_url && cfgForm.fallback_stream_url.trim());
  return (
    <div onMouseDown={onFocus}
      className="overflow-hidden rounded-[14px] border border-[#1F1F23] bg-[#111114] transition-colors"
      style={focused ? { boxShadow: "inset 0 0 0 1px rgba(0,214,50,.5)" } : undefined}
      data-testid={`bj-dealer-control-${tableId}`}>
      {/* AD-04 card header row: icon + name + status pill + meta · phase tabs · Operator console */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-[#1F1F23] px-5 py-3.5">
        <span className="flex h-5 w-5 flex-none items-center justify-center text-white"><Icon name="blackjack" size={18} /></span>
        <span className="font-display text-[16px] font-bold text-white">{bj?.name || label}</span>
        <StatusPill status={isOn ? "LIVE" : "OFFLINE"} bg={isOn ? "rgba(0,214,50,.12)" : "#26262B"} fg={isOn ? "#00D632" : "#71717a"} />
        <span className="text-[13px] font-medium text-[#71717a]">
          Round <b className="text-[#d4d4d8]">#{bj?.round_number ?? "—"}</b>{bj && <> · {seated} of {bj.seat_count} seats</>}
        </span>
        <div className="ml-auto flex flex-wrap items-center gap-1">
          {BJ_PHASES.map((p) => (activePhase === p.key
            ? <span key={p.key} className="flex h-7 items-center gap-1.5 rounded-full bg-[#1C1C21] px-2.5 text-[12px] font-semibold text-white"><span className="h-1.5 w-1.5 rounded-full bg-[#00D632]" />{p.label}</span>
            : <span key={p.key} className="flex h-7 items-center px-2.5 text-[12px] font-medium text-[#52525b]">{p.label}</span>))}
          {focused && <span className="rounded-md bg-white/10 px-2 py-[3px] text-[10px] font-extrabold uppercase tracking-[.08em] text-white" data-testid={`bj-focus-badge-${tableId}`}>⌨ keyboard</span>}
          <a href={`/admin/live-blackjack/${tableId}/dealer-tv`} target="_blank" rel="noreferrer" title="Opens the secure admin Dealer TV (auto-mints its token). For a separate dealer device, use Operator → Copy TV link." className="flex h-8 items-center gap-1.5 rounded-[8px] px-3 text-[13px] font-semibold text-[#E4E4E7]" style={{ background: "#18181C", boxShadow: "inset 0 0 0 1px #26262B" }}><Icon name="livedealer" size={14} /> Dealer TV</a>
          <a href={`/admin/live-blackjack/${tableId}/operator`} className="flex h-8 items-center gap-1.5 rounded-[8px] px-3 text-[13px] font-semibold text-[#00D632]" style={{ background: "rgba(0,214,50,.12)", boxShadow: "inset 0 0 0 1px rgba(0,214,50,.4)" }}>Operator console <Icon name="arrow-right" size={13} /></a>
        </div>
      </div>
      {(bj?.push_22 || bj?.auto_settle) && (
        <div className="border-b border-[#1F1F23] px-5 py-2">
          {bj?.push_22 && <p className="text-[10px] font-semibold text-[#00D632]">Double Down Madness: double any # of cards · dealer 22 = PUSH</p>}
          {bj?.auto_settle && <p className="text-[10px] font-semibold text-[#00D632]">Auto-settle: pays out automatically once the dealer reaches 17+</p>}
        </div>
      )}
      {/* AD-04 two-column body: LEFT = STREAM (+ SEAT BOARD below) · RIGHT = ROUND CONTROLS */}
      <div className="grid grid-cols-1 lg:grid-cols-2">
      <div className="flex flex-col gap-3.5 p-5 lg:border-r lg:border-[#1F1F23]">
        <div className="flex items-center gap-2">
          <span className="text-[12px] font-bold uppercase tracking-[.1em] text-[#71717a]">Stream</span>
          <span className="ml-auto flex items-center gap-1.5 text-[12px] font-medium text-[#a1a1aa]"><span className="h-[7px] w-[7px] rounded-full" style={{ background: streamDotColor }} />{streamLabel}</span>
        </div>

      {isSuperAdmin ? (
        <div data-testid={`bj-stream-keys-${tableId}`}>
          <p className="mb-2 text-[11px] text-[#71717a]">The table player is low-latency <b className="text-[#d4d4d8]">WebRTC</b>. In OBS 30+ → <b className="text-[#d4d4d8]">Settings → Stream → Service: WHIP</b>, paste the <b className="text-[#d4d4d8]">WHIP URL</b> below and leave <b className="text-[#d4d4d8]">Bearer Token blank</b>. The feed appears on the site automatically when you go live.</p>
          <p className="mb-2.5 rounded-[8px] border border-[#EF4444]/30 bg-[#EF4444]/[.08] px-2.5 py-1.5 text-[10px] leading-snug text-[#d4d4d8]"><b className="text-[#EF4444]">Must use WHIP for the live player.</b> The stream provider only serves the WebRTC player for streams published via WHIP. If you broadcast via RTMPS/SRT instead, the WebRTC player stays black — those only play through the slower HLS fallback.</p>
          <div className="flex flex-wrap gap-2">
            <BtnPrimary disabled={streamBusy} data-testid={`bj-stream-reveal-${tableId}`} onClick={revealStreamKeys}>
              {streamBusy ? "…" : (streamKeys ? "Refresh keys" : "Reveal keys")}
            </BtnPrimary>
            <BtnGhost disabled={streamBusy} data-testid={`bj-stream-rotate-${tableId}`} onClick={rotateStreamKeys}>
              Rotate key
            </BtnGhost>
          </div>
          {streamKeys && (
            <div className="mt-3 space-y-2.5" data-testid={`bj-stream-values-${tableId}`}>
              <SecretField labelText="① OBS Server (WHIP URL · Service: WHIP)" value={streamKeys.whip_url} fieldKey="whip" show={showKeys} setShow={setShowKeys} onCopy={copyStreamValue} />
              <p className="text-[10px] text-[#71717a]">② OBS Bearer Token — leave <b className="text-[#d4d4d8]">blank</b> (the key is already in the WHIP URL).</p>
              <details className="pt-1" data-testid={`bj-stream-advanced-${tableId}`}>
                <summary className="cursor-pointer list-none text-[10px] font-semibold text-[#71717a] hover:text-white">▸ Advanced — RTMPS / SRT (HLS playback only · NOT the WebRTC player)</summary>
                <div className="mt-2 space-y-2">
                  <p className="text-[10px] leading-snug text-[#a1a1aa]">Only use these if your encoder can't do WHIP. RTMPS/SRT streams play on the table via the <b className="text-[#d4d4d8]">HLS fallback</b> (a few seconds behind) — the low-latency WebRTC player will not show them.</p>
                  <SecretField labelText="RTMPS Server" value={streamKeys.rtmps?.url} fieldKey="rtmps_url" show={showKeys} setShow={setShowKeys} onCopy={copyStreamValue} />
                  <SecretField labelText="RTMPS Stream Key" value={streamKeys.rtmps?.streamKey} fieldKey="rtmps_key" show={showKeys} setShow={setShowKeys} onCopy={copyStreamValue} />
                  <SecretField labelText="SRT URL" value={streamKeys.srt?.url} fieldKey="srt_url" show={showKeys} setShow={setShowKeys} onCopy={copyStreamValue} />
                  <SecretField labelText="SRT stream ID" value={streamKeys.srt?.streamId} fieldKey="srt_id" show={showKeys} setShow={setShowKeys} onCopy={copyStreamValue} />
                  <SecretField labelText="SRT passphrase" value={streamKeys.srt?.passphrase} fieldKey="srt_pass" show={showKeys} setShow={setShowKeys} onCopy={copyStreamValue} />
                </div>
              </details>
              <p className="text-[10px] text-[#71717a]">Live input: {streamKeys.live_input_name || "—"} · public playback {streamKeys.whep_play_url ? "wired" : "—"}</p>
            </div>
          )}
          {/* Twitch fallback username + auto-switch (AD-04 stream footer) */}
          <div className="mt-3.5 flex flex-col gap-1.5">
            <span className="text-[12px] font-semibold text-[#a1a1aa]">Twitch fallback username</span>
            <div className="flex flex-wrap items-center gap-2.5">
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <div className="flex h-[38px] min-w-0 flex-1 items-center rounded-[9px] bg-[#0A0A0A] px-3 text-[13px] font-medium" style={{ boxShadow: "inset 0 0 0 1px #26262B" }}>
                  <span className="text-[#52525b]">twitch.tv/</span>
                  <input data-testid={`bj-twitch-fb-${tableId}`} value={twitchFb} onChange={(e) => setTwitchFb(e.target.value)} placeholder="username"
                    className="min-w-0 flex-1 border-0 bg-transparent text-white placeholder:text-[#52525b] focus:outline-none" />
                </div>
                <BtnPrimary data-testid={`bj-twitch-fb-save-${tableId}`} onClick={saveTwitchFallback} className="h-[38px]">Save</BtnPrimary>
              </div>
              <span className="flex items-center gap-2 whitespace-nowrap text-[13px] font-medium text-[#a1a1aa]">
                <span className="h-6 w-10 flex-none rounded-full" style={{ background: twitchOn ? "#00D632" : "#3F3F46", position: "relative" }}>
                  <span className="absolute top-[3px] h-[18px] w-[18px] rounded-full bg-white" style={{ left: twitchOn ? "19px" : "3px" }} />
                </span>
                Auto-switch if OBS drops
              </span>
            </div>
          </div>
        </div>
      ) : (
        <p className="text-[11px] text-[#71717a]">Stream keys are visible to super admins only.</p>
      )}

      {/* SEAT BOARD (AD-04) — the 7-seat detail grid lives below the stream column */}
      <div className="mt-1 border-t border-[#1F1F23] pt-3.5">
      <span className="mb-2.5 block text-[12px] font-bold uppercase tracking-[.1em] text-[#71717a]">Seat board</span>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
        {bj && bj.seats.map((s) => (
          <div key={s.seat} className={`rounded-[12px] border p-2 text-center text-[11px] ${bj.active_seat === s.seat && bj.status === "acting" ? "border-[#00D632] bg-[#00D632]/[.06]" : "border-[#1F1F23] bg-[#0C0C0E]"}`}>
            <p className="text-[9px] font-bold uppercase tracking-wider text-[#71717a]">Seat #{s.seat + 1}</p>
            {s.occupied ? (<>
              <p className="mt-0.5 truncate text-[12px] font-bold text-white">{s.username}</p>
              {s.hands.map((h, hi) => {
                const isActive = bj.active_seat === s.seat && (bj.active_hand ?? 0) === hi && bj.status === "acting" && !bj.awaiting;
                const awaitingReason = bj.awaiting && bj.awaiting.seat === s.seat && (bj.awaiting.hand ?? 0) === hi ? bj.awaiting.reason : null;
                const dec = (bj.status === "acting" || bj.status === "dealer" || bj.status === "settled") && h.bet > 0 && h.cards && h.cards.length > 0 ? bjDecision(h, isActive, awaitingReason) : null;
                return (
                  <div key={hi} className="mt-1">
                    <div className="flex flex-wrap justify-center gap-0.5" data-testid={`bj-seat-cards-${tableId}-${s.seat}`}>
                      {h.cards && h.cards.length > 0
                        ? h.cards.map((c, ci) => (
                            <PlayingCard key={ci} card={c} size="xs" faceDown={c.rank === "?"} />))
                        : <span className="text-[10px] text-[#52525B]">{h.bet > 0 ? "waiting deal" : "no bet"}</span>}
                    </div>
                    {h.cards && h.cards.length > 0 && <p className="text-xl font-black leading-tight text-white">{h.value}</p>}
                    {dec && <span className={`mt-1 inline-block rounded-md px-2.5 py-1 text-base font-black uppercase leading-none tracking-wide ${dec.cls}`}>{dec.label}</span>}
                  </div>
                );})}
              <p className="mt-0.5 font-bold text-[#00D632]">${s.hands.reduce((a, h) => a + h.bet, 0).toFixed(2)}</p>
              {s.insurance > 0 && <p className="text-[9px] text-[#00D632]">ins ${s.insurance.toFixed(2)}</p>}
              {bj.status === "settled" && <p className="text-[9px] text-[#a1a1aa]">{s.hands.map((h) => h.result).filter(Boolean).join(", ")}</p>}
              <button data-testid={`bj-kick-${tableId}-${s.seat}`} onClick={() => bjAction("/admin/bj/kick", { seat: s.seat })} className="mt-0.5 text-[9px] font-semibold text-[#EF4444] underline hover:text-[#EF4444]">kick</button>
            </>) : <p className="py-3 text-[#52525B]">empty</p>}
          </div>
        ))}
      </div>
      </div>
      </div>

      {/* RIGHT column = ROUND CONTROLS */}
      <div className="flex flex-col gap-3.5 p-5">
        <div className="flex items-center gap-2">
          <span className="text-[12px] font-bold uppercase tracking-[.1em] text-[#71717a]">Round controls</span>
          <span className="ml-auto font-display text-[13px] font-bold text-white">
            {bj?.status === "acting" && bj.awaiting ? `Seat #${bj.awaiting.seat + 1} to act` : bj?.status === "acting" ? "Players deciding" : bj?.status === "open" ? "Betting open" : bj?.status === "dealer" ? "Dealer to draw" : ""}
            {bj?.seconds_left != null && <span data-testid={`bj-timer-${tableId}`}> · {bj.seconds_left}s</span>}
          </span>
        </div>

      <div className="flex flex-col">

      {/* AD-04 primary round-control grid (all original bj-admin controls preserved) */}
      <div className="grid grid-cols-2 gap-2">
        <button data-testid={`bj-admin-open-${tableId}`} onClick={() => bjAction("/admin/bj/open")} className="flex h-[44px] items-center justify-center rounded-[10px] bg-[#00D632] font-display text-[14px] font-extrabold text-[#0A0A0A] hover:bg-[#00B829]">New bets · 20s</button>
        <button data-testid={`bj-admin-deal-${tableId}`} onClick={() => bjAction("/admin/bj/deal")} className="flex h-[44px] items-center justify-center rounded-[10px] bg-[#18181C] text-[14px] font-semibold text-[#E4E4E7] hover:bg-[#1F1F23]" style={{ boxShadow: "inset 0 0 0 1px #26262B" }}>Deal cards</button>
        {bj?.status === "off"
          ? <button data-testid={`bj-admin-on-${tableId}`} onClick={() => bjAction("/admin/bj/on")} className="flex h-[44px] items-center justify-center rounded-[10px] bg-[#00D632] font-display text-[14px] font-extrabold text-[#0A0A0A] hover:bg-[#00B829]">Table ON</button>
          : <button data-testid={`bj-admin-off-${tableId}`} onClick={() => bjAction("/admin/bj/off")} className="flex h-[44px] items-center justify-center rounded-[10px] text-[14px] font-semibold text-[#EF4444]" style={{ boxShadow: "inset 0 0 0 1px rgba(239,68,68,.45)" }}>Table OFF</button>}
        <button data-testid={`bj-admin-reset-${tableId}`} onClick={() => bjAction("/admin/bj/reset")} className="flex h-[44px] items-center justify-center rounded-[10px] text-[14px] font-semibold text-[#EF4444]" style={{ boxShadow: "inset 0 0 0 1px rgba(239,68,68,.45)" }}>Reset · new shoe</button>
      </div>
      <p className="mt-2.5 text-[11px] leading-snug text-[#52525b]">New bets opens a 20s window. Deal 2 to each seat + the dealer's single up-card, then players auto-decide 15s each in seat order. After the last seat, deal the dealer's hole card + draws and settle (auto-settle tables pay themselves at 17+).</p>

      </div>
      </div>
      </div>

      {/* Full-width operator detail tools below the two-column overview */}
      <div className="border-t border-[#1F1F23] p-5">

      <button onClick={() => setShowCfg((v) => !v)} data-testid={`bj-cfg-toggle-${tableId}`} className="mb-3 text-[11px] font-semibold text-[#71717a] underline underline-offset-2 hover:text-white">
        {showCfg ? "Hide table settings" : "Table settings — rename, limits & live stream"}
      </button>
      {showCfg && (
        <div className="mb-3 rounded-[14px] border border-[#1F1F23] bg-[#0A0A0A] p-3.5" data-testid={`bj-table-config-${tableId}`}>
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-[160px] flex-1">
              <label className="text-[10px] font-semibold uppercase tracking-[.12em] text-[#71717a]">Name</label>
              <Input data-testid={`bj-cfg-name-${tableId}`} value={cfgForm.name} onChange={(e) => setCfgForm((s) => ({ ...s, name: e.target.value }))} placeholder="Table name" className="h-9 text-sm" />
            </div>
            <div className="w-24">
              <label className="text-[10px] font-semibold uppercase tracking-[.12em] text-[#71717a]">Min $</label>
              <Input data-testid={`bj-cfg-min-${tableId}`} type="number" value={cfgForm.min_bet} onChange={(e) => setCfgForm((s) => ({ ...s, min_bet: e.target.value }))} placeholder="1" className="h-9 text-sm" />
            </div>
            <div className="w-24">
              <label className="text-[10px] font-semibold uppercase tracking-[.12em] text-[#71717a]">Max $</label>
              <Input data-testid={`bj-cfg-max-${tableId}`} type="number" value={cfgForm.max_bet} onChange={(e) => setCfgForm((s) => ({ ...s, max_bet: e.target.value }))} placeholder="250" className="h-9 text-sm" />
            </div>
            <BtnPrimary data-testid={`bj-cfg-save-${tableId}`} onClick={saveBjConfig} className="h-9">Save table</BtnPrimary>
          </div>
          <div className="mt-2 grid gap-2 sm:grid-cols-[1fr_160px]">
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-[.12em] text-[#71717a]">Public playback URL (WebRTC / YouTube / Twitch / HLS .m3u8)</label>
              <Input data-testid={`bj-cfg-stream-${tableId}`} value={cfgForm.stream_url} onChange={(e) => setCfgForm((s) => ({ ...s, stream_url: e.target.value }))} placeholder="https://…/webRTC/play   or   YouTube / Twitch / …/stream.m3u8" className="h-9 text-sm" />
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-[.12em] text-[#71717a]">Overlay delay (ms)</label>
              <Input data-testid={`bj-cfg-delay-${tableId}`} type="number" min="0" max="30000" step="250" value={cfgForm.stream_delay_ms} onChange={(e) => setCfgForm((s) => ({ ...s, stream_delay_ms: e.target.value }))} className="h-9 text-sm" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-[10px] font-semibold uppercase tracking-[.12em] text-[#71717a]">Operator preview URL (optional · low latency/private)</label>
              <Input
                data-testid={`bj-cfg-operator-stream-${tableId}`}
                value={cfgForm.operator_stream_url}
                onChange={(e) => setCfgForm((state) => ({ ...state, operator_stream_url: e.target.value }))}
                placeholder="Optional local/low-latency camera feed for the card-entry operator"
                className="h-9 text-sm"
              />
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-[.12em] text-[#71717a]">Video provider</label>
              <select
                data-testid={`bj-cfg-provider-${tableId}`}
                value={cfgForm.video_provider}
                onChange={(e) => setCfgForm((s) => ({ ...s, video_provider: e.target.value }))}
                className="h-9 w-full rounded-[9px] border-0 bg-[#0A0A0A] px-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#00D632]/40"
                style={{ boxShadow: "inset 0 0 0 1px #26262B" }}
              >
                <option value="auto">Auto-detect</option>
                <option value="webrtc">WebRTC (low latency)</option>
                <option value="hls">HLS</option>
                <option value="youtube">YouTube</option>
                <option value="twitch">Twitch</option>
                <option value="iframe">Generic iframe</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-[.12em] text-[#71717a]">Live input ID (non-secret)</label>
              <Input
                data-testid={`bj-cfg-cf-input-${tableId}`}
                value={cfgForm.live_input_id}
                onChange={(e) => setCfgForm((s) => ({ ...s, live_input_id: e.target.value }))}
                placeholder="Live input UID (never the publish key)"
                className="h-9 text-sm"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-[10px] font-semibold uppercase tracking-[.12em] text-[#71717a]">Fallback stream URL (optional · e.g. HLS if WebRTC drops)</label>
              <Input
                data-testid={`bj-cfg-fallback-${tableId}`}
                value={cfgForm.fallback_stream_url}
                onChange={(e) => setCfgForm((s) => ({ ...s, fallback_stream_url: e.target.value }))}
                placeholder="Auto-derived from the the live input if left blank; or set a YouTube/Twitch/HLS fallback"
                className="h-9 text-sm"
              />
            </div>
            <p className="text-[10px] text-[#71717a] sm:col-span-2">WebRTC is the low-latency primary; if it drops, the player falls back automatically. The broadcast (WHIP) key is never entered here — it belongs to infrastructure/OBS setup only. Player stream remains the canonical public feed. Use overlay delay only to align player UI with that public video. For the operator in another room, use a direct/local low-latency feed when available so card entry is not delayed twice.</p>
          </div>
        </div>
      )}

      {bj && ["acting", "dealer", "settled"].includes(bj.status) && bj.seats.some((s) => s.occupied && s.hands.some((h) => h.bet > 0)) && (
        <div className="mb-4 rounded-xl border-2 border-[#00D632]/30 bg-black p-3" data-testid={`bj-seat-board-${tableId}`}>
          <p className="mb-2 text-center text-[11px] font-bold uppercase tracking-[0.3em] text-[#a1a1aa]">Dealer board — seat status</p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
            {bj.seats.filter((s) => s.occupied && s.hands.some((h) => h.bet > 0)).map((s) => (
              <div key={s.seat} className={`rounded-xl border-2 p-3 text-center ${bj.active_seat === s.seat && bj.status === "acting" ? "border-[#00D632] bg-[#00D632]/10" : "border-[#27272A] bg-[#141417]"}`} data-testid={`bj-board-seat-${tableId}-${s.seat}`}>
                <p className="text-xs font-bold text-[#a1a1aa]">SEAT #{s.seat + 1} · <span className="text-white">{s.username}</span></p>
                {s.hands.map((h, hi) => {
                  const isActive = bj.active_seat === s.seat && (bj.active_hand ?? 0) === hi && bj.status === "acting" && !bj.awaiting;
                  const awaitingReason = bj.awaiting && bj.awaiting.seat === s.seat && (bj.awaiting.hand ?? 0) === hi ? bj.awaiting.reason : null;
                  const dec = h.bet > 0 && h.cards && h.cards.length > 0 ? bjDecision(h, isActive, awaitingReason) : null;
                  return (
                    <div key={hi} className="mt-1.5">
                      <span className="font-display text-4xl font-black leading-none tabular-nums text-white">{h.cards && h.cards.length ? h.value : "—"}</span>
                      {dec && <div className={`mt-1.5 rounded-md px-2 py-1.5 text-lg font-black uppercase leading-none tracking-wide ${dec.cls}`}>{dec.label}</div>}
                      {s.hands.length > 1 && <p className="text-[9px] text-[#71717a]">hand {hi + 1}</p>}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      {bj && bj.dealer && bj.dealer.length > 0 && (
        <div className="mb-3">
          <p className="mb-1 text-[10px] uppercase tracking-widest text-[#a1a1aa]">Dealer {(bj.status === "dealer" || bj.status === "settled") ? `· ${bj.dealer_value}` : (bj.status === "acting" ? "· showing (hole card face-down)" : "")}</p>
          <div className="flex gap-1">
            {bj.dealer.map((c, i) => <PlayingCard key={i} card={c} size="xs" faceDown={c.rank === "?"} />)}
            {(bj.status === "acting" || bj.status === "insurance") && <PlayingCard faceDown size="xs" />}
          </div>
        </div>
      )}

      {bj && ["dealing", "acting", "dealer"].includes(bj.status) && (
        <div className="mb-3 rounded-[14px] border border-[#1F1F23] bg-[#0C0C0E] p-4" data-testid={`bj-card-entry-${tableId}`}>
          <div className="mb-2.5 flex items-center justify-between gap-2">
            <span className="text-[11px] font-bold uppercase tracking-[.14em] text-[#71717a]">Card entry</span>
            <span className="rounded-md bg-[#18181C] px-2 py-1 font-display text-sm font-extrabold" style={{ color: selSuit === "H" || selSuit === "D" ? "#EF4444" : "#fff" }}>{selRank}{BJ_SUIT[selSuit]}</span>
          </div>
          <p className="mb-2.5 text-[13px] font-semibold text-[#00D632]">
            {bj.status === "dealing" ? "Deal 2 cards to each seat + 1 up-card to the dealer" :
             bj.status === "acting" && bj.awaiting ? `Card needed for Seat #${bj.awaiting.seat + 1} (${bj.awaiting.reason})` :
             bj.status === "acting" ? "Players are deciding (15s each, seat 1 → 7)…" :
             "All seats done — enter the dealer's hole card + draws, then settle below"}
          </p>
          {/* Rank grid (13, 7-col) — premium AD-04 */}
          <div className="mb-2 grid grid-cols-7 gap-1">
            {BJ_RANKS_UI.map((r) => (
              <button key={r} data-testid={`bj-rank-${tableId}-${r}`} onClick={() => setSelRank(r)} className={`flex h-[30px] items-center justify-center rounded-[6px] text-[12px] font-bold ${selRank === r ? "bg-[#00D632] text-[#0A0A0A]" : "bg-[#18181C] text-white hover:bg-[#26262B]"}`}>{r}</button>
            ))}
          </div>
          {/* Suit grid (4) */}
          <div className="mb-3 grid grid-cols-4 gap-1">
            {[["S", "♠"], ["H", "♥"], ["D", "♦"], ["C", "♣"]].map(([v, sym]) => {
              const red = v === "H" || v === "D";
              return (
                <button key={v} data-testid={`bj-suit-${tableId}-${v}`} onClick={() => setSelSuit(v)} className={`flex h-[30px] items-center justify-center rounded-[6px] text-[14px] font-bold ${selSuit === v ? "bg-[#00D632] text-[#0A0A0A]" : `bg-[#18181C] ${red ? "text-[#EF4444]" : "text-white"} hover:bg-[#26262B]`}`}>{sym}</button>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-2">
            {bj.status === "dealing" && <>
              {bj.seats.filter((s) => s.occupied && s.hands.some((h) => h.bet > 0)).map((s) => (
                <button key={s.seat} data-testid={`bj-deal-seat-${tableId}-${s.seat}`} disabled={s.hands[0].cards.length >= 2} onClick={() => addCard(s.seat)} className="flex h-[38px] items-center rounded-[10px] bg-[#00D632] px-3.5 text-[13px] font-bold text-[#0A0A0A] hover:bg-[#00c02d] disabled:opacity-40">Seat #{s.seat + 1} · {s.hands[0].cards.length}/2</button>
              ))}
              <button data-testid={`bj-deal-dealer-${tableId}`} disabled={bj.dealer.length >= 1} onClick={() => addCard(-1)} className="flex h-[38px] items-center rounded-[10px] border border-[#1F1F23] bg-[#141417] px-3.5 text-[13px] font-semibold text-white hover:bg-[#1a1a1e] disabled:opacity-40">Dealer up · {bj.dealer.length}/1</button>
            </>}
            {bj.status === "acting" && bj.awaiting && <button data-testid={`bj-give-card-${tableId}`} onClick={() => addCard(bj.awaiting.seat)} className="flex h-[38px] items-center rounded-[10px] bg-[#00D632] px-3.5 text-[13px] font-bold text-[#0A0A0A] hover:bg-[#00c02d]">Give {selRank}{BJ_SUIT[selSuit]} to Seat #{bj.awaiting.seat + 1}</button>}
            {bj.status === "dealer" && <button data-testid={`bj-dealer-card-${tableId}`} onClick={() => addCard(-1)} className="flex h-[38px] items-center rounded-[10px] bg-[#00D632] px-3.5 text-[13px] font-bold text-[#0A0A0A] hover:bg-[#00c02d]">Add dealer {selRank}{BJ_SUIT[selSuit]} · now {bj.dealer_value}</button>}
            <button data-testid={`bj-quick-add-${tableId}`} onClick={submitCard} className="flex h-[38px] items-center rounded-[10px] bg-[#00D632] px-4 font-display text-[13px] font-extrabold text-[#0A0A0A] hover:bg-[#00c02d]">Add {selRank}{BJ_SUIT[selSuit]}</button>
            <button data-testid={`bj-undo-${tableId}`} onClick={() => bjAction("/admin/bj/undo")} className="flex h-[38px] items-center gap-1.5 rounded-[10px] border border-[#1F1F23] bg-[#141417] px-3.5 text-[13px] font-semibold text-[#00D632] hover:bg-[#1a1a1e]"><Icon name="refresh" size={15} /> Undo</button>
          </div>
          {focused && <p className="mt-2.5 text-[11px] text-[#52525b]">Rank + suit, then <b className="text-[#a1a1aa]">Enter</b> · <b className="text-[#a1a1aa]">Backspace</b> undoes.</p>}
        </div>
      )}

      {bj && (bj.status === "acting" || bj.status === "dealer") && (() => {
        const RESULTS = [
          { id: "win", label: "WIN", cls: "bg-[#00D632] text-[#0A0A0A]" },
          { id: "blackjack", label: "BJ 3:2", cls: "bg-surface-5 text-white" },
          { id: "push", label: "PUSH", cls: "bg-[#52525B] text-white" },
          { id: "lose", label: "LOSE", cls: "bg-[#EF4444] text-white" },
          { id: "surrender", label: "SURR", cls: "bg-surface-5 text-white" },
        ];
        const rows = [];
        (bj.seats || []).forEach((s) => {
          if (!s.occupied) return;
          s.hands.forEach((h, hi) => { if (h.bet > 0) rows.push({ seat: s.seat, hi, h, user: s.username }); });
        });
        if (rows.length === 0) return null;
        const settleAll = () => {
          const results = {};
          rows.forEach((r) => { results[`${r.seat}-${r.hi}`] = settleSel[`${r.seat}-${r.hi}`] || r.h.outcome || "lose"; });
          bjAction("/admin/bj/settle", { results });
          setSettleSel({});
        };
        const autoSettle = () => { bjAction("/admin/bj/settle", { results: {} }); setSettleSel({}); };
        return (
          <div className="mt-4 rounded-xl border border-surface-border bg-white/5 p-4" data-testid={`bj-settle-panel-${tableId}`}>
            <p className="mb-1 font-display text-sm font-bold text-[#00D632]">Settle the table — enter the dealer's hole card + draws, then pay</p>
            <p className="mb-3 text-[10px] text-[#a1a1aa]">Once every seat is done, deal the dealer's hole card + any draws above, then tap <b className="text-[#00D632]">Auto-settle</b> to pay winners straight from the cards, or adjust any seat and use Settle &amp; pay. Dealer {(bj.status === "dealer" || bj.status === "settled") ? `total ${bj.dealer_value}` : "hole card still face-down"}.</p>
            <Button data-testid={`bj-auto-settle-${tableId}`} onClick={autoSettle} className="mb-3 w-full rounded-xl bg-[#00D632] py-5 font-black text-[#0A0A0A] hover:bg-[#00B829]">⚡ Auto-settle — pay winners by the cards</Button>
            <p className="mb-2 text-[10px] uppercase tracking-[0.2em] text-[#71717a]">or override any seat, then settle:</p>
            <div className="space-y-2">
              {rows.map((r) => {
                const key = `${r.seat}-${r.hi}`;
                const sel = settleSel[key] || r.h.outcome || "lose";
                return (
                  <div key={key} data-testid={`bj-settle-row-${tableId}-${key}`} className="flex flex-wrap items-center gap-2 rounded-lg bg-[#1C1C21] px-3 py-2">
                    <span className="min-w-[130px] text-xs text-white">Seat #{r.seat + 1} · <b>{r.user}</b> · <span className="tabular-nums text-[#a1a1aa]">{r.h.value}</span> · ${r.h.bet.toFixed(2)}</span>
                    <div className="flex gap-1">
                      {RESULTS.map((res) => (
                        <button key={res.id} data-testid={`bj-settle-${tableId}-${key}-${res.id}`} onClick={() => setSettleSel((p) => ({ ...p, [key]: res.id }))}
                          className={`rounded px-2 py-1 text-[10px] font-black uppercase transition-opacity ${sel === res.id ? res.cls : "bg-[#27272A] text-[#71717a] hover:text-white"}`}>{res.label}</button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            <Button size="sm" variant="outline" data-testid={`bj-settle-pay-${tableId}`} onClick={settleAll} className="mt-3 rounded-full border-surface-border text-[#00D632] hover:bg-[#00B829]/5">Settle &amp; pay all (with my changes)</Button>
          </div>
        );
      })()}

      {bj && bj.history && bj.history.length > 0 && (
        <div className="mt-4 rounded-xl border border-[#27272A] bg-[#141417] p-4" data-testid={`bj-history-${tableId}`}>
          <p className="mb-2 font-display text-sm font-bold tracking-tight">Recent rounds <span className="text-[10px] font-normal text-[#a1a1aa]">(last {bj.history.length}, for dispute checks)</span></p>
          <div className="space-y-2">
            {bj.history.map((r, ri) => {
              const rc = { win: "text-[#00D632]", blackjack: "text-[#00D632]", push: "text-[#a1a1aa]", surrender: "text-[#a1a1aa]", lose: "text-[#EF4444]" };
              const cardStr = (cards) => (cards || []).map((c) => `${c.rank}${BJ_SUIT[c.suit] || ""}`).join(" ");
              return (
                <div key={ri} className="rounded-lg bg-[#1C1C21] px-3 py-2 text-xs">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="font-bold text-white">Round #{r.round_number}</span>
                    <span className="text-[#a1a1aa]">Dealer <b className="text-white">{r.dealer.value}</b> · {cardStr(r.dealer.cards)}</span>
                  </div>
                  <div className="space-y-0.5">
                    {r.seats.map((s, si) => (
                      <div key={si} className="flex items-center justify-between gap-2">
                        <span className="text-[#a1a1aa]">#{s.seat + 1} {s.username} · <span className="tabular-nums text-white">{s.value}</span> · {cardStr(s.cards)}</span>
                        <span className={`font-black uppercase ${rc[s.result] || "text-[#a1a1aa]"}`}>{s.result} {s.payout > 0 ? `$${s.payout.toFixed(2)}` : ""}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      </div>
    </div>
  );
}


/* -------- House P/L -------- */
/* Live Tables — the prominent operator surface: set up + run the live Baccarat &
   Blackjack tables and reveal/rotate each table's OBS stream keys (super admin). */
/* Live Baccarat stream keys + Twitch fallback — same UX as the blackjack panels. */
function BaccaratStreamKeys() {
  const { confirm } = useConfirm();
  const [streamKeys, setStreamKeys] = useState(null);
  const [streamBusy, setStreamBusy] = useState(false);
  const [showKeys, setShowKeys] = useState({});
  const [twitchFb, setTwitchFb] = useState("");
  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      const fb = data?.baccarat_stream?.fallback_stream_url || "";
      setTwitchFb((/twitch\.tv\/([^/?#]+)/i.exec(fb) || [])[1] || "");
    }).catch(() => {});
  }, []);
  const copyStreamValue = async (value, labelText) => { if (!value) return; try { await navigator.clipboard.writeText(value); toast.success(`${labelText} copied`); } catch { toast.error("Copy failed — select and copy manually"); } };
  const reveal = async () => { setStreamBusy(true); try { const { data } = await api.post("/admin/bj/stream-credentials", { table: "baccarat" }); setStreamKeys(data); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setStreamBusy(false); } };
  const rotate = async () => { if (!(await confirm({ title: "Rotate Baccarat stream keys", body: "This generates NEW keys for the Baccarat table. Any active OBS broadcast must restart with the new key.", confirmText: "Rotate keys" }))) return; setStreamBusy(true); try { const { data } = await api.post("/admin/bj/stream-rotate", { table: "baccarat" }); setStreamKeys(data); toast.success("New stream keys generated — restart OBS with them"); } catch (e) { if (e.response?.status === 501) toast.error("Rotation not configured yet — a streaming API credentials are needed."); else toast.error(apiError(e.response?.data?.detail)); } finally { setStreamBusy(false); } };
  const saveFallback = async () => { const u = twitchFb.trim().replace(/^@/, "").split("/").pop(); const url = u ? `https://www.twitch.tv/${u}` : ""; try { await api.post("/admin/baccarat/stream-config", { fallback_stream_url: url }); toast.success(u ? `Twitch fallback set to ${u}` : "Twitch fallback cleared"); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  return (
    <div data-testid="baccarat-stream-keys">
      <p className="mb-2 text-[11px] text-[#71717a]">The table player is low-latency <b className="text-[#d4d4d8]">WebRTC</b>. In OBS 30+ → <b className="text-[#d4d4d8]">Settings → Stream → Service: WHIP</b>, paste the <b className="text-[#d4d4d8]">WHIP URL</b> below and leave <b className="text-[#d4d4d8]">Bearer Token blank</b>. If it drops, players fall back to the Twitch channel.</p>
      <p className="mb-2.5 rounded-[8px] border border-[#EF4444]/30 bg-[#EF4444]/[.08] px-2.5 py-1.5 text-[10px] leading-snug text-[#d4d4d8]"><b className="text-[#EF4444]">Must use WHIP for the live player.</b> The stream provider only serves the WebRTC player for streams published via WHIP. RTMPS/SRT will not show on the WebRTC player.</p>
      <div className="flex flex-wrap gap-2">
        <BtnPrimary disabled={streamBusy} data-testid="baccarat-stream-reveal" onClick={reveal}>{streamBusy ? "…" : (streamKeys ? "Refresh keys" : "Reveal keys")}</BtnPrimary>
        <BtnGhost disabled={streamBusy} data-testid="baccarat-stream-rotate" onClick={rotate}>Rotate key</BtnGhost>
      </div>
      <div className="mt-3 rounded-[12px] p-3" style={{ background: "#0C0C0E", boxShadow: "inset 0 0 0 1px #1F1F23" }}>
        <label className="text-[10px] font-semibold uppercase tracking-[.12em] text-[#71717a]">Twitch fallback (username)</label>
        <p className="mb-1.5 text-[10px] text-[#71717a]">If the WebRTC feed isn't working, players auto-switch to this Twitch channel. Enter just the username — leave blank for none.</p>
        <div className="flex gap-2">
          <input data-testid="baccarat-twitch-fb" value={twitchFb} onChange={(e) => setTwitchFb(e.target.value)} placeholder="twitch username"
            className="h-9 min-w-0 flex-1 rounded-[9px] border-0 bg-[#0A0A0A] px-2.5 text-[13px] font-medium text-white placeholder:text-[#52525b] focus:outline-none focus:ring-1 focus:ring-[#00D632]/40"
            style={{ boxShadow: "inset 0 0 0 1px #26262B" }} />
          <BtnPrimary data-testid="baccarat-twitch-fb-save" onClick={saveFallback} className="h-9">Save</BtnPrimary>
        </div>
      </div>
      {streamKeys && (
        <div className="mt-3 space-y-2.5" data-testid="baccarat-stream-values">
          <SecretField labelText="① OBS Server (WHIP URL · Service: WHIP)" value={streamKeys.whip_url} fieldKey="b_whip" show={showKeys} setShow={setShowKeys} onCopy={copyStreamValue} />
          <p className="text-[10px] text-[#71717a]">② OBS Bearer Token — leave <b className="text-[#d4d4d8]">blank</b> (the key is already in the WHIP URL).</p>
          <details className="pt-1"><summary className="cursor-pointer list-none text-[10px] font-semibold text-[#71717a] hover:text-white">▸ Advanced — RTMPS / SRT (HLS playback only · NOT the WebRTC player)</summary>
            <div className="mt-2 space-y-2">
              <p className="text-[10px] leading-snug text-[#a1a1aa]">Only use these if your encoder can't do WHIP. RTMPS/SRT plays via the HLS fallback (a few seconds behind) — not the low-latency WebRTC player.</p>
              <SecretField labelText="RTMPS Server" value={streamKeys.rtmps?.url} fieldKey="b_rtmps_url" show={showKeys} setShow={setShowKeys} onCopy={copyStreamValue} />
              <SecretField labelText="RTMPS Stream Key" value={streamKeys.rtmps?.streamKey} fieldKey="b_rtmps_key" show={showKeys} setShow={setShowKeys} onCopy={copyStreamValue} />
              <SecretField labelText="SRT URL" value={streamKeys.srt?.url} fieldKey="b_srt_url" show={showKeys} setShow={setShowKeys} onCopy={copyStreamValue} />
              <SecretField labelText="SRT stream ID" value={streamKeys.srt?.streamId} fieldKey="b_srt_id" show={showKeys} setShow={setShowKeys} onCopy={copyStreamValue} />
              <SecretField labelText="SRT passphrase" value={streamKeys.srt?.passphrase} fieldKey="b_srt_pass" show={showKeys} setShow={setShowKeys} onCopy={copyStreamValue} />
            </div>
          </details>
          <p className="text-[10px] text-[#71717a]">Live input: {streamKeys.live_input_name || "—"}</p>
        </div>
      )}
    </div>
  );
}

function LiveTablesTab() {
  const [channel, setChannel] = useState("");
  const [savingCh, setSavingCh] = useState(false);
  const [live, setLive] = useState(null);
  const loadLive = () => api.get("/live/current").then(({ data }) => setLive(data)).catch(() => {});
  useEffect(() => { loadLive(); const id = setInterval(loadLive, 4000); return () => clearInterval(id); }, []);
  const [bacc, setBacc] = useState({ p: ["", "", ""], b: ["", "", ""] });
  const BACC_RANKS = ["A", "2", "3", "4", "5", "6", "7", "8", "9", "10", "J", "Q", "K"];
  const BACC_VAL = { A: 1, "2": 2, "3": 3, "4": 4, "5": 5, "6": 6, "7": 7, "8": 8, "9": 9, "10": 0, J: 0, Q: 0, K: 0 };
  const baccTotal = (cards) => cards.filter(Boolean).reduce((a, c) => a + (BACC_VAL[c] || 0), 0) % 10;
  const [focusedBj, setFocusedBj] = useState("main");
  // AD-04 header pill tabs. Providers integration is a future/blocked feature with no real
  // data yet, so its tab renders an honest "not connected" empty state (never fabricated).
  const [csTab, setCsTab] = useState("tables");
  // Operated live blackjack tables that get a full dealer-control card (operator console,
  // Dealer TV, stream keys, config, round controls). Breakout is a real live table — the
  // backend /admin/bj/* endpoints + :tableId operator/dealer-TV routes already support it.
  const BJ_TABLES = [{ id: "main", label: "Main Blackjack" }, { id: "vip", label: "VIP Blackjack" }, { id: "breakout", label: "Breakout Blackjack" }];
  const liveTableCount = 1 + BJ_TABLES.length; // Baccarat + blackjack tables
  const liveAction = async (path, body) => {
    try { const { data } = await api.post(path, body); setLive(data); toast.success("Live table updated"); return true; }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); return false; }
  };
  useEffect(() => { api.get("/settings").then(({ data }) => setChannel(data.twitch_channel || "")).catch(() => {}); }, []);
  const saveChannel = async () => {
    setSavingCh(true);
    try { await api.post("/admin/settings", { twitch_channel: channel }); toast.success("Live stream channel updated"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSavingCh(false); }
  };
  const baccOn = live && String(live.status).toLowerCase() !== "none" && live.status != null;
  // AD-04 phase tabs — baccarat has NO player turns, so no "Player decisions" phase.
  // Lit honestly from the real live.status (betting → open, locked → dealing, settled → settling).
  const BACC_PHASES = [
    { key: "open", label: "Betting open" },
    { key: "dealing", label: "Dealing" },
    { key: "settling", label: "Settling" },
  ];
  const baccPhase = !baccOn ? null
    : live.status === "betting" ? "open"
    : live.status === "locked" ? "dealing"
    : live.status === "settled" ? "settling"
    : null;
  return (
    <div className="space-y-6">
      {/* AD-04 section header: Casino title + subtitle · pill tabs · Dealer TV / Operator console */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-col gap-3.5">
          <div className="flex flex-wrap items-baseline gap-3">
            <span className="font-display text-[30px] font-extrabold tracking-[-.03em] text-white">Casino</span>
            <span className="text-[14px] font-medium text-[#71717a]">Live dealer tables and game providers</span>
          </div>
          <div className="flex w-max max-w-full items-center gap-0.5 overflow-hidden rounded-full border border-[#1F1F23] bg-[#111114] p-[3px]">
            <button data-testid="cs-tab-tables" onClick={() => setCsTab("tables")} className={`flex h-8 flex-none items-center gap-1.5 rounded-full px-3.5 text-[13px] ${csTab === "tables" ? "bg-white font-bold text-[#0A0A0A]" : "font-semibold text-[#a1a1aa] hover:bg-[#18181C] hover:text-white"}`}>Live tables <span className="font-bold tabular-nums text-[#71717a]">{liveTableCount}</span></button>
            <button data-testid="cs-tab-providers" onClick={() => setCsTab("providers")} className={`flex h-8 flex-none items-center gap-1.5 rounded-full px-3.5 text-[13px] ${csTab === "providers" ? "bg-white font-bold text-[#0A0A0A]" : "font-semibold text-[#a1a1aa] hover:bg-[#18181C] hover:text-white"}`}>Providers <span className="font-bold tabular-nums text-[#71717a]">0</span></button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a href={`/admin/live-blackjack/main/dealer-tv`} target="_blank" rel="noreferrer" className="flex h-9 items-center gap-1.5 rounded-[9px] px-3 text-[13px] font-semibold text-[#E4E4E7]" style={{ background: "#18181C", boxShadow: "inset 0 0 0 1px #26262B" }}><Icon name="livedealer" size={14} /> Dealer TV</a>
          <a href={`/admin/live-blackjack/main/operator`} className="flex h-9 items-center gap-1.5 rounded-[9px] bg-[#00D632] px-3.5 font-display text-[13px] font-extrabold text-[#0A0A0A] hover:bg-[#00B829]">Operator console <Icon name="arrow-right" size={14} /></a>
        </div>
      </div>

      {csTab === "providers" ? (
        <Panel className="flex flex-col items-center gap-2 px-6 py-16 text-center" data-testid="cs-providers-empty">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#111114] text-[#52525b]" style={{ boxShadow: "inset 0 0 0 1px #1F1F23" }}><Icon name="casino" size={20} /></span>
          <p className="font-display text-[15px] font-bold text-white">No game providers connected yet</p>
          <p className="max-w-md text-[13px] font-medium text-[#71717a]">Third-party casino-provider integration isn't wired up yet. When a provider is connected, its games and status will appear here. Use the Live tables tab to run PRYMEBET's own live dealer tables.</p>
        </Panel>
      ) : (
      <div className="space-y-8">
      <div className="overflow-hidden rounded-[14px] border border-[#1F1F23] bg-[#111114]" data-testid="baccarat-panel">
      {/* AD-04 card header row: icon + name + status pill + meta · phase tabs (no operator/dealer-tv route for baccarat) */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-[#1F1F23] px-5 py-3.5">
        <span className="flex h-5 w-5 flex-none items-center justify-center text-white"><Icon name="baccarat" size={18} /></span>
        <span className="font-display text-[16px] font-bold text-white">Live Baccarat</span>
        <StatusPill status={baccOn ? "LIVE" : "OFFLINE"} bg={baccOn ? "rgba(0,214,50,.12)" : "#26262B"} fg={baccOn ? "#00D632" : "#71717a"} />
        <span className="text-[13px] font-medium text-[#71717a]">
          Round <b className="text-[#d4d4d8]">#{live?.round_number ?? "—"}</b>{live?.bet_count != null && <> · {live.bet_count} bets</>}
        </span>
        <div className="ml-auto flex flex-wrap items-center gap-1">
          {BACC_PHASES.map((p) => (baccPhase === p.key
            ? <span key={p.key} className="flex h-7 items-center gap-1.5 rounded-full bg-[#1C1C21] px-2.5 text-[12px] font-semibold text-white"><span className="h-1.5 w-1.5 rounded-full bg-[#00D632]" />{p.label}</span>
            : <span key={p.key} className="flex h-7 items-center px-2.5 text-[12px] font-medium text-[#52525b]">{p.label}</span>))}
        </div>
      </div>
      {/* AD-04 two-column body: LEFT = STREAM · RIGHT = ROUND CONTROLS (no seat board — baccarat has no seats) */}
      <div className="grid grid-cols-1 lg:grid-cols-2">
      {/* LEFT column = STREAM */}
      <div className="flex flex-col gap-3.5 p-5 lg:border-r lg:border-[#1F1F23]">
        <div className="flex items-center gap-2">
          <span className="text-[12px] font-bold uppercase tracking-[.1em] text-[#71717a]">Stream</span>
          <span className="ml-auto flex items-center gap-1.5 text-[12px] font-medium text-[#a1a1aa]"><span className="h-[7px] w-[7px] rounded-full" style={{ background: baccOn ? "#00D632" : "#52525b" }} />{baccOn ? "Table live" : "Table offline"}</span>
        </div>
        <BaccaratStreamKeys />
      </div>

      {/* RIGHT column = ROUND CONTROLS */}
      <div className="flex flex-col gap-3.5 p-5">
        <div className="flex items-center gap-2">
          <span className="text-[12px] font-bold uppercase tracking-[.1em] text-[#71717a]">Round controls</span>
          <span className="ml-auto font-display text-[13px] font-bold text-white">
            {live?.status === "betting" ? "Betting open" : live?.status === "locked" ? "Dealing" : live?.status === "settled" ? "Settled" : ""}
          </span>
        </div>

        <div className="flex flex-wrap gap-2" data-testid="live-dealer-control">
          <BtnPrimary data-testid="live-new-round" onClick={() => liveAction("/admin/live/new")}>New round (open betting)</BtnPrimary>
          <BtnGhost data-testid="live-lock" onClick={() => liveAction("/admin/live/lock")}>Lock betting</BtnGhost>
        </div>

        <div className="rounded-[12px] p-3" style={{ background: "#0C0C0E", boxShadow: "inset 0 0 0 1px #1F1F23" }} data-testid="live-card-entry">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-[.12em] text-[#71717a]">Enter the cards dealt (up to 3 per side) — app computes the score</p>
          <div className="grid grid-cols-2 gap-4">
            {[{ key: "p", label: "Player", testid: "player", color: "text-[#2563EB]" }, { key: "b", label: "Banker", testid: "banker", color: "text-[#EF4444]" }].map((side) => (
              <div key={side.key}>
                <div className="mb-1 flex items-center justify-between">
                  <label className={`text-[10px] font-bold uppercase tracking-[0.15em] ${side.color}`}>{side.label}</label>
                  <span className="font-display text-2xl font-black tabular-nums text-white" data-testid={`live-${side.testid}-computed`}>{baccTotal(bacc[side.key])}</span>
                </div>
                <div className="flex gap-1.5">
                  {[0, 1, 2].map((ci) => (
                    <select key={ci} data-testid={`live-${side.testid}-card-${ci}`} value={bacc[side.key][ci]}
                      onChange={(e) => setBacc((s) => { const arr = [...s[side.key]]; arr[ci] = e.target.value; return { ...s, [side.key]: arr }; })}
                      className="h-9 flex-1 rounded-[9px] border-0 bg-[#0A0A0A] px-1 text-center text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#00D632]/40"
                      style={{ boxShadow: "inset 0 0 0 1px #26262B" }}>
                      <option value="">{ci < 2 ? "card" : "3rd?"}</option>
                      {BACC_RANKS.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <Button size="sm" data-testid="live-settle-cards"
            onClick={async () => {
              const pc = bacc.p.filter(Boolean), bc = bacc.b.filter(Boolean);
              if (pc.length < 2 || bc.length < 2) { toast.error("Enter at least 2 cards for each side"); return; }
              const ok = await liveAction("/admin/live/settle", { player_cards: pc, banker_cards: bc });
              if (ok) setBacc({ p: ["", "", ""], b: ["", "", ""] });
            }}
            className="mt-3 w-full rounded-[12px] bg-[#00D632] py-4 font-display font-extrabold text-[#0A0A0A] hover:bg-[#00c02d]">Settle from cards (Player {baccTotal(bacc.p)} · Banker {baccTotal(bacc.b)})</Button>
          <p className="mt-2 text-[10px] text-[#71717a]">Winner = higher total (equal = tie). A two-card 8 or 9 is a natural. Bonus pays: natural 1:1 · by 4 = 1:1 · 5 = 2:1 · 6 = 4:1 · 7 = 6:1 · 8 = 10:1 · 9 = 30:1.</p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[13px] font-medium text-[#71717a]">Quick winner (refunds bonus bets):</span>
          <Button size="sm" data-testid="live-settle-player" onClick={() => liveAction("/admin/live/settle", { winner: "player" })} className="rounded-[10px] bg-[#2563EB] font-bold text-white hover:bg-[#2563EB]/90">Player</Button>
          <Button size="sm" data-testid="live-settle-banker" onClick={() => liveAction("/admin/live/settle", { winner: "banker" })} className="rounded-[10px] bg-[#EF4444] font-bold text-white hover:bg-[#EF4444]/90">Banker</Button>
          <Button size="sm" data-testid="live-settle-tie" onClick={() => liveAction("/admin/live/settle", { winner: "tie" })} className="rounded-[10px] bg-[#00D632] font-bold text-[#0A0A0A] hover:bg-[#00c02d]">Tie</Button>
        </div>
      </div>
      </div>
      </div>

      <div className="space-y-6" data-testid="bj-dealer-controls">
        <div>
          <CardHead title="Live Dealer Blackjack — all tables" />
          <p className="mt-1 text-[13px] font-medium text-[#71717a]">Each table has its own panel below (rename, limits, stream keys, seats &amp; dealing). Deal 2 cards to every seat + the dealer's single up-card; players then auto-decide 15s each in seat order (1 → 7). After the last seat, deal the dealer's hole card &amp; draws, then settle. Click a panel to use its keyboard shortcuts.</p>
        </div>
        {BJ_TABLES.map((r) => (
          <BlackjackTablePanel key={r.id} tableId={r.id} label={r.label} accent={r.accent} focused={focusedBj === r.id} onFocus={() => setFocusedBj(r.id)} />
        ))}
      </div>
      </div>
      )}
    </div>
  );
}

function HouseTab() {
  const [h, setH] = useState(null);
  // The /admin/house endpoint returns a single all-time aggregate — it takes no
  // range param and exposes no daily series. Rather than show period toggles
  // (24h/7d/30d) that would all return the SAME figure (misleading), the view is
  // honestly labeled "All-time". Add ranged aggregates server-side to reintroduce.
  useEffect(() => { api.get("/admin/house").then(({ data }) => setH(data)).catch(() => {}); }, []);
  if (!h) return <div className="flex justify-center py-16"><Icon name="refresh" size={24} className="animate-spin text-[#00D632]" /></div>;

  const money = (n) => `${n < 0 ? "−" : ""}$${Math.abs(n).toFixed(2)}`;
  const num = (n) => `${n < 0 ? "−" : ""}${Math.abs(n).toFixed(2)}`;
  const signedMoney = (n) => `${n < 0 ? "−" : "+"}$${Math.abs(n).toFixed(2)}`;
  const Profit = ({ v }) => (
    <span className={`inline-flex items-center gap-1 font-bold tabular-nums ${v >= 0 ? "text-[#00D632]" : "text-[#EF4444]"}`}>
      {v >= 0 ? <Icon name="zap" size={16} /> : <Icon name="trending-down" size={16} />}{num(v)}
    </span>
  );

  // AD-03a / AD-M5 "House P/L" panel — real PCash data only.
  // Headline net = PCash total profit. There is no daily series or prior-period
  // figure from the endpoint, so the "% vs prior" delta is omitted (never faked)
  // and the bar chart is derived from the REAL per-game profit breakdown rather
  // than a fabricated 7-day trend — each bar maps to one game's actual profit.
  const cash = h.cash;
  const plGames = (cash.by_game || []).slice(0, 6).map((g) => ({
    name: (g.game || "other").replace(/_/g, " "),
    profit: g.profit,
  }));
  const maxAbs = Math.max(1, ...plGames.map((g) => Math.abs(g.profit)));
  const bars = plGames.map((g) => ({
    label: (g.name.split(" ")[0] || "").slice(0, 4),
    heightPct: Math.max(6, Math.round((Math.abs(g.profit) / maxAbs) * 100)),
    color: g.profit >= 0 ? "#00D632" : "#EF4444",
  }));
  const GREEN = "#00D632", RED = "#EF4444";

  // Currency detail — Casino + Sportsbook breakdown in the restrained kit style.
  // No glow headline (net profit is already on the House P/L panel above) and no
  // "Casino by game" table for cash (that lives in the panel's per-game list).
  // Pending liability is a single per-currency figure from the endpoint, so it is
  // shown once (under Sportsbook, its origin) — not duplicated, to avoid implying
  // two separate liabilities. Green is used only on the profit VALUE text.
  const GRID_GAME = "minmax(0,1fr) 70px 110px 110px";
  const DetailBlock = ({ title, d, fmt, testid, showGames }) => (
    <Panel className="flex flex-col gap-4 p-5" data-testid={testid}>
      <CardHead title={title} />
      <div className="grid gap-2.5 sm:grid-cols-2">
        <Panel className="flex flex-col gap-1 bg-[#111114] p-4">
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-[.12em] text-[#71717a]">Casino</p>
          <Row label="Wagered" value={fmt(d.casino_wagered)} />
          <Row label="Paid out" value={fmt(d.casino_paid)} />
          <Row label="Profit" node={<Profit v={d.casino_profit} />} />
          <Row label="Plays" value={d.plays} />
        </Panel>
        <Panel className="flex flex-col gap-1 bg-[#111114] p-4">
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-[.12em] text-[#71717a]">Sportsbook</p>
          <Row label="Wagered" value={fmt(d.sb_wagered)} />
          <Row label="Paid out" value={fmt(d.sb_paid)} />
          <Row label="Profit" node={<Profit v={d.sb_profit} />} />
          <Row label="Settled bets" value={d.settled_bets} />
          <Row label="Pending liability" value={fmt(d.pending_liability)} />
        </Panel>
      </div>
      {showGames && d.by_game.length > 0 && (
        <DataTable
          cols={GRID_GAME}
          header={["GAME", { label: "PLAYS", right: true }, { label: "WAGERED", right: true }, { label: "PROFIT", right: true }]}
        >
          {d.by_game.map((g) => (
            <TRow key={g.game} cols={GRID_GAME} data-testid={`house-game-row-${g.game}`}>
              <span className="truncate capitalize font-medium" data-testid={`house-game-label-${g.game}`}>{(g.game || "").replace(/_/g, " ")}</span>
              <span className="text-right tabular-nums">{g.plays}</span>
              <span className="text-right tabular-nums">{fmt(g.wagered)}</span>
              <span className="text-right font-display font-extrabold tabular-nums" style={{ color: g.profit >= 0 ? GREEN : RED }}>{fmt(g.profit)}</span>
            </TRow>
          ))}
        </DataTable>
      )}
    </Panel>
  );

  return (
    <div className="space-y-8">
      {/* House P/L panel — 1:1 with design 07 Admin · AD-03a (desktop) / AD-M5 (mobile) */}
      <Panel className="flex flex-col gap-4 p-5" data-testid="house-pl-panel">
        <div className="flex items-center">
          <span className="font-display text-[18px] font-extrabold">House P/L</span>
          <span data-testid="house-range-label" className="ml-auto rounded-[7px] bg-[#26262B] px-2.5 py-[5px] text-[12px] font-bold text-[#d4d4d8]">All-time</span>
        </div>
        <div className="flex flex-wrap items-baseline gap-3">
          <span
            className="font-display text-[42px] font-extrabold leading-none tracking-[-.04em] tabular-nums sm:text-[48px]"
            style={{ color: cash.total_profit >= 0 ? GREEN : RED }}
            data-testid="house-pl-net"
          >{signedMoney(cash.total_profit)}</span>
          <span className="text-[13px] font-medium text-[#a1a1aa]">PCash net</span>
        </div>
        {bars.length > 0 && (
          <div className="flex h-[120px] items-end gap-2 rounded-2xl border border-[#1F1F23] bg-[#111114] p-3" data-testid="house-pl-bars">
            {bars.map((b, i) => (
              <div key={i} className="flex h-full flex-1 flex-col items-center justify-end gap-1.5">
                <div className="w-full rounded-t-[8px] rounded-b-[3px]" style={{ height: `${b.heightPct}%`, background: b.color }} />
                <span className="text-[10px] font-semibold capitalize text-[#52525b]">{b.label}</span>
              </div>
            ))}
          </div>
        )}
        {plGames.length > 0 && (
          <div className="flex flex-col gap-2 rounded-2xl border border-[#1F1F23] bg-[#111114] p-3 sm:border-0 sm:bg-transparent sm:p-0" data-testid="house-pl-games">
            {plGames.map((g) => (
              <div key={g.name} data-testid={`house-pl-game-${g.name.replace(/\s+/g, "-")}`} className="grid grid-cols-[100px_minmax(0,1fr)_80px] items-center gap-2.5 sm:grid-cols-[120px_minmax(0,1fr)_90px]">
                <span className="truncate text-[13px] font-medium capitalize text-[#d4d4d8]">{g.name}</span>
                <div className="h-2 rounded-full bg-[#1C1C21]">
                  <div className="h-full rounded-full" style={{ width: `${Math.max(4, Math.round((Math.abs(g.profit) / maxAbs) * 100))}%`, background: g.profit >= 0 ? GREEN : RED }} />
                </div>
                <span className="text-right font-display text-[13px] font-extrabold tabular-nums" style={{ color: g.profit >= 0 ? GREEN : RED }}>{signedMoney(g.profit)}</span>
              </div>
            ))}
          </div>
        )}
      </Panel>

      {/* PCash breakdown — additional detail not shown on the panel headline.
          Cash per-game lives in the panel above, so games are hidden here. */}
      <DetailBlock title="PCash" d={h.cash} fmt={money} testid="house-cash" showGames={false} />

      {/* Free PCoins breakdown — only rendered when real free-coin activity
          exists (otherwise the panel above already tells the whole story). Its
          per-game table IS kept here since it appears nowhere else. */}
      {(h.coins.plays > 0 || h.coins.settled_bets > 0 || h.coins.by_game.length > 0) && (
        <DetailBlock title="Free PCoins" d={h.coins} fmt={num} testid="house-coins" showGames />
      )}

      <div className="grid gap-2.5 md:grid-cols-2">
        <Panel className="flex flex-col gap-1 p-5" data-testid="house-cashflow">
          <CardHead title="Cash flow" />
          <div className="mt-2 flex flex-col gap-1">
            <Row label="Deposits approved" value={money(h.cashflow.deposits)} />
            <Row label="Withdrawals approved" value={money(h.cashflow.withdrawals)} />
            <Row label="Net cash held" node={<Profit v={h.cashflow.net_cash} />} />
          </div>
        </Panel>
        <Panel className="flex flex-col gap-1 p-5" data-testid="house-free-issued">
          <CardHead title="Free coins issued" />
          <p className="mt-2 font-display text-[30px] font-extrabold tabular-nums">{num(h.free_issued)}</p>
          <p className="text-[12px] font-medium text-[#71717a]">Bonuses + admin coin credits (not PCash)</p>
        </Panel>
      </div>
    </div>
  );
}

function Row({ label, value, node }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[#a1a1aa]">{label}</span>
      {node || <span className="font-semibold tabular-nums">{value}</span>}
    </div>
  );
}
